import React, { useEffect, useRef, forwardRef, useState, createRef, Fragment } from 'react';
import { makeStyles as makeStyles$1, Menu, MenuItem, createStyles, Modal as Modal$1 } from '@material-ui/core';
export { createStyles } from '@material-ui/core';
import { createMuiTheme, ThemeProvider as ThemeProvider$1 } from '@material-ui/core/styles';
import clsx from 'clsx';
export { StylesProvider } from '@material-ui/styles';

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles(theme) {
    return {
        "action-button": {
            "&&:hover": {
                backgroundColor: theme.palette.grey["400"],
            },
        },
        "action-button__menu": {},
        "action-button__menu-item": {
            fontFamily: theme.typography.body2.fontFamily,
            fontSize: "1rem",
            fontWeight: theme.typography.fontWeightLight,
            lineHeight: 1.5,
            minHeight: "auto",
        },
        "action-button__menu-item--disabled-button": {
            "&&": {
                backgroundColor: theme.palette.grey["300"],
                opacity: 1,
            },
        },
        "action-button__menu-item--disabled-element": {
            backgroundColor: theme.palette.grey["400"],
            margin: "0.5rem 0",
            opacity: 1,
            padding: 0,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var PatternStatus;
(function (PatternStatus) {
    PatternStatus[PatternStatus["Experimental"] = 0] = "Experimental";
    PatternStatus[PatternStatus["Stable"] = 1] = "Stable";
    PatternStatus[PatternStatus["Deprecated"] = 2] = "Deprecated";
})(PatternStatus || (PatternStatus = {}));

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const actionButton = {
    name: "actionButton",
    label: "Action Button",
    status: PatternStatus.Experimental,
    styles,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function parseHex(hex) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
        return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        }
        : null;
}
function parseRGB(color) {
    const rgb = color.match(/\d+/g);
    return rgb && rgb.length >= 3 ? { r: parseInt(rgb[0], 10), g: parseInt(rgb[1], 10), b: parseInt(rgb[2], 10) } : null;
}
function parseColor(color) {
    let normalizedColor;
    if (color[0] === "#")
        normalizedColor = parseHex(color);
    if (color.substr(0, 3) === "rgb")
        normalizedColor = parseRGB(color);
    return normalizedColor || { r: 0, g: 0, b: 0 };
}
function adjustAlpha(color, alpha) {
    const normalizedColor = parseColor(color);
    return `rgba(${normalizedColor.r},${normalizedColor.g},${normalizedColor.b},${alpha})`;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function createPrismTheme(configuration, options = {}, ...args) {
    const theme = createMuiTheme(configuration, ...args);
    theme.icons = options.icons || configuration.iconSet;
    theme.palette.adjustAlpha = adjustAlpha;
    theme.typography.body = theme.typography.body2;
    theme.typography.heading = theme.typography.body1;
    return theme;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
var IconSetType;
(function (IconSetType) {
    IconSetType[IconSetType["FontAwesome"] = 0] = "FontAwesome";
})(IconSetType || (IconSetType = {}));

var fa = {
	"-moz-osx-font-smoothing": "grayscale",
	"-webkit-font-smoothing": "antialiased",
	display: "inline-block",
	fontStyle: "normal",
	fontVariant: "normal",
	textRendering: "auto",
	lineHeight: "1",
	"&$fa-pull-left": {
		marginRight: ".3em"
	},
	"&$fa-pull-right": {
		marginLeft: ".3em"
	},
	fontFamily: "'Font Awesome 5 Free'",
	fontWeight: "900"
};
var fas = {
	"-moz-osx-font-smoothing": "grayscale",
	"-webkit-font-smoothing": "antialiased",
	display: "inline-block",
	fontStyle: "normal",
	fontVariant: "normal",
	textRendering: "auto",
	lineHeight: "1",
	"&$fa-pull-left": {
		marginRight: ".3em"
	},
	"&$fa-pull-right": {
		marginLeft: ".3em"
	},
	fontFamily: "'Font Awesome 5 Free'",
	fontWeight: "900"
};
var far = {
	"-moz-osx-font-smoothing": "grayscale",
	"-webkit-font-smoothing": "antialiased",
	display: "inline-block",
	fontStyle: "normal",
	fontVariant: "normal",
	textRendering: "auto",
	lineHeight: "1",
	"&$fa-pull-left": {
		marginRight: ".3em"
	},
	"&$fa-pull-right": {
		marginLeft: ".3em"
	},
	fontFamily: "'Font Awesome 5 Free'",
	fontWeight: "400"
};
var fal = {
	"-moz-osx-font-smoothing": "grayscale",
	"-webkit-font-smoothing": "antialiased",
	display: "inline-block",
	fontStyle: "normal",
	fontVariant: "normal",
	textRendering: "auto",
	lineHeight: "1",
	"&$fa-pull-left": {
		marginRight: ".3em"
	},
	"&$fa-pull-right": {
		marginLeft: ".3em"
	}
};
var fad = {
	"-moz-osx-font-smoothing": "grayscale",
	"-webkit-font-smoothing": "antialiased",
	display: "inline-block",
	fontStyle: "normal",
	fontVariant: "normal",
	textRendering: "auto",
	lineHeight: "1"
};
var fab = {
	"-moz-osx-font-smoothing": "grayscale",
	"-webkit-font-smoothing": "antialiased",
	display: "inline-block",
	fontStyle: "normal",
	fontVariant: "normal",
	textRendering: "auto",
	lineHeight: "1",
	"&$fa-pull-left": {
		marginRight: ".3em"
	},
	"&$fa-pull-right": {
		marginLeft: ".3em"
	},
	fontFamily: "'Font Awesome 5 Brands'",
	fontWeight: "400"
};
var jssStyles = {
	fa: fa,
	fas: fas,
	far: far,
	fal: fal,
	fad: fad,
	fab: fab,
	"fa-lg": {
	fontSize: "1.33333em",
	lineHeight: "0.75em",
	verticalAlign: "-.0667em"
},
	"fa-xs": {
	fontSize: ".75em"
},
	"fa-sm": {
	fontSize: ".875em"
},
	"fa-1x": {
	fontSize: "1em"
},
	"fa-2x": {
	fontSize: "2em"
},
	"fa-3x": {
	fontSize: "3em"
},
	"fa-4x": {
	fontSize: "4em"
},
	"fa-5x": {
	fontSize: "5em"
},
	"fa-6x": {
	fontSize: "6em"
},
	"fa-7x": {
	fontSize: "7em"
},
	"fa-8x": {
	fontSize: "8em"
},
	"fa-9x": {
	fontSize: "9em"
},
	"fa-10x": {
	fontSize: "10em"
},
	"fa-fw": {
	textAlign: "center",
	width: "1.25em"
},
	"fa-ul": {
	listStyleType: "none",
	marginLeft: "2.5em",
	paddingLeft: "0"
},
	"fa-ul > li": {
	"& > li": {
		position: "relative"
	}
},
	"fa-li": {
	left: "-2em",
	position: "absolute",
	textAlign: "center",
	width: "2em",
	lineHeight: "inherit"
},
	"fa-border": {
	border: "solid 0.08em #eee",
	borderRadius: ".1em",
	padding: ".2em .25em .15em"
},
	"fa-pull-left": {
	float: "left"
},
	"fa-pull-right": {
	float: "right"
},
	"fa-spin": {
	"-webkit-animation": "fa-spin 2s infinite linear",
	animation: "fa-spin 2s infinite linear"
},
	"fa-pulse": {
	"-webkit-animation": "fa-spin 1s infinite steps(8)",
	animation: "fa-spin 1s infinite steps(8)"
},
	"fa-rotate-90": {
	"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=1)\"",
	"-webkit-transform": "rotate(90deg)",
	transform: "rotate(90deg)"
},
	"fa-rotate-180": {
	"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=2)\"",
	"-webkit-transform": "rotate(180deg)",
	transform: "rotate(180deg)"
},
	"fa-rotate-270": {
	"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=3)\"",
	"-webkit-transform": "rotate(270deg)",
	transform: "rotate(270deg)"
},
	"fa-flip-horizontal": {
	"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=0, mirror=1)\"",
	"-webkit-transform": "scale(-1, 1)",
	transform: "scale(-1, 1)",
	"&$fa-flip-vertical": {
		"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=2, mirror=1)\"",
		"-webkit-transform": "scale(-1, -1)",
		transform: "scale(-1, -1)"
	}
},
	"fa-flip-vertical": {
	"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=2, mirror=1)\"",
	"-webkit-transform": "scale(1, -1)",
	transform: "scale(1, -1)"
},
	"fa-flip-both": {
	"-ms-filter": "\"progid:DXImageTransform.Microsoft.BasicImage(rotation=2, mirror=1)\"",
	"-webkit-transform": "scale(-1, -1)",
	transform: "scale(-1, -1)"
},
	"fa-stack": {
	display: "inline-block",
	height: "2em",
	lineHeight: "2em",
	position: "relative",
	verticalAlign: "middle",
	width: "2.5em"
},
	"fa-stack-1x": {
	left: "0",
	position: "absolute",
	textAlign: "center",
	width: "100%",
	lineHeight: "inherit"
},
	"fa-stack-2x": {
	left: "0",
	position: "absolute",
	textAlign: "center",
	width: "100%",
	fontSize: "2em"
},
	"fa-inverse": {
	color: "#fff"
},
	"fa-500px": {
	"&:before": {
		content: "\"\\f26e\""
	}
},
	"fa-accessible-icon": {
	"&:before": {
		content: "\"\\f368\""
	}
},
	"fa-accusoft": {
	"&:before": {
		content: "\"\\f369\""
	}
},
	"fa-acquisitions-incorporated": {
	"&:before": {
		content: "\"\\f6af\""
	}
},
	"fa-ad": {
	"&:before": {
		content: "\"\\f641\""
	}
},
	"fa-address-book": {
	"&:before": {
		content: "\"\\f2b9\""
	}
},
	"fa-address-card": {
	"&:before": {
		content: "\"\\f2bb\""
	}
},
	"fa-adjust": {
	"&:before": {
		content: "\"\\f042\""
	}
},
	"fa-adn": {
	"&:before": {
		content: "\"\\f170\""
	}
},
	"fa-adobe": {
	"&:before": {
		content: "\"\\f778\""
	}
},
	"fa-adversal": {
	"&:before": {
		content: "\"\\f36a\""
	}
},
	"fa-affiliatetheme": {
	"&:before": {
		content: "\"\\f36b\""
	}
},
	"fa-air-freshener": {
	"&:before": {
		content: "\"\\f5d0\""
	}
},
	"fa-airbnb": {
	"&:before": {
		content: "\"\\f834\""
	}
},
	"fa-algolia": {
	"&:before": {
		content: "\"\\f36c\""
	}
},
	"fa-align-center": {
	"&:before": {
		content: "\"\\f037\""
	}
},
	"fa-align-justify": {
	"&:before": {
		content: "\"\\f039\""
	}
},
	"fa-align-left": {
	"&:before": {
		content: "\"\\f036\""
	}
},
	"fa-align-right": {
	"&:before": {
		content: "\"\\f038\""
	}
},
	"fa-alipay": {
	"&:before": {
		content: "\"\\f642\""
	}
},
	"fa-allergies": {
	"&:before": {
		content: "\"\\f461\""
	}
},
	"fa-amazon": {
	"&:before": {
		content: "\"\\f270\""
	}
},
	"fa-amazon-pay": {
	"&:before": {
		content: "\"\\f42c\""
	}
},
	"fa-ambulance": {
	"&:before": {
		content: "\"\\f0f9\""
	}
},
	"fa-american-sign-language-interpreting": {
	"&:before": {
		content: "\"\\f2a3\""
	}
},
	"fa-amilia": {
	"&:before": {
		content: "\"\\f36d\""
	}
},
	"fa-anchor": {
	"&:before": {
		content: "\"\\f13d\""
	}
},
	"fa-android": {
	"&:before": {
		content: "\"\\f17b\""
	}
},
	"fa-angellist": {
	"&:before": {
		content: "\"\\f209\""
	}
},
	"fa-angle-double-down": {
	"&:before": {
		content: "\"\\f103\""
	}
},
	"fa-angle-double-left": {
	"&:before": {
		content: "\"\\f100\""
	}
},
	"fa-angle-double-right": {
	"&:before": {
		content: "\"\\f101\""
	}
},
	"fa-angle-double-up": {
	"&:before": {
		content: "\"\\f102\""
	}
},
	"fa-angle-down": {
	"&:before": {
		content: "\"\\f107\""
	}
},
	"fa-angle-left": {
	"&:before": {
		content: "\"\\f104\""
	}
},
	"fa-angle-right": {
	"&:before": {
		content: "\"\\f105\""
	}
},
	"fa-angle-up": {
	"&:before": {
		content: "\"\\f106\""
	}
},
	"fa-angry": {
	"&:before": {
		content: "\"\\f556\""
	}
},
	"fa-angrycreative": {
	"&:before": {
		content: "\"\\f36e\""
	}
},
	"fa-angular": {
	"&:before": {
		content: "\"\\f420\""
	}
},
	"fa-ankh": {
	"&:before": {
		content: "\"\\f644\""
	}
},
	"fa-app-store": {
	"&:before": {
		content: "\"\\f36f\""
	}
},
	"fa-app-store-ios": {
	"&:before": {
		content: "\"\\f370\""
	}
},
	"fa-apper": {
	"&:before": {
		content: "\"\\f371\""
	}
},
	"fa-apple": {
	"&:before": {
		content: "\"\\f179\""
	}
},
	"fa-apple-alt": {
	"&:before": {
		content: "\"\\f5d1\""
	}
},
	"fa-apple-pay": {
	"&:before": {
		content: "\"\\f415\""
	}
},
	"fa-archive": {
	"&:before": {
		content: "\"\\f187\""
	}
},
	"fa-archway": {
	"&:before": {
		content: "\"\\f557\""
	}
},
	"fa-arrow-alt-circle-down": {
	"&:before": {
		content: "\"\\f358\""
	}
},
	"fa-arrow-alt-circle-left": {
	"&:before": {
		content: "\"\\f359\""
	}
},
	"fa-arrow-alt-circle-right": {
	"&:before": {
		content: "\"\\f35a\""
	}
},
	"fa-arrow-alt-circle-up": {
	"&:before": {
		content: "\"\\f35b\""
	}
},
	"fa-arrow-circle-down": {
	"&:before": {
		content: "\"\\f0ab\""
	}
},
	"fa-arrow-circle-left": {
	"&:before": {
		content: "\"\\f0a8\""
	}
},
	"fa-arrow-circle-right": {
	"&:before": {
		content: "\"\\f0a9\""
	}
},
	"fa-arrow-circle-up": {
	"&:before": {
		content: "\"\\f0aa\""
	}
},
	"fa-arrow-down": {
	"&:before": {
		content: "\"\\f063\""
	}
},
	"fa-arrow-left": {
	"&:before": {
		content: "\"\\f060\""
	}
},
	"fa-arrow-right": {
	"&:before": {
		content: "\"\\f061\""
	}
},
	"fa-arrow-up": {
	"&:before": {
		content: "\"\\f062\""
	}
},
	"fa-arrows-alt": {
	"&:before": {
		content: "\"\\f0b2\""
	}
},
	"fa-arrows-alt-h": {
	"&:before": {
		content: "\"\\f337\""
	}
},
	"fa-arrows-alt-v": {
	"&:before": {
		content: "\"\\f338\""
	}
},
	"fa-artstation": {
	"&:before": {
		content: "\"\\f77a\""
	}
},
	"fa-assistive-listening-systems": {
	"&:before": {
		content: "\"\\f2a2\""
	}
},
	"fa-asterisk": {
	"&:before": {
		content: "\"\\f069\""
	}
},
	"fa-asymmetrik": {
	"&:before": {
		content: "\"\\f372\""
	}
},
	"fa-at": {
	"&:before": {
		content: "\"\\f1fa\""
	}
},
	"fa-atlas": {
	"&:before": {
		content: "\"\\f558\""
	}
},
	"fa-atlassian": {
	"&:before": {
		content: "\"\\f77b\""
	}
},
	"fa-atom": {
	"&:before": {
		content: "\"\\f5d2\""
	}
},
	"fa-audible": {
	"&:before": {
		content: "\"\\f373\""
	}
},
	"fa-audio-description": {
	"&:before": {
		content: "\"\\f29e\""
	}
},
	"fa-autoprefixer": {
	"&:before": {
		content: "\"\\f41c\""
	}
},
	"fa-avianex": {
	"&:before": {
		content: "\"\\f374\""
	}
},
	"fa-aviato": {
	"&:before": {
		content: "\"\\f421\""
	}
},
	"fa-award": {
	"&:before": {
		content: "\"\\f559\""
	}
},
	"fa-aws": {
	"&:before": {
		content: "\"\\f375\""
	}
},
	"fa-baby": {
	"&:before": {
		content: "\"\\f77c\""
	}
},
	"fa-baby-carriage": {
	"&:before": {
		content: "\"\\f77d\""
	}
},
	"fa-backspace": {
	"&:before": {
		content: "\"\\f55a\""
	}
},
	"fa-backward": {
	"&:before": {
		content: "\"\\f04a\""
	}
},
	"fa-bacon": {
	"&:before": {
		content: "\"\\f7e5\""
	}
},
	"fa-bahai": {
	"&:before": {
		content: "\"\\f666\""
	}
},
	"fa-balance-scale": {
	"&:before": {
		content: "\"\\f24e\""
	}
},
	"fa-balance-scale-left": {
	"&:before": {
		content: "\"\\f515\""
	}
},
	"fa-balance-scale-right": {
	"&:before": {
		content: "\"\\f516\""
	}
},
	"fa-ban": {
	"&:before": {
		content: "\"\\f05e\""
	}
},
	"fa-band-aid": {
	"&:before": {
		content: "\"\\f462\""
	}
},
	"fa-bandcamp": {
	"&:before": {
		content: "\"\\f2d5\""
	}
},
	"fa-barcode": {
	"&:before": {
		content: "\"\\f02a\""
	}
},
	"fa-bars": {
	"&:before": {
		content: "\"\\f0c9\""
	}
},
	"fa-baseball-ball": {
	"&:before": {
		content: "\"\\f433\""
	}
},
	"fa-basketball-ball": {
	"&:before": {
		content: "\"\\f434\""
	}
},
	"fa-bath": {
	"&:before": {
		content: "\"\\f2cd\""
	}
},
	"fa-battery-empty": {
	"&:before": {
		content: "\"\\f244\""
	}
},
	"fa-battery-full": {
	"&:before": {
		content: "\"\\f240\""
	}
},
	"fa-battery-half": {
	"&:before": {
		content: "\"\\f242\""
	}
},
	"fa-battery-quarter": {
	"&:before": {
		content: "\"\\f243\""
	}
},
	"fa-battery-three-quarters": {
	"&:before": {
		content: "\"\\f241\""
	}
},
	"fa-battle-net": {
	"&:before": {
		content: "\"\\f835\""
	}
},
	"fa-bed": {
	"&:before": {
		content: "\"\\f236\""
	}
},
	"fa-beer": {
	"&:before": {
		content: "\"\\f0fc\""
	}
},
	"fa-behance": {
	"&:before": {
		content: "\"\\f1b4\""
	}
},
	"fa-behance-square": {
	"&:before": {
		content: "\"\\f1b5\""
	}
},
	"fa-bell": {
	"&:before": {
		content: "\"\\f0f3\""
	}
},
	"fa-bell-slash": {
	"&:before": {
		content: "\"\\f1f6\""
	}
},
	"fa-bezier-curve": {
	"&:before": {
		content: "\"\\f55b\""
	}
},
	"fa-bible": {
	"&:before": {
		content: "\"\\f647\""
	}
},
	"fa-bicycle": {
	"&:before": {
		content: "\"\\f206\""
	}
},
	"fa-biking": {
	"&:before": {
		content: "\"\\f84a\""
	}
},
	"fa-bimobject": {
	"&:before": {
		content: "\"\\f378\""
	}
},
	"fa-binoculars": {
	"&:before": {
		content: "\"\\f1e5\""
	}
},
	"fa-biohazard": {
	"&:before": {
		content: "\"\\f780\""
	}
},
	"fa-birthday-cake": {
	"&:before": {
		content: "\"\\f1fd\""
	}
},
	"fa-bitbucket": {
	"&:before": {
		content: "\"\\f171\""
	}
},
	"fa-bitcoin": {
	"&:before": {
		content: "\"\\f379\""
	}
},
	"fa-bity": {
	"&:before": {
		content: "\"\\f37a\""
	}
},
	"fa-black-tie": {
	"&:before": {
		content: "\"\\f27e\""
	}
},
	"fa-blackberry": {
	"&:before": {
		content: "\"\\f37b\""
	}
},
	"fa-blender": {
	"&:before": {
		content: "\"\\f517\""
	}
},
	"fa-blender-phone": {
	"&:before": {
		content: "\"\\f6b6\""
	}
},
	"fa-blind": {
	"&:before": {
		content: "\"\\f29d\""
	}
},
	"fa-blog": {
	"&:before": {
		content: "\"\\f781\""
	}
},
	"fa-blogger": {
	"&:before": {
		content: "\"\\f37c\""
	}
},
	"fa-blogger-b": {
	"&:before": {
		content: "\"\\f37d\""
	}
},
	"fa-bluetooth": {
	"&:before": {
		content: "\"\\f293\""
	}
},
	"fa-bluetooth-b": {
	"&:before": {
		content: "\"\\f294\""
	}
},
	"fa-bold": {
	"&:before": {
		content: "\"\\f032\""
	}
},
	"fa-bolt": {
	"&:before": {
		content: "\"\\f0e7\""
	}
},
	"fa-bomb": {
	"&:before": {
		content: "\"\\f1e2\""
	}
},
	"fa-bone": {
	"&:before": {
		content: "\"\\f5d7\""
	}
},
	"fa-bong": {
	"&:before": {
		content: "\"\\f55c\""
	}
},
	"fa-book": {
	"&:before": {
		content: "\"\\f02d\""
	}
},
	"fa-book-dead": {
	"&:before": {
		content: "\"\\f6b7\""
	}
},
	"fa-book-medical": {
	"&:before": {
		content: "\"\\f7e6\""
	}
},
	"fa-book-open": {
	"&:before": {
		content: "\"\\f518\""
	}
},
	"fa-book-reader": {
	"&:before": {
		content: "\"\\f5da\""
	}
},
	"fa-bookmark": {
	"&:before": {
		content: "\"\\f02e\""
	}
},
	"fa-bootstrap": {
	"&:before": {
		content: "\"\\f836\""
	}
},
	"fa-border-all": {
	"&:before": {
		content: "\"\\f84c\""
	}
},
	"fa-border-none": {
	"&:before": {
		content: "\"\\f850\""
	}
},
	"fa-border-style": {
	"&:before": {
		content: "\"\\f853\""
	}
},
	"fa-bowling-ball": {
	"&:before": {
		content: "\"\\f436\""
	}
},
	"fa-box": {
	"&:before": {
		content: "\"\\f466\""
	}
},
	"fa-box-open": {
	"&:before": {
		content: "\"\\f49e\""
	}
},
	"fa-box-tissue": {
	"&:before": {
		content: "\"\\f95b\""
	}
},
	"fa-boxes": {
	"&:before": {
		content: "\"\\f468\""
	}
},
	"fa-braille": {
	"&:before": {
		content: "\"\\f2a1\""
	}
},
	"fa-brain": {
	"&:before": {
		content: "\"\\f5dc\""
	}
},
	"fa-bread-slice": {
	"&:before": {
		content: "\"\\f7ec\""
	}
},
	"fa-briefcase": {
	"&:before": {
		content: "\"\\f0b1\""
	}
},
	"fa-briefcase-medical": {
	"&:before": {
		content: "\"\\f469\""
	}
},
	"fa-broadcast-tower": {
	"&:before": {
		content: "\"\\f519\""
	}
},
	"fa-broom": {
	"&:before": {
		content: "\"\\f51a\""
	}
},
	"fa-brush": {
	"&:before": {
		content: "\"\\f55d\""
	}
},
	"fa-btc": {
	"&:before": {
		content: "\"\\f15a\""
	}
},
	"fa-buffer": {
	"&:before": {
		content: "\"\\f837\""
	}
},
	"fa-bug": {
	"&:before": {
		content: "\"\\f188\""
	}
},
	"fa-building": {
	"&:before": {
		content: "\"\\f1ad\""
	}
},
	"fa-bullhorn": {
	"&:before": {
		content: "\"\\f0a1\""
	}
},
	"fa-bullseye": {
	"&:before": {
		content: "\"\\f140\""
	}
},
	"fa-burn": {
	"&:before": {
		content: "\"\\f46a\""
	}
},
	"fa-buromobelexperte": {
	"&:before": {
		content: "\"\\f37f\""
	}
},
	"fa-bus": {
	"&:before": {
		content: "\"\\f207\""
	}
},
	"fa-bus-alt": {
	"&:before": {
		content: "\"\\f55e\""
	}
},
	"fa-business-time": {
	"&:before": {
		content: "\"\\f64a\""
	}
},
	"fa-buy-n-large": {
	"&:before": {
		content: "\"\\f8a6\""
	}
},
	"fa-buysellads": {
	"&:before": {
		content: "\"\\f20d\""
	}
},
	"fa-calculator": {
	"&:before": {
		content: "\"\\f1ec\""
	}
},
	"fa-calendar": {
	"&:before": {
		content: "\"\\f133\""
	}
},
	"fa-calendar-alt": {
	"&:before": {
		content: "\"\\f073\""
	}
},
	"fa-calendar-check": {
	"&:before": {
		content: "\"\\f274\""
	}
},
	"fa-calendar-day": {
	"&:before": {
		content: "\"\\f783\""
	}
},
	"fa-calendar-minus": {
	"&:before": {
		content: "\"\\f272\""
	}
},
	"fa-calendar-plus": {
	"&:before": {
		content: "\"\\f271\""
	}
},
	"fa-calendar-times": {
	"&:before": {
		content: "\"\\f273\""
	}
},
	"fa-calendar-week": {
	"&:before": {
		content: "\"\\f784\""
	}
},
	"fa-camera": {
	"&:before": {
		content: "\"\\f030\""
	}
},
	"fa-camera-retro": {
	"&:before": {
		content: "\"\\f083\""
	}
},
	"fa-campground": {
	"&:before": {
		content: "\"\\f6bb\""
	}
},
	"fa-canadian-maple-leaf": {
	"&:before": {
		content: "\"\\f785\""
	}
},
	"fa-candy-cane": {
	"&:before": {
		content: "\"\\f786\""
	}
},
	"fa-cannabis": {
	"&:before": {
		content: "\"\\f55f\""
	}
},
	"fa-capsules": {
	"&:before": {
		content: "\"\\f46b\""
	}
},
	"fa-car": {
	"&:before": {
		content: "\"\\f1b9\""
	}
},
	"fa-car-alt": {
	"&:before": {
		content: "\"\\f5de\""
	}
},
	"fa-car-battery": {
	"&:before": {
		content: "\"\\f5df\""
	}
},
	"fa-car-crash": {
	"&:before": {
		content: "\"\\f5e1\""
	}
},
	"fa-car-side": {
	"&:before": {
		content: "\"\\f5e4\""
	}
},
	"fa-caravan": {
	"&:before": {
		content: "\"\\f8ff\""
	}
},
	"fa-caret-down": {
	"&:before": {
		content: "\"\\f0d7\""
	}
},
	"fa-caret-left": {
	"&:before": {
		content: "\"\\f0d9\""
	}
},
	"fa-caret-right": {
	"&:before": {
		content: "\"\\f0da\""
	}
},
	"fa-caret-square-down": {
	"&:before": {
		content: "\"\\f150\""
	}
},
	"fa-caret-square-left": {
	"&:before": {
		content: "\"\\f191\""
	}
},
	"fa-caret-square-right": {
	"&:before": {
		content: "\"\\f152\""
	}
},
	"fa-caret-square-up": {
	"&:before": {
		content: "\"\\f151\""
	}
},
	"fa-caret-up": {
	"&:before": {
		content: "\"\\f0d8\""
	}
},
	"fa-carrot": {
	"&:before": {
		content: "\"\\f787\""
	}
},
	"fa-cart-arrow-down": {
	"&:before": {
		content: "\"\\f218\""
	}
},
	"fa-cart-plus": {
	"&:before": {
		content: "\"\\f217\""
	}
},
	"fa-cash-register": {
	"&:before": {
		content: "\"\\f788\""
	}
},
	"fa-cat": {
	"&:before": {
		content: "\"\\f6be\""
	}
},
	"fa-cc-amazon-pay": {
	"&:before": {
		content: "\"\\f42d\""
	}
},
	"fa-cc-amex": {
	"&:before": {
		content: "\"\\f1f3\""
	}
},
	"fa-cc-apple-pay": {
	"&:before": {
		content: "\"\\f416\""
	}
},
	"fa-cc-diners-club": {
	"&:before": {
		content: "\"\\f24c\""
	}
},
	"fa-cc-discover": {
	"&:before": {
		content: "\"\\f1f2\""
	}
},
	"fa-cc-jcb": {
	"&:before": {
		content: "\"\\f24b\""
	}
},
	"fa-cc-mastercard": {
	"&:before": {
		content: "\"\\f1f1\""
	}
},
	"fa-cc-paypal": {
	"&:before": {
		content: "\"\\f1f4\""
	}
},
	"fa-cc-stripe": {
	"&:before": {
		content: "\"\\f1f5\""
	}
},
	"fa-cc-visa": {
	"&:before": {
		content: "\"\\f1f0\""
	}
},
	"fa-centercode": {
	"&:before": {
		content: "\"\\f380\""
	}
},
	"fa-centos": {
	"&:before": {
		content: "\"\\f789\""
	}
},
	"fa-certificate": {
	"&:before": {
		content: "\"\\f0a3\""
	}
},
	"fa-chair": {
	"&:before": {
		content: "\"\\f6c0\""
	}
},
	"fa-chalkboard": {
	"&:before": {
		content: "\"\\f51b\""
	}
},
	"fa-chalkboard-teacher": {
	"&:before": {
		content: "\"\\f51c\""
	}
},
	"fa-charging-station": {
	"&:before": {
		content: "\"\\f5e7\""
	}
},
	"fa-chart-area": {
	"&:before": {
		content: "\"\\f1fe\""
	}
},
	"fa-chart-bar": {
	"&:before": {
		content: "\"\\f080\""
	}
},
	"fa-chart-line": {
	"&:before": {
		content: "\"\\f201\""
	}
},
	"fa-chart-pie": {
	"&:before": {
		content: "\"\\f200\""
	}
},
	"fa-check": {
	"&:before": {
		content: "\"\\f00c\""
	}
},
	"fa-check-circle": {
	"&:before": {
		content: "\"\\f058\""
	}
},
	"fa-check-double": {
	"&:before": {
		content: "\"\\f560\""
	}
},
	"fa-check-square": {
	"&:before": {
		content: "\"\\f14a\""
	}
},
	"fa-cheese": {
	"&:before": {
		content: "\"\\f7ef\""
	}
},
	"fa-chess": {
	"&:before": {
		content: "\"\\f439\""
	}
},
	"fa-chess-bishop": {
	"&:before": {
		content: "\"\\f43a\""
	}
},
	"fa-chess-board": {
	"&:before": {
		content: "\"\\f43c\""
	}
},
	"fa-chess-king": {
	"&:before": {
		content: "\"\\f43f\""
	}
},
	"fa-chess-knight": {
	"&:before": {
		content: "\"\\f441\""
	}
},
	"fa-chess-pawn": {
	"&:before": {
		content: "\"\\f443\""
	}
},
	"fa-chess-queen": {
	"&:before": {
		content: "\"\\f445\""
	}
},
	"fa-chess-rook": {
	"&:before": {
		content: "\"\\f447\""
	}
},
	"fa-chevron-circle-down": {
	"&:before": {
		content: "\"\\f13a\""
	}
},
	"fa-chevron-circle-left": {
	"&:before": {
		content: "\"\\f137\""
	}
},
	"fa-chevron-circle-right": {
	"&:before": {
		content: "\"\\f138\""
	}
},
	"fa-chevron-circle-up": {
	"&:before": {
		content: "\"\\f139\""
	}
},
	"fa-chevron-down": {
	"&:before": {
		content: "\"\\f078\""
	}
},
	"fa-chevron-left": {
	"&:before": {
		content: "\"\\f053\""
	}
},
	"fa-chevron-right": {
	"&:before": {
		content: "\"\\f054\""
	}
},
	"fa-chevron-up": {
	"&:before": {
		content: "\"\\f077\""
	}
},
	"fa-child": {
	"&:before": {
		content: "\"\\f1ae\""
	}
},
	"fa-chrome": {
	"&:before": {
		content: "\"\\f268\""
	}
},
	"fa-chromecast": {
	"&:before": {
		content: "\"\\f838\""
	}
},
	"fa-church": {
	"&:before": {
		content: "\"\\f51d\""
	}
},
	"fa-circle": {
	"&:before": {
		content: "\"\\f111\""
	}
},
	"fa-circle-notch": {
	"&:before": {
		content: "\"\\f1ce\""
	}
},
	"fa-city": {
	"&:before": {
		content: "\"\\f64f\""
	}
},
	"fa-clinic-medical": {
	"&:before": {
		content: "\"\\f7f2\""
	}
},
	"fa-clipboard": {
	"&:before": {
		content: "\"\\f328\""
	}
},
	"fa-clipboard-check": {
	"&:before": {
		content: "\"\\f46c\""
	}
},
	"fa-clipboard-list": {
	"&:before": {
		content: "\"\\f46d\""
	}
},
	"fa-clock": {
	"&:before": {
		content: "\"\\f017\""
	}
},
	"fa-clone": {
	"&:before": {
		content: "\"\\f24d\""
	}
},
	"fa-closed-captioning": {
	"&:before": {
		content: "\"\\f20a\""
	}
},
	"fa-cloud": {
	"&:before": {
		content: "\"\\f0c2\""
	}
},
	"fa-cloud-download-alt": {
	"&:before": {
		content: "\"\\f381\""
	}
},
	"fa-cloud-meatball": {
	"&:before": {
		content: "\"\\f73b\""
	}
},
	"fa-cloud-moon": {
	"&:before": {
		content: "\"\\f6c3\""
	}
},
	"fa-cloud-moon-rain": {
	"&:before": {
		content: "\"\\f73c\""
	}
},
	"fa-cloud-rain": {
	"&:before": {
		content: "\"\\f73d\""
	}
},
	"fa-cloud-showers-heavy": {
	"&:before": {
		content: "\"\\f740\""
	}
},
	"fa-cloud-sun": {
	"&:before": {
		content: "\"\\f6c4\""
	}
},
	"fa-cloud-sun-rain": {
	"&:before": {
		content: "\"\\f743\""
	}
},
	"fa-cloud-upload-alt": {
	"&:before": {
		content: "\"\\f382\""
	}
},
	"fa-cloudscale": {
	"&:before": {
		content: "\"\\f383\""
	}
},
	"fa-cloudsmith": {
	"&:before": {
		content: "\"\\f384\""
	}
},
	"fa-cloudversify": {
	"&:before": {
		content: "\"\\f385\""
	}
},
	"fa-cocktail": {
	"&:before": {
		content: "\"\\f561\""
	}
},
	"fa-code": {
	"&:before": {
		content: "\"\\f121\""
	}
},
	"fa-code-branch": {
	"&:before": {
		content: "\"\\f126\""
	}
},
	"fa-codepen": {
	"&:before": {
		content: "\"\\f1cb\""
	}
},
	"fa-codiepie": {
	"&:before": {
		content: "\"\\f284\""
	}
},
	"fa-coffee": {
	"&:before": {
		content: "\"\\f0f4\""
	}
},
	"fa-cog": {
	"&:before": {
		content: "\"\\f013\""
	}
},
	"fa-cogs": {
	"&:before": {
		content: "\"\\f085\""
	}
},
	"fa-coins": {
	"&:before": {
		content: "\"\\f51e\""
	}
},
	"fa-columns": {
	"&:before": {
		content: "\"\\f0db\""
	}
},
	"fa-comment": {
	"&:before": {
		content: "\"\\f075\""
	}
},
	"fa-comment-alt": {
	"&:before": {
		content: "\"\\f27a\""
	}
},
	"fa-comment-dollar": {
	"&:before": {
		content: "\"\\f651\""
	}
},
	"fa-comment-dots": {
	"&:before": {
		content: "\"\\f4ad\""
	}
},
	"fa-comment-medical": {
	"&:before": {
		content: "\"\\f7f5\""
	}
},
	"fa-comment-slash": {
	"&:before": {
		content: "\"\\f4b3\""
	}
},
	"fa-comments": {
	"&:before": {
		content: "\"\\f086\""
	}
},
	"fa-comments-dollar": {
	"&:before": {
		content: "\"\\f653\""
	}
},
	"fa-compact-disc": {
	"&:before": {
		content: "\"\\f51f\""
	}
},
	"fa-compass": {
	"&:before": {
		content: "\"\\f14e\""
	}
},
	"fa-compress": {
	"&:before": {
		content: "\"\\f066\""
	}
},
	"fa-compress-alt": {
	"&:before": {
		content: "\"\\f422\""
	}
},
	"fa-compress-arrows-alt": {
	"&:before": {
		content: "\"\\f78c\""
	}
},
	"fa-concierge-bell": {
	"&:before": {
		content: "\"\\f562\""
	}
},
	"fa-confluence": {
	"&:before": {
		content: "\"\\f78d\""
	}
},
	"fa-connectdevelop": {
	"&:before": {
		content: "\"\\f20e\""
	}
},
	"fa-contao": {
	"&:before": {
		content: "\"\\f26d\""
	}
},
	"fa-cookie": {
	"&:before": {
		content: "\"\\f563\""
	}
},
	"fa-cookie-bite": {
	"&:before": {
		content: "\"\\f564\""
	}
},
	"fa-copy": {
	"&:before": {
		content: "\"\\f0c5\""
	}
},
	"fa-copyright": {
	"&:before": {
		content: "\"\\f1f9\""
	}
},
	"fa-cotton-bureau": {
	"&:before": {
		content: "\"\\f89e\""
	}
},
	"fa-couch": {
	"&:before": {
		content: "\"\\f4b8\""
	}
},
	"fa-cpanel": {
	"&:before": {
		content: "\"\\f388\""
	}
},
	"fa-creative-commons": {
	"&:before": {
		content: "\"\\f25e\""
	}
},
	"fa-creative-commons-by": {
	"&:before": {
		content: "\"\\f4e7\""
	}
},
	"fa-creative-commons-nc": {
	"&:before": {
		content: "\"\\f4e8\""
	}
},
	"fa-creative-commons-nc-eu": {
	"&:before": {
		content: "\"\\f4e9\""
	}
},
	"fa-creative-commons-nc-jp": {
	"&:before": {
		content: "\"\\f4ea\""
	}
},
	"fa-creative-commons-nd": {
	"&:before": {
		content: "\"\\f4eb\""
	}
},
	"fa-creative-commons-pd": {
	"&:before": {
		content: "\"\\f4ec\""
	}
},
	"fa-creative-commons-pd-alt": {
	"&:before": {
		content: "\"\\f4ed\""
	}
},
	"fa-creative-commons-remix": {
	"&:before": {
		content: "\"\\f4ee\""
	}
},
	"fa-creative-commons-sa": {
	"&:before": {
		content: "\"\\f4ef\""
	}
},
	"fa-creative-commons-sampling": {
	"&:before": {
		content: "\"\\f4f0\""
	}
},
	"fa-creative-commons-sampling-plus": {
	"&:before": {
		content: "\"\\f4f1\""
	}
},
	"fa-creative-commons-share": {
	"&:before": {
		content: "\"\\f4f2\""
	}
},
	"fa-creative-commons-zero": {
	"&:before": {
		content: "\"\\f4f3\""
	}
},
	"fa-credit-card": {
	"&:before": {
		content: "\"\\f09d\""
	}
},
	"fa-critical-role": {
	"&:before": {
		content: "\"\\f6c9\""
	}
},
	"fa-crop": {
	"&:before": {
		content: "\"\\f125\""
	}
},
	"fa-crop-alt": {
	"&:before": {
		content: "\"\\f565\""
	}
},
	"fa-cross": {
	"&:before": {
		content: "\"\\f654\""
	}
},
	"fa-crosshairs": {
	"&:before": {
		content: "\"\\f05b\""
	}
},
	"fa-crow": {
	"&:before": {
		content: "\"\\f520\""
	}
},
	"fa-crown": {
	"&:before": {
		content: "\"\\f521\""
	}
},
	"fa-crutch": {
	"&:before": {
		content: "\"\\f7f7\""
	}
},
	"fa-css3": {
	"&:before": {
		content: "\"\\f13c\""
	}
},
	"fa-css3-alt": {
	"&:before": {
		content: "\"\\f38b\""
	}
},
	"fa-cube": {
	"&:before": {
		content: "\"\\f1b2\""
	}
},
	"fa-cubes": {
	"&:before": {
		content: "\"\\f1b3\""
	}
},
	"fa-cut": {
	"&:before": {
		content: "\"\\f0c4\""
	}
},
	"fa-cuttlefish": {
	"&:before": {
		content: "\"\\f38c\""
	}
},
	"fa-d-and-d": {
	"&:before": {
		content: "\"\\f38d\""
	}
},
	"fa-d-and-d-beyond": {
	"&:before": {
		content: "\"\\f6ca\""
	}
},
	"fa-dailymotion": {
	"&:before": {
		content: "\"\\f952\""
	}
},
	"fa-dashcube": {
	"&:before": {
		content: "\"\\f210\""
	}
},
	"fa-database": {
	"&:before": {
		content: "\"\\f1c0\""
	}
},
	"fa-deaf": {
	"&:before": {
		content: "\"\\f2a4\""
	}
},
	"fa-delicious": {
	"&:before": {
		content: "\"\\f1a5\""
	}
},
	"fa-democrat": {
	"&:before": {
		content: "\"\\f747\""
	}
},
	"fa-deploydog": {
	"&:before": {
		content: "\"\\f38e\""
	}
},
	"fa-deskpro": {
	"&:before": {
		content: "\"\\f38f\""
	}
},
	"fa-desktop": {
	"&:before": {
		content: "\"\\f108\""
	}
},
	"fa-dev": {
	"&:before": {
		content: "\"\\f6cc\""
	}
},
	"fa-deviantart": {
	"&:before": {
		content: "\"\\f1bd\""
	}
},
	"fa-dharmachakra": {
	"&:before": {
		content: "\"\\f655\""
	}
},
	"fa-dhl": {
	"&:before": {
		content: "\"\\f790\""
	}
},
	"fa-diagnoses": {
	"&:before": {
		content: "\"\\f470\""
	}
},
	"fa-diaspora": {
	"&:before": {
		content: "\"\\f791\""
	}
},
	"fa-dice": {
	"&:before": {
		content: "\"\\f522\""
	}
},
	"fa-dice-d20": {
	"&:before": {
		content: "\"\\f6cf\""
	}
},
	"fa-dice-d6": {
	"&:before": {
		content: "\"\\f6d1\""
	}
},
	"fa-dice-five": {
	"&:before": {
		content: "\"\\f523\""
	}
},
	"fa-dice-four": {
	"&:before": {
		content: "\"\\f524\""
	}
},
	"fa-dice-one": {
	"&:before": {
		content: "\"\\f525\""
	}
},
	"fa-dice-six": {
	"&:before": {
		content: "\"\\f526\""
	}
},
	"fa-dice-three": {
	"&:before": {
		content: "\"\\f527\""
	}
},
	"fa-dice-two": {
	"&:before": {
		content: "\"\\f528\""
	}
},
	"fa-digg": {
	"&:before": {
		content: "\"\\f1a6\""
	}
},
	"fa-digital-ocean": {
	"&:before": {
		content: "\"\\f391\""
	}
},
	"fa-digital-tachograph": {
	"&:before": {
		content: "\"\\f566\""
	}
},
	"fa-directions": {
	"&:before": {
		content: "\"\\f5eb\""
	}
},
	"fa-discord": {
	"&:before": {
		content: "\"\\f392\""
	}
},
	"fa-discourse": {
	"&:before": {
		content: "\"\\f393\""
	}
},
	"fa-disease": {
	"&:before": {
		content: "\"\\f7fa\""
	}
},
	"fa-divide": {
	"&:before": {
		content: "\"\\f529\""
	}
},
	"fa-dizzy": {
	"&:before": {
		content: "\"\\f567\""
	}
},
	"fa-dna": {
	"&:before": {
		content: "\"\\f471\""
	}
},
	"fa-dochub": {
	"&:before": {
		content: "\"\\f394\""
	}
},
	"fa-docker": {
	"&:before": {
		content: "\"\\f395\""
	}
},
	"fa-dog": {
	"&:before": {
		content: "\"\\f6d3\""
	}
},
	"fa-dollar-sign": {
	"&:before": {
		content: "\"\\f155\""
	}
},
	"fa-dolly": {
	"&:before": {
		content: "\"\\f472\""
	}
},
	"fa-dolly-flatbed": {
	"&:before": {
		content: "\"\\f474\""
	}
},
	"fa-donate": {
	"&:before": {
		content: "\"\\f4b9\""
	}
},
	"fa-door-closed": {
	"&:before": {
		content: "\"\\f52a\""
	}
},
	"fa-door-open": {
	"&:before": {
		content: "\"\\f52b\""
	}
},
	"fa-dot-circle": {
	"&:before": {
		content: "\"\\f192\""
	}
},
	"fa-dove": {
	"&:before": {
		content: "\"\\f4ba\""
	}
},
	"fa-download": {
	"&:before": {
		content: "\"\\f019\""
	}
},
	"fa-draft2digital": {
	"&:before": {
		content: "\"\\f396\""
	}
},
	"fa-drafting-compass": {
	"&:before": {
		content: "\"\\f568\""
	}
},
	"fa-dragon": {
	"&:before": {
		content: "\"\\f6d5\""
	}
},
	"fa-draw-polygon": {
	"&:before": {
		content: "\"\\f5ee\""
	}
},
	"fa-dribbble": {
	"&:before": {
		content: "\"\\f17d\""
	}
},
	"fa-dribbble-square": {
	"&:before": {
		content: "\"\\f397\""
	}
},
	"fa-dropbox": {
	"&:before": {
		content: "\"\\f16b\""
	}
},
	"fa-drum": {
	"&:before": {
		content: "\"\\f569\""
	}
},
	"fa-drum-steelpan": {
	"&:before": {
		content: "\"\\f56a\""
	}
},
	"fa-drumstick-bite": {
	"&:before": {
		content: "\"\\f6d7\""
	}
},
	"fa-drupal": {
	"&:before": {
		content: "\"\\f1a9\""
	}
},
	"fa-dumbbell": {
	"&:before": {
		content: "\"\\f44b\""
	}
},
	"fa-dumpster": {
	"&:before": {
		content: "\"\\f793\""
	}
},
	"fa-dumpster-fire": {
	"&:before": {
		content: "\"\\f794\""
	}
},
	"fa-dungeon": {
	"&:before": {
		content: "\"\\f6d9\""
	}
},
	"fa-dyalog": {
	"&:before": {
		content: "\"\\f399\""
	}
},
	"fa-earlybirds": {
	"&:before": {
		content: "\"\\f39a\""
	}
},
	"fa-ebay": {
	"&:before": {
		content: "\"\\f4f4\""
	}
},
	"fa-edge": {
	"&:before": {
		content: "\"\\f282\""
	}
},
	"fa-edit": {
	"&:before": {
		content: "\"\\f044\""
	}
},
	"fa-egg": {
	"&:before": {
		content: "\"\\f7fb\""
	}
},
	"fa-eject": {
	"&:before": {
		content: "\"\\f052\""
	}
},
	"fa-elementor": {
	"&:before": {
		content: "\"\\f430\""
	}
},
	"fa-ellipsis-h": {
	"&:before": {
		content: "\"\\f141\""
	}
},
	"fa-ellipsis-v": {
	"&:before": {
		content: "\"\\f142\""
	}
},
	"fa-ello": {
	"&:before": {
		content: "\"\\f5f1\""
	}
},
	"fa-ember": {
	"&:before": {
		content: "\"\\f423\""
	}
},
	"fa-empire": {
	"&:before": {
		content: "\"\\f1d1\""
	}
},
	"fa-envelope": {
	"&:before": {
		content: "\"\\f0e0\""
	}
},
	"fa-envelope-open": {
	"&:before": {
		content: "\"\\f2b6\""
	}
},
	"fa-envelope-open-text": {
	"&:before": {
		content: "\"\\f658\""
	}
},
	"fa-envelope-square": {
	"&:before": {
		content: "\"\\f199\""
	}
},
	"fa-envira": {
	"&:before": {
		content: "\"\\f299\""
	}
},
	"fa-equals": {
	"&:before": {
		content: "\"\\f52c\""
	}
},
	"fa-eraser": {
	"&:before": {
		content: "\"\\f12d\""
	}
},
	"fa-erlang": {
	"&:before": {
		content: "\"\\f39d\""
	}
},
	"fa-ethereum": {
	"&:before": {
		content: "\"\\f42e\""
	}
},
	"fa-ethernet": {
	"&:before": {
		content: "\"\\f796\""
	}
},
	"fa-etsy": {
	"&:before": {
		content: "\"\\f2d7\""
	}
},
	"fa-euro-sign": {
	"&:before": {
		content: "\"\\f153\""
	}
},
	"fa-evernote": {
	"&:before": {
		content: "\"\\f839\""
	}
},
	"fa-exchange-alt": {
	"&:before": {
		content: "\"\\f362\""
	}
},
	"fa-exclamation": {
	"&:before": {
		content: "\"\\f12a\""
	}
},
	"fa-exclamation-circle": {
	"&:before": {
		content: "\"\\f06a\""
	}
},
	"fa-exclamation-triangle": {
	"&:before": {
		content: "\"\\f071\""
	}
},
	"fa-expand": {
	"&:before": {
		content: "\"\\f065\""
	}
},
	"fa-expand-alt": {
	"&:before": {
		content: "\"\\f424\""
	}
},
	"fa-expand-arrows-alt": {
	"&:before": {
		content: "\"\\f31e\""
	}
},
	"fa-expeditedssl": {
	"&:before": {
		content: "\"\\f23e\""
	}
},
	"fa-external-link-alt": {
	"&:before": {
		content: "\"\\f35d\""
	}
},
	"fa-external-link-square-alt": {
	"&:before": {
		content: "\"\\f360\""
	}
},
	"fa-eye": {
	"&:before": {
		content: "\"\\f06e\""
	}
},
	"fa-eye-dropper": {
	"&:before": {
		content: "\"\\f1fb\""
	}
},
	"fa-eye-slash": {
	"&:before": {
		content: "\"\\f070\""
	}
},
	"fa-facebook": {
	"&:before": {
		content: "\"\\f09a\""
	}
},
	"fa-facebook-f": {
	"&:before": {
		content: "\"\\f39e\""
	}
},
	"fa-facebook-messenger": {
	"&:before": {
		content: "\"\\f39f\""
	}
},
	"fa-facebook-square": {
	"&:before": {
		content: "\"\\f082\""
	}
},
	"fa-fan": {
	"&:before": {
		content: "\"\\f863\""
	}
},
	"fa-fantasy-flight-games": {
	"&:before": {
		content: "\"\\f6dc\""
	}
},
	"fa-fast-backward": {
	"&:before": {
		content: "\"\\f049\""
	}
},
	"fa-fast-forward": {
	"&:before": {
		content: "\"\\f050\""
	}
},
	"fa-faucet": {
	"&:before": {
		content: "\"\\f905\""
	}
},
	"fa-fax": {
	"&:before": {
		content: "\"\\f1ac\""
	}
},
	"fa-feather": {
	"&:before": {
		content: "\"\\f52d\""
	}
},
	"fa-feather-alt": {
	"&:before": {
		content: "\"\\f56b\""
	}
},
	"fa-fedex": {
	"&:before": {
		content: "\"\\f797\""
	}
},
	"fa-fedora": {
	"&:before": {
		content: "\"\\f798\""
	}
},
	"fa-female": {
	"&:before": {
		content: "\"\\f182\""
	}
},
	"fa-fighter-jet": {
	"&:before": {
		content: "\"\\f0fb\""
	}
},
	"fa-figma": {
	"&:before": {
		content: "\"\\f799\""
	}
},
	"fa-file": {
	"&:before": {
		content: "\"\\f15b\""
	}
},
	"fa-file-alt": {
	"&:before": {
		content: "\"\\f15c\""
	}
},
	"fa-file-archive": {
	"&:before": {
		content: "\"\\f1c6\""
	}
},
	"fa-file-audio": {
	"&:before": {
		content: "\"\\f1c7\""
	}
},
	"fa-file-code": {
	"&:before": {
		content: "\"\\f1c9\""
	}
},
	"fa-file-contract": {
	"&:before": {
		content: "\"\\f56c\""
	}
},
	"fa-file-csv": {
	"&:before": {
		content: "\"\\f6dd\""
	}
},
	"fa-file-download": {
	"&:before": {
		content: "\"\\f56d\""
	}
},
	"fa-file-excel": {
	"&:before": {
		content: "\"\\f1c3\""
	}
},
	"fa-file-export": {
	"&:before": {
		content: "\"\\f56e\""
	}
},
	"fa-file-image": {
	"&:before": {
		content: "\"\\f1c5\""
	}
},
	"fa-file-import": {
	"&:before": {
		content: "\"\\f56f\""
	}
},
	"fa-file-invoice": {
	"&:before": {
		content: "\"\\f570\""
	}
},
	"fa-file-invoice-dollar": {
	"&:before": {
		content: "\"\\f571\""
	}
},
	"fa-file-medical": {
	"&:before": {
		content: "\"\\f477\""
	}
},
	"fa-file-medical-alt": {
	"&:before": {
		content: "\"\\f478\""
	}
},
	"fa-file-pdf": {
	"&:before": {
		content: "\"\\f1c1\""
	}
},
	"fa-file-powerpoint": {
	"&:before": {
		content: "\"\\f1c4\""
	}
},
	"fa-file-prescription": {
	"&:before": {
		content: "\"\\f572\""
	}
},
	"fa-file-signature": {
	"&:before": {
		content: "\"\\f573\""
	}
},
	"fa-file-upload": {
	"&:before": {
		content: "\"\\f574\""
	}
},
	"fa-file-video": {
	"&:before": {
		content: "\"\\f1c8\""
	}
},
	"fa-file-word": {
	"&:before": {
		content: "\"\\f1c2\""
	}
},
	"fa-fill": {
	"&:before": {
		content: "\"\\f575\""
	}
},
	"fa-fill-drip": {
	"&:before": {
		content: "\"\\f576\""
	}
},
	"fa-film": {
	"&:before": {
		content: "\"\\f008\""
	}
},
	"fa-filter": {
	"&:before": {
		content: "\"\\f0b0\""
	}
},
	"fa-fingerprint": {
	"&:before": {
		content: "\"\\f577\""
	}
},
	"fa-fire": {
	"&:before": {
		content: "\"\\f06d\""
	}
},
	"fa-fire-alt": {
	"&:before": {
		content: "\"\\f7e4\""
	}
},
	"fa-fire-extinguisher": {
	"&:before": {
		content: "\"\\f134\""
	}
},
	"fa-firefox": {
	"&:before": {
		content: "\"\\f269\""
	}
},
	"fa-firefox-browser": {
	"&:before": {
		content: "\"\\f907\""
	}
},
	"fa-first-aid": {
	"&:before": {
		content: "\"\\f479\""
	}
},
	"fa-first-order": {
	"&:before": {
		content: "\"\\f2b0\""
	}
},
	"fa-first-order-alt": {
	"&:before": {
		content: "\"\\f50a\""
	}
},
	"fa-firstdraft": {
	"&:before": {
		content: "\"\\f3a1\""
	}
},
	"fa-fish": {
	"&:before": {
		content: "\"\\f578\""
	}
},
	"fa-fist-raised": {
	"&:before": {
		content: "\"\\f6de\""
	}
},
	"fa-flag": {
	"&:before": {
		content: "\"\\f024\""
	}
},
	"fa-flag-checkered": {
	"&:before": {
		content: "\"\\f11e\""
	}
},
	"fa-flag-usa": {
	"&:before": {
		content: "\"\\f74d\""
	}
},
	"fa-flask": {
	"&:before": {
		content: "\"\\f0c3\""
	}
},
	"fa-flickr": {
	"&:before": {
		content: "\"\\f16e\""
	}
},
	"fa-flipboard": {
	"&:before": {
		content: "\"\\f44d\""
	}
},
	"fa-flushed": {
	"&:before": {
		content: "\"\\f579\""
	}
},
	"fa-fly": {
	"&:before": {
		content: "\"\\f417\""
	}
},
	"fa-folder": {
	"&:before": {
		content: "\"\\f07b\""
	}
},
	"fa-folder-minus": {
	"&:before": {
		content: "\"\\f65d\""
	}
},
	"fa-folder-open": {
	"&:before": {
		content: "\"\\f07c\""
	}
},
	"fa-folder-plus": {
	"&:before": {
		content: "\"\\f65e\""
	}
},
	"fa-font": {
	"&:before": {
		content: "\"\\f031\""
	}
},
	"fa-font-awesome": {
	"&:before": {
		content: "\"\\f2b4\""
	}
},
	"fa-font-awesome-alt": {
	"&:before": {
		content: "\"\\f35c\""
	}
},
	"fa-font-awesome-flag": {
	"&:before": {
		content: "\"\\f425\""
	}
},
	"fa-font-awesome-logo-full": {
	"&:before": {
		content: "\"\\f4e6\""
	}
},
	"fa-fonticons": {
	"&:before": {
		content: "\"\\f280\""
	}
},
	"fa-fonticons-fi": {
	"&:before": {
		content: "\"\\f3a2\""
	}
},
	"fa-football-ball": {
	"&:before": {
		content: "\"\\f44e\""
	}
},
	"fa-fort-awesome": {
	"&:before": {
		content: "\"\\f286\""
	}
},
	"fa-fort-awesome-alt": {
	"&:before": {
		content: "\"\\f3a3\""
	}
},
	"fa-forumbee": {
	"&:before": {
		content: "\"\\f211\""
	}
},
	"fa-forward": {
	"&:before": {
		content: "\"\\f04e\""
	}
},
	"fa-foursquare": {
	"&:before": {
		content: "\"\\f180\""
	}
},
	"fa-free-code-camp": {
	"&:before": {
		content: "\"\\f2c5\""
	}
},
	"fa-freebsd": {
	"&:before": {
		content: "\"\\f3a4\""
	}
},
	"fa-frog": {
	"&:before": {
		content: "\"\\f52e\""
	}
},
	"fa-frown": {
	"&:before": {
		content: "\"\\f119\""
	}
},
	"fa-frown-open": {
	"&:before": {
		content: "\"\\f57a\""
	}
},
	"fa-fulcrum": {
	"&:before": {
		content: "\"\\f50b\""
	}
},
	"fa-funnel-dollar": {
	"&:before": {
		content: "\"\\f662\""
	}
},
	"fa-futbol": {
	"&:before": {
		content: "\"\\f1e3\""
	}
},
	"fa-galactic-republic": {
	"&:before": {
		content: "\"\\f50c\""
	}
},
	"fa-galactic-senate": {
	"&:before": {
		content: "\"\\f50d\""
	}
},
	"fa-gamepad": {
	"&:before": {
		content: "\"\\f11b\""
	}
},
	"fa-gas-pump": {
	"&:before": {
		content: "\"\\f52f\""
	}
},
	"fa-gavel": {
	"&:before": {
		content: "\"\\f0e3\""
	}
},
	"fa-gem": {
	"&:before": {
		content: "\"\\f3a5\""
	}
},
	"fa-genderless": {
	"&:before": {
		content: "\"\\f22d\""
	}
},
	"fa-get-pocket": {
	"&:before": {
		content: "\"\\f265\""
	}
},
	"fa-gg": {
	"&:before": {
		content: "\"\\f260\""
	}
},
	"fa-gg-circle": {
	"&:before": {
		content: "\"\\f261\""
	}
},
	"fa-ghost": {
	"&:before": {
		content: "\"\\f6e2\""
	}
},
	"fa-gift": {
	"&:before": {
		content: "\"\\f06b\""
	}
},
	"fa-gifts": {
	"&:before": {
		content: "\"\\f79c\""
	}
},
	"fa-git": {
	"&:before": {
		content: "\"\\f1d3\""
	}
},
	"fa-git-alt": {
	"&:before": {
		content: "\"\\f841\""
	}
},
	"fa-git-square": {
	"&:before": {
		content: "\"\\f1d2\""
	}
},
	"fa-github": {
	"&:before": {
		content: "\"\\f09b\""
	}
},
	"fa-github-alt": {
	"&:before": {
		content: "\"\\f113\""
	}
},
	"fa-github-square": {
	"&:before": {
		content: "\"\\f092\""
	}
},
	"fa-gitkraken": {
	"&:before": {
		content: "\"\\f3a6\""
	}
},
	"fa-gitlab": {
	"&:before": {
		content: "\"\\f296\""
	}
},
	"fa-gitter": {
	"&:before": {
		content: "\"\\f426\""
	}
},
	"fa-glass-cheers": {
	"&:before": {
		content: "\"\\f79f\""
	}
},
	"fa-glass-martini": {
	"&:before": {
		content: "\"\\f000\""
	}
},
	"fa-glass-martini-alt": {
	"&:before": {
		content: "\"\\f57b\""
	}
},
	"fa-glass-whiskey": {
	"&:before": {
		content: "\"\\f7a0\""
	}
},
	"fa-glasses": {
	"&:before": {
		content: "\"\\f530\""
	}
},
	"fa-glide": {
	"&:before": {
		content: "\"\\f2a5\""
	}
},
	"fa-glide-g": {
	"&:before": {
		content: "\"\\f2a6\""
	}
},
	"fa-globe": {
	"&:before": {
		content: "\"\\f0ac\""
	}
},
	"fa-globe-africa": {
	"&:before": {
		content: "\"\\f57c\""
	}
},
	"fa-globe-americas": {
	"&:before": {
		content: "\"\\f57d\""
	}
},
	"fa-globe-asia": {
	"&:before": {
		content: "\"\\f57e\""
	}
},
	"fa-globe-europe": {
	"&:before": {
		content: "\"\\f7a2\""
	}
},
	"fa-gofore": {
	"&:before": {
		content: "\"\\f3a7\""
	}
},
	"fa-golf-ball": {
	"&:before": {
		content: "\"\\f450\""
	}
},
	"fa-goodreads": {
	"&:before": {
		content: "\"\\f3a8\""
	}
},
	"fa-goodreads-g": {
	"&:before": {
		content: "\"\\f3a9\""
	}
},
	"fa-google": {
	"&:before": {
		content: "\"\\f1a0\""
	}
},
	"fa-google-drive": {
	"&:before": {
		content: "\"\\f3aa\""
	}
},
	"fa-google-play": {
	"&:before": {
		content: "\"\\f3ab\""
	}
},
	"fa-google-plus": {
	"&:before": {
		content: "\"\\f2b3\""
	}
},
	"fa-google-plus-g": {
	"&:before": {
		content: "\"\\f0d5\""
	}
},
	"fa-google-plus-square": {
	"&:before": {
		content: "\"\\f0d4\""
	}
},
	"fa-google-wallet": {
	"&:before": {
		content: "\"\\f1ee\""
	}
},
	"fa-gopuram": {
	"&:before": {
		content: "\"\\f664\""
	}
},
	"fa-graduation-cap": {
	"&:before": {
		content: "\"\\f19d\""
	}
},
	"fa-gratipay": {
	"&:before": {
		content: "\"\\f184\""
	}
},
	"fa-grav": {
	"&:before": {
		content: "\"\\f2d6\""
	}
},
	"fa-greater-than": {
	"&:before": {
		content: "\"\\f531\""
	}
},
	"fa-greater-than-equal": {
	"&:before": {
		content: "\"\\f532\""
	}
},
	"fa-grimace": {
	"&:before": {
		content: "\"\\f57f\""
	}
},
	"fa-grin": {
	"&:before": {
		content: "\"\\f580\""
	}
},
	"fa-grin-alt": {
	"&:before": {
		content: "\"\\f581\""
	}
},
	"fa-grin-beam": {
	"&:before": {
		content: "\"\\f582\""
	}
},
	"fa-grin-beam-sweat": {
	"&:before": {
		content: "\"\\f583\""
	}
},
	"fa-grin-hearts": {
	"&:before": {
		content: "\"\\f584\""
	}
},
	"fa-grin-squint": {
	"&:before": {
		content: "\"\\f585\""
	}
},
	"fa-grin-squint-tears": {
	"&:before": {
		content: "\"\\f586\""
	}
},
	"fa-grin-stars": {
	"&:before": {
		content: "\"\\f587\""
	}
},
	"fa-grin-tears": {
	"&:before": {
		content: "\"\\f588\""
	}
},
	"fa-grin-tongue": {
	"&:before": {
		content: "\"\\f589\""
	}
},
	"fa-grin-tongue-squint": {
	"&:before": {
		content: "\"\\f58a\""
	}
},
	"fa-grin-tongue-wink": {
	"&:before": {
		content: "\"\\f58b\""
	}
},
	"fa-grin-wink": {
	"&:before": {
		content: "\"\\f58c\""
	}
},
	"fa-grip-horizontal": {
	"&:before": {
		content: "\"\\f58d\""
	}
},
	"fa-grip-lines": {
	"&:before": {
		content: "\"\\f7a4\""
	}
},
	"fa-grip-lines-vertical": {
	"&:before": {
		content: "\"\\f7a5\""
	}
},
	"fa-grip-vertical": {
	"&:before": {
		content: "\"\\f58e\""
	}
},
	"fa-gripfire": {
	"&:before": {
		content: "\"\\f3ac\""
	}
},
	"fa-grunt": {
	"&:before": {
		content: "\"\\f3ad\""
	}
},
	"fa-guitar": {
	"&:before": {
		content: "\"\\f7a6\""
	}
},
	"fa-gulp": {
	"&:before": {
		content: "\"\\f3ae\""
	}
},
	"fa-h-square": {
	"&:before": {
		content: "\"\\f0fd\""
	}
},
	"fa-hacker-news": {
	"&:before": {
		content: "\"\\f1d4\""
	}
},
	"fa-hacker-news-square": {
	"&:before": {
		content: "\"\\f3af\""
	}
},
	"fa-hackerrank": {
	"&:before": {
		content: "\"\\f5f7\""
	}
},
	"fa-hamburger": {
	"&:before": {
		content: "\"\\f805\""
	}
},
	"fa-hammer": {
	"&:before": {
		content: "\"\\f6e3\""
	}
},
	"fa-hamsa": {
	"&:before": {
		content: "\"\\f665\""
	}
},
	"fa-hand-holding": {
	"&:before": {
		content: "\"\\f4bd\""
	}
},
	"fa-hand-holding-heart": {
	"&:before": {
		content: "\"\\f4be\""
	}
},
	"fa-hand-holding-medical": {
	"&:before": {
		content: "\"\\f95c\""
	}
},
	"fa-hand-holding-usd": {
	"&:before": {
		content: "\"\\f4c0\""
	}
},
	"fa-hand-holding-water": {
	"&:before": {
		content: "\"\\f4c1\""
	}
},
	"fa-hand-lizard": {
	"&:before": {
		content: "\"\\f258\""
	}
},
	"fa-hand-middle-finger": {
	"&:before": {
		content: "\"\\f806\""
	}
},
	"fa-hand-paper": {
	"&:before": {
		content: "\"\\f256\""
	}
},
	"fa-hand-peace": {
	"&:before": {
		content: "\"\\f25b\""
	}
},
	"fa-hand-point-down": {
	"&:before": {
		content: "\"\\f0a7\""
	}
},
	"fa-hand-point-left": {
	"&:before": {
		content: "\"\\f0a5\""
	}
},
	"fa-hand-point-right": {
	"&:before": {
		content: "\"\\f0a4\""
	}
},
	"fa-hand-point-up": {
	"&:before": {
		content: "\"\\f0a6\""
	}
},
	"fa-hand-pointer": {
	"&:before": {
		content: "\"\\f25a\""
	}
},
	"fa-hand-rock": {
	"&:before": {
		content: "\"\\f255\""
	}
},
	"fa-hand-scissors": {
	"&:before": {
		content: "\"\\f257\""
	}
},
	"fa-hand-sparkles": {
	"&:before": {
		content: "\"\\f95d\""
	}
},
	"fa-hand-spock": {
	"&:before": {
		content: "\"\\f259\""
	}
},
	"fa-hands": {
	"&:before": {
		content: "\"\\f4c2\""
	}
},
	"fa-hands-helping": {
	"&:before": {
		content: "\"\\f4c4\""
	}
},
	"fa-hands-wash": {
	"&:before": {
		content: "\"\\f95e\""
	}
},
	"fa-handshake": {
	"&:before": {
		content: "\"\\f2b5\""
	}
},
	"fa-handshake-alt-slash": {
	"&:before": {
		content: "\"\\f95f\""
	}
},
	"fa-handshake-slash": {
	"&:before": {
		content: "\"\\f960\""
	}
},
	"fa-hanukiah": {
	"&:before": {
		content: "\"\\f6e6\""
	}
},
	"fa-hard-hat": {
	"&:before": {
		content: "\"\\f807\""
	}
},
	"fa-hashtag": {
	"&:before": {
		content: "\"\\f292\""
	}
},
	"fa-hat-cowboy": {
	"&:before": {
		content: "\"\\f8c0\""
	}
},
	"fa-hat-cowboy-side": {
	"&:before": {
		content: "\"\\f8c1\""
	}
},
	"fa-hat-wizard": {
	"&:before": {
		content: "\"\\f6e8\""
	}
},
	"fa-hdd": {
	"&:before": {
		content: "\"\\f0a0\""
	}
},
	"fa-head-side-cough": {
	"&:before": {
		content: "\"\\f961\""
	}
},
	"fa-head-side-cough-slash": {
	"&:before": {
		content: "\"\\f962\""
	}
},
	"fa-head-side-mask": {
	"&:before": {
		content: "\"\\f963\""
	}
},
	"fa-head-side-virus": {
	"&:before": {
		content: "\"\\f964\""
	}
},
	"fa-heading": {
	"&:before": {
		content: "\"\\f1dc\""
	}
},
	"fa-headphones": {
	"&:before": {
		content: "\"\\f025\""
	}
},
	"fa-headphones-alt": {
	"&:before": {
		content: "\"\\f58f\""
	}
},
	"fa-headset": {
	"&:before": {
		content: "\"\\f590\""
	}
},
	"fa-heart": {
	"&:before": {
		content: "\"\\f004\""
	}
},
	"fa-heart-broken": {
	"&:before": {
		content: "\"\\f7a9\""
	}
},
	"fa-heartbeat": {
	"&:before": {
		content: "\"\\f21e\""
	}
},
	"fa-helicopter": {
	"&:before": {
		content: "\"\\f533\""
	}
},
	"fa-highlighter": {
	"&:before": {
		content: "\"\\f591\""
	}
},
	"fa-hiking": {
	"&:before": {
		content: "\"\\f6ec\""
	}
},
	"fa-hippo": {
	"&:before": {
		content: "\"\\f6ed\""
	}
},
	"fa-hips": {
	"&:before": {
		content: "\"\\f452\""
	}
},
	"fa-hire-a-helper": {
	"&:before": {
		content: "\"\\f3b0\""
	}
},
	"fa-history": {
	"&:before": {
		content: "\"\\f1da\""
	}
},
	"fa-hockey-puck": {
	"&:before": {
		content: "\"\\f453\""
	}
},
	"fa-holly-berry": {
	"&:before": {
		content: "\"\\f7aa\""
	}
},
	"fa-home": {
	"&:before": {
		content: "\"\\f015\""
	}
},
	"fa-hooli": {
	"&:before": {
		content: "\"\\f427\""
	}
},
	"fa-hornbill": {
	"&:before": {
		content: "\"\\f592\""
	}
},
	"fa-horse": {
	"&:before": {
		content: "\"\\f6f0\""
	}
},
	"fa-horse-head": {
	"&:before": {
		content: "\"\\f7ab\""
	}
},
	"fa-hospital": {
	"&:before": {
		content: "\"\\f0f8\""
	}
},
	"fa-hospital-alt": {
	"&:before": {
		content: "\"\\f47d\""
	}
},
	"fa-hospital-symbol": {
	"&:before": {
		content: "\"\\f47e\""
	}
},
	"fa-hospital-user": {
	"&:before": {
		content: "\"\\f80d\""
	}
},
	"fa-hot-tub": {
	"&:before": {
		content: "\"\\f593\""
	}
},
	"fa-hotdog": {
	"&:before": {
		content: "\"\\f80f\""
	}
},
	"fa-hotel": {
	"&:before": {
		content: "\"\\f594\""
	}
},
	"fa-hotjar": {
	"&:before": {
		content: "\"\\f3b1\""
	}
},
	"fa-hourglass": {
	"&:before": {
		content: "\"\\f254\""
	}
},
	"fa-hourglass-end": {
	"&:before": {
		content: "\"\\f253\""
	}
},
	"fa-hourglass-half": {
	"&:before": {
		content: "\"\\f252\""
	}
},
	"fa-hourglass-start": {
	"&:before": {
		content: "\"\\f251\""
	}
},
	"fa-house-damage": {
	"&:before": {
		content: "\"\\f6f1\""
	}
},
	"fa-house-user": {
	"&:before": {
		content: "\"\\f965\""
	}
},
	"fa-houzz": {
	"&:before": {
		content: "\"\\f27c\""
	}
},
	"fa-hryvnia": {
	"&:before": {
		content: "\"\\f6f2\""
	}
},
	"fa-html5": {
	"&:before": {
		content: "\"\\f13b\""
	}
},
	"fa-hubspot": {
	"&:before": {
		content: "\"\\f3b2\""
	}
},
	"fa-i-cursor": {
	"&:before": {
		content: "\"\\f246\""
	}
},
	"fa-ice-cream": {
	"&:before": {
		content: "\"\\f810\""
	}
},
	"fa-icicles": {
	"&:before": {
		content: "\"\\f7ad\""
	}
},
	"fa-icons": {
	"&:before": {
		content: "\"\\f86d\""
	}
},
	"fa-id-badge": {
	"&:before": {
		content: "\"\\f2c1\""
	}
},
	"fa-id-card": {
	"&:before": {
		content: "\"\\f2c2\""
	}
},
	"fa-id-card-alt": {
	"&:before": {
		content: "\"\\f47f\""
	}
},
	"fa-ideal": {
	"&:before": {
		content: "\"\\f913\""
	}
},
	"fa-igloo": {
	"&:before": {
		content: "\"\\f7ae\""
	}
},
	"fa-image": {
	"&:before": {
		content: "\"\\f03e\""
	}
},
	"fa-images": {
	"&:before": {
		content: "\"\\f302\""
	}
},
	"fa-imdb": {
	"&:before": {
		content: "\"\\f2d8\""
	}
},
	"fa-inbox": {
	"&:before": {
		content: "\"\\f01c\""
	}
},
	"fa-indent": {
	"&:before": {
		content: "\"\\f03c\""
	}
},
	"fa-industry": {
	"&:before": {
		content: "\"\\f275\""
	}
},
	"fa-infinity": {
	"&:before": {
		content: "\"\\f534\""
	}
},
	"fa-info": {
	"&:before": {
		content: "\"\\f129\""
	}
},
	"fa-info-circle": {
	"&:before": {
		content: "\"\\f05a\""
	}
},
	"fa-instagram": {
	"&:before": {
		content: "\"\\f16d\""
	}
},
	"fa-instagram-square": {
	"&:before": {
		content: "\"\\f955\""
	}
},
	"fa-intercom": {
	"&:before": {
		content: "\"\\f7af\""
	}
},
	"fa-internet-explorer": {
	"&:before": {
		content: "\"\\f26b\""
	}
},
	"fa-invision": {
	"&:before": {
		content: "\"\\f7b0\""
	}
},
	"fa-ioxhost": {
	"&:before": {
		content: "\"\\f208\""
	}
},
	"fa-italic": {
	"&:before": {
		content: "\"\\f033\""
	}
},
	"fa-itch-io": {
	"&:before": {
		content: "\"\\f83a\""
	}
},
	"fa-itunes": {
	"&:before": {
		content: "\"\\f3b4\""
	}
},
	"fa-itunes-note": {
	"&:before": {
		content: "\"\\f3b5\""
	}
},
	"fa-java": {
	"&:before": {
		content: "\"\\f4e4\""
	}
},
	"fa-jedi": {
	"&:before": {
		content: "\"\\f669\""
	}
},
	"fa-jedi-order": {
	"&:before": {
		content: "\"\\f50e\""
	}
},
	"fa-jenkins": {
	"&:before": {
		content: "\"\\f3b6\""
	}
},
	"fa-jira": {
	"&:before": {
		content: "\"\\f7b1\""
	}
},
	"fa-joget": {
	"&:before": {
		content: "\"\\f3b7\""
	}
},
	"fa-joint": {
	"&:before": {
		content: "\"\\f595\""
	}
},
	"fa-joomla": {
	"&:before": {
		content: "\"\\f1aa\""
	}
},
	"fa-journal-whills": {
	"&:before": {
		content: "\"\\f66a\""
	}
},
	"fa-js": {
	"&:before": {
		content: "\"\\f3b8\""
	}
},
	"fa-js-square": {
	"&:before": {
		content: "\"\\f3b9\""
	}
},
	"fa-jsfiddle": {
	"&:before": {
		content: "\"\\f1cc\""
	}
},
	"fa-kaaba": {
	"&:before": {
		content: "\"\\f66b\""
	}
},
	"fa-kaggle": {
	"&:before": {
		content: "\"\\f5fa\""
	}
},
	"fa-key": {
	"&:before": {
		content: "\"\\f084\""
	}
},
	"fa-keybase": {
	"&:before": {
		content: "\"\\f4f5\""
	}
},
	"fa-keyboard": {
	"&:before": {
		content: "\"\\f11c\""
	}
},
	"fa-keycdn": {
	"&:before": {
		content: "\"\\f3ba\""
	}
},
	"fa-khanda": {
	"&:before": {
		content: "\"\\f66d\""
	}
},
	"fa-kickstarter": {
	"&:before": {
		content: "\"\\f3bb\""
	}
},
	"fa-kickstarter-k": {
	"&:before": {
		content: "\"\\f3bc\""
	}
},
	"fa-kiss": {
	"&:before": {
		content: "\"\\f596\""
	}
},
	"fa-kiss-beam": {
	"&:before": {
		content: "\"\\f597\""
	}
},
	"fa-kiss-wink-heart": {
	"&:before": {
		content: "\"\\f598\""
	}
},
	"fa-kiwi-bird": {
	"&:before": {
		content: "\"\\f535\""
	}
},
	"fa-korvue": {
	"&:before": {
		content: "\"\\f42f\""
	}
},
	"fa-landmark": {
	"&:before": {
		content: "\"\\f66f\""
	}
},
	"fa-language": {
	"&:before": {
		content: "\"\\f1ab\""
	}
},
	"fa-laptop": {
	"&:before": {
		content: "\"\\f109\""
	}
},
	"fa-laptop-code": {
	"&:before": {
		content: "\"\\f5fc\""
	}
},
	"fa-laptop-house": {
	"&:before": {
		content: "\"\\f966\""
	}
},
	"fa-laptop-medical": {
	"&:before": {
		content: "\"\\f812\""
	}
},
	"fa-laravel": {
	"&:before": {
		content: "\"\\f3bd\""
	}
},
	"fa-lastfm": {
	"&:before": {
		content: "\"\\f202\""
	}
},
	"fa-lastfm-square": {
	"&:before": {
		content: "\"\\f203\""
	}
},
	"fa-laugh": {
	"&:before": {
		content: "\"\\f599\""
	}
},
	"fa-laugh-beam": {
	"&:before": {
		content: "\"\\f59a\""
	}
},
	"fa-laugh-squint": {
	"&:before": {
		content: "\"\\f59b\""
	}
},
	"fa-laugh-wink": {
	"&:before": {
		content: "\"\\f59c\""
	}
},
	"fa-layer-group": {
	"&:before": {
		content: "\"\\f5fd\""
	}
},
	"fa-leaf": {
	"&:before": {
		content: "\"\\f06c\""
	}
},
	"fa-leanpub": {
	"&:before": {
		content: "\"\\f212\""
	}
},
	"fa-lemon": {
	"&:before": {
		content: "\"\\f094\""
	}
},
	"fa-less": {
	"&:before": {
		content: "\"\\f41d\""
	}
},
	"fa-less-than": {
	"&:before": {
		content: "\"\\f536\""
	}
},
	"fa-less-than-equal": {
	"&:before": {
		content: "\"\\f537\""
	}
},
	"fa-level-down-alt": {
	"&:before": {
		content: "\"\\f3be\""
	}
},
	"fa-level-up-alt": {
	"&:before": {
		content: "\"\\f3bf\""
	}
},
	"fa-life-ring": {
	"&:before": {
		content: "\"\\f1cd\""
	}
},
	"fa-lightbulb": {
	"&:before": {
		content: "\"\\f0eb\""
	}
},
	"fa-line": {
	"&:before": {
		content: "\"\\f3c0\""
	}
},
	"fa-link": {
	"&:before": {
		content: "\"\\f0c1\""
	}
},
	"fa-linkedin": {
	"&:before": {
		content: "\"\\f08c\""
	}
},
	"fa-linkedin-in": {
	"&:before": {
		content: "\"\\f0e1\""
	}
},
	"fa-linode": {
	"&:before": {
		content: "\"\\f2b8\""
	}
},
	"fa-linux": {
	"&:before": {
		content: "\"\\f17c\""
	}
},
	"fa-lira-sign": {
	"&:before": {
		content: "\"\\f195\""
	}
},
	"fa-list": {
	"&:before": {
		content: "\"\\f03a\""
	}
},
	"fa-list-alt": {
	"&:before": {
		content: "\"\\f022\""
	}
},
	"fa-list-ol": {
	"&:before": {
		content: "\"\\f0cb\""
	}
},
	"fa-list-ul": {
	"&:before": {
		content: "\"\\f0ca\""
	}
},
	"fa-location-arrow": {
	"&:before": {
		content: "\"\\f124\""
	}
},
	"fa-lock": {
	"&:before": {
		content: "\"\\f023\""
	}
},
	"fa-lock-open": {
	"&:before": {
		content: "\"\\f3c1\""
	}
},
	"fa-long-arrow-alt-down": {
	"&:before": {
		content: "\"\\f309\""
	}
},
	"fa-long-arrow-alt-left": {
	"&:before": {
		content: "\"\\f30a\""
	}
},
	"fa-long-arrow-alt-right": {
	"&:before": {
		content: "\"\\f30b\""
	}
},
	"fa-long-arrow-alt-up": {
	"&:before": {
		content: "\"\\f30c\""
	}
},
	"fa-low-vision": {
	"&:before": {
		content: "\"\\f2a8\""
	}
},
	"fa-luggage-cart": {
	"&:before": {
		content: "\"\\f59d\""
	}
},
	"fa-lungs": {
	"&:before": {
		content: "\"\\f604\""
	}
},
	"fa-lungs-virus": {
	"&:before": {
		content: "\"\\f967\""
	}
},
	"fa-lyft": {
	"&:before": {
		content: "\"\\f3c3\""
	}
},
	"fa-magento": {
	"&:before": {
		content: "\"\\f3c4\""
	}
},
	"fa-magic": {
	"&:before": {
		content: "\"\\f0d0\""
	}
},
	"fa-magnet": {
	"&:before": {
		content: "\"\\f076\""
	}
},
	"fa-mail-bulk": {
	"&:before": {
		content: "\"\\f674\""
	}
},
	"fa-mailchimp": {
	"&:before": {
		content: "\"\\f59e\""
	}
},
	"fa-male": {
	"&:before": {
		content: "\"\\f183\""
	}
},
	"fa-mandalorian": {
	"&:before": {
		content: "\"\\f50f\""
	}
},
	"fa-map": {
	"&:before": {
		content: "\"\\f279\""
	}
},
	"fa-map-marked": {
	"&:before": {
		content: "\"\\f59f\""
	}
},
	"fa-map-marked-alt": {
	"&:before": {
		content: "\"\\f5a0\""
	}
},
	"fa-map-marker": {
	"&:before": {
		content: "\"\\f041\""
	}
},
	"fa-map-marker-alt": {
	"&:before": {
		content: "\"\\f3c5\""
	}
},
	"fa-map-pin": {
	"&:before": {
		content: "\"\\f276\""
	}
},
	"fa-map-signs": {
	"&:before": {
		content: "\"\\f277\""
	}
},
	"fa-markdown": {
	"&:before": {
		content: "\"\\f60f\""
	}
},
	"fa-marker": {
	"&:before": {
		content: "\"\\f5a1\""
	}
},
	"fa-mars": {
	"&:before": {
		content: "\"\\f222\""
	}
},
	"fa-mars-double": {
	"&:before": {
		content: "\"\\f227\""
	}
},
	"fa-mars-stroke": {
	"&:before": {
		content: "\"\\f229\""
	}
},
	"fa-mars-stroke-h": {
	"&:before": {
		content: "\"\\f22b\""
	}
},
	"fa-mars-stroke-v": {
	"&:before": {
		content: "\"\\f22a\""
	}
},
	"fa-mask": {
	"&:before": {
		content: "\"\\f6fa\""
	}
},
	"fa-mastodon": {
	"&:before": {
		content: "\"\\f4f6\""
	}
},
	"fa-maxcdn": {
	"&:before": {
		content: "\"\\f136\""
	}
},
	"fa-mdb": {
	"&:before": {
		content: "\"\\f8ca\""
	}
},
	"fa-medal": {
	"&:before": {
		content: "\"\\f5a2\""
	}
},
	"fa-medapps": {
	"&:before": {
		content: "\"\\f3c6\""
	}
},
	"fa-medium": {
	"&:before": {
		content: "\"\\f23a\""
	}
},
	"fa-medium-m": {
	"&:before": {
		content: "\"\\f3c7\""
	}
},
	"fa-medkit": {
	"&:before": {
		content: "\"\\f0fa\""
	}
},
	"fa-medrt": {
	"&:before": {
		content: "\"\\f3c8\""
	}
},
	"fa-meetup": {
	"&:before": {
		content: "\"\\f2e0\""
	}
},
	"fa-megaport": {
	"&:before": {
		content: "\"\\f5a3\""
	}
},
	"fa-meh": {
	"&:before": {
		content: "\"\\f11a\""
	}
},
	"fa-meh-blank": {
	"&:before": {
		content: "\"\\f5a4\""
	}
},
	"fa-meh-rolling-eyes": {
	"&:before": {
		content: "\"\\f5a5\""
	}
},
	"fa-memory": {
	"&:before": {
		content: "\"\\f538\""
	}
},
	"fa-mendeley": {
	"&:before": {
		content: "\"\\f7b3\""
	}
},
	"fa-menorah": {
	"&:before": {
		content: "\"\\f676\""
	}
},
	"fa-mercury": {
	"&:before": {
		content: "\"\\f223\""
	}
},
	"fa-meteor": {
	"&:before": {
		content: "\"\\f753\""
	}
},
	"fa-microblog": {
	"&:before": {
		content: "\"\\f91a\""
	}
},
	"fa-microchip": {
	"&:before": {
		content: "\"\\f2db\""
	}
},
	"fa-microphone": {
	"&:before": {
		content: "\"\\f130\""
	}
},
	"fa-microphone-alt": {
	"&:before": {
		content: "\"\\f3c9\""
	}
},
	"fa-microphone-alt-slash": {
	"&:before": {
		content: "\"\\f539\""
	}
},
	"fa-microphone-slash": {
	"&:before": {
		content: "\"\\f131\""
	}
},
	"fa-microscope": {
	"&:before": {
		content: "\"\\f610\""
	}
},
	"fa-microsoft": {
	"&:before": {
		content: "\"\\f3ca\""
	}
},
	"fa-minus": {
	"&:before": {
		content: "\"\\f068\""
	}
},
	"fa-minus-circle": {
	"&:before": {
		content: "\"\\f056\""
	}
},
	"fa-minus-square": {
	"&:before": {
		content: "\"\\f146\""
	}
},
	"fa-mitten": {
	"&:before": {
		content: "\"\\f7b5\""
	}
},
	"fa-mix": {
	"&:before": {
		content: "\"\\f3cb\""
	}
},
	"fa-mixcloud": {
	"&:before": {
		content: "\"\\f289\""
	}
},
	"fa-mixer": {
	"&:before": {
		content: "\"\\f956\""
	}
},
	"fa-mizuni": {
	"&:before": {
		content: "\"\\f3cc\""
	}
},
	"fa-mobile": {
	"&:before": {
		content: "\"\\f10b\""
	}
},
	"fa-mobile-alt": {
	"&:before": {
		content: "\"\\f3cd\""
	}
},
	"fa-modx": {
	"&:before": {
		content: "\"\\f285\""
	}
},
	"fa-monero": {
	"&:before": {
		content: "\"\\f3d0\""
	}
},
	"fa-money-bill": {
	"&:before": {
		content: "\"\\f0d6\""
	}
},
	"fa-money-bill-alt": {
	"&:before": {
		content: "\"\\f3d1\""
	}
},
	"fa-money-bill-wave": {
	"&:before": {
		content: "\"\\f53a\""
	}
},
	"fa-money-bill-wave-alt": {
	"&:before": {
		content: "\"\\f53b\""
	}
},
	"fa-money-check": {
	"&:before": {
		content: "\"\\f53c\""
	}
},
	"fa-money-check-alt": {
	"&:before": {
		content: "\"\\f53d\""
	}
},
	"fa-monument": {
	"&:before": {
		content: "\"\\f5a6\""
	}
},
	"fa-moon": {
	"&:before": {
		content: "\"\\f186\""
	}
},
	"fa-mortar-pestle": {
	"&:before": {
		content: "\"\\f5a7\""
	}
},
	"fa-mosque": {
	"&:before": {
		content: "\"\\f678\""
	}
},
	"fa-motorcycle": {
	"&:before": {
		content: "\"\\f21c\""
	}
},
	"fa-mountain": {
	"&:before": {
		content: "\"\\f6fc\""
	}
},
	"fa-mouse": {
	"&:before": {
		content: "\"\\f8cc\""
	}
},
	"fa-mouse-pointer": {
	"&:before": {
		content: "\"\\f245\""
	}
},
	"fa-mug-hot": {
	"&:before": {
		content: "\"\\f7b6\""
	}
},
	"fa-music": {
	"&:before": {
		content: "\"\\f001\""
	}
},
	"fa-napster": {
	"&:before": {
		content: "\"\\f3d2\""
	}
},
	"fa-neos": {
	"&:before": {
		content: "\"\\f612\""
	}
},
	"fa-network-wired": {
	"&:before": {
		content: "\"\\f6ff\""
	}
},
	"fa-neuter": {
	"&:before": {
		content: "\"\\f22c\""
	}
},
	"fa-newspaper": {
	"&:before": {
		content: "\"\\f1ea\""
	}
},
	"fa-nimblr": {
	"&:before": {
		content: "\"\\f5a8\""
	}
},
	"fa-node": {
	"&:before": {
		content: "\"\\f419\""
	}
},
	"fa-node-js": {
	"&:before": {
		content: "\"\\f3d3\""
	}
},
	"fa-not-equal": {
	"&:before": {
		content: "\"\\f53e\""
	}
},
	"fa-notes-medical": {
	"&:before": {
		content: "\"\\f481\""
	}
},
	"fa-npm": {
	"&:before": {
		content: "\"\\f3d4\""
	}
},
	"fa-ns8": {
	"&:before": {
		content: "\"\\f3d5\""
	}
},
	"fa-nutritionix": {
	"&:before": {
		content: "\"\\f3d6\""
	}
},
	"fa-object-group": {
	"&:before": {
		content: "\"\\f247\""
	}
},
	"fa-object-ungroup": {
	"&:before": {
		content: "\"\\f248\""
	}
},
	"fa-odnoklassniki": {
	"&:before": {
		content: "\"\\f263\""
	}
},
	"fa-odnoklassniki-square": {
	"&:before": {
		content: "\"\\f264\""
	}
},
	"fa-oil-can": {
	"&:before": {
		content: "\"\\f613\""
	}
},
	"fa-old-republic": {
	"&:before": {
		content: "\"\\f510\""
	}
},
	"fa-om": {
	"&:before": {
		content: "\"\\f679\""
	}
},
	"fa-opencart": {
	"&:before": {
		content: "\"\\f23d\""
	}
},
	"fa-openid": {
	"&:before": {
		content: "\"\\f19b\""
	}
},
	"fa-opera": {
	"&:before": {
		content: "\"\\f26a\""
	}
},
	"fa-optin-monster": {
	"&:before": {
		content: "\"\\f23c\""
	}
},
	"fa-orcid": {
	"&:before": {
		content: "\"\\f8d2\""
	}
},
	"fa-osi": {
	"&:before": {
		content: "\"\\f41a\""
	}
},
	"fa-otter": {
	"&:before": {
		content: "\"\\f700\""
	}
},
	"fa-outdent": {
	"&:before": {
		content: "\"\\f03b\""
	}
},
	"fa-page4": {
	"&:before": {
		content: "\"\\f3d7\""
	}
},
	"fa-pagelines": {
	"&:before": {
		content: "\"\\f18c\""
	}
},
	"fa-pager": {
	"&:before": {
		content: "\"\\f815\""
	}
},
	"fa-paint-brush": {
	"&:before": {
		content: "\"\\f1fc\""
	}
},
	"fa-paint-roller": {
	"&:before": {
		content: "\"\\f5aa\""
	}
},
	"fa-palette": {
	"&:before": {
		content: "\"\\f53f\""
	}
},
	"fa-palfed": {
	"&:before": {
		content: "\"\\f3d8\""
	}
},
	"fa-pallet": {
	"&:before": {
		content: "\"\\f482\""
	}
},
	"fa-paper-plane": {
	"&:before": {
		content: "\"\\f1d8\""
	}
},
	"fa-paperclip": {
	"&:before": {
		content: "\"\\f0c6\""
	}
},
	"fa-parachute-box": {
	"&:before": {
		content: "\"\\f4cd\""
	}
},
	"fa-paragraph": {
	"&:before": {
		content: "\"\\f1dd\""
	}
},
	"fa-parking": {
	"&:before": {
		content: "\"\\f540\""
	}
},
	"fa-passport": {
	"&:before": {
		content: "\"\\f5ab\""
	}
},
	"fa-pastafarianism": {
	"&:before": {
		content: "\"\\f67b\""
	}
},
	"fa-paste": {
	"&:before": {
		content: "\"\\f0ea\""
	}
},
	"fa-patreon": {
	"&:before": {
		content: "\"\\f3d9\""
	}
},
	"fa-pause": {
	"&:before": {
		content: "\"\\f04c\""
	}
},
	"fa-pause-circle": {
	"&:before": {
		content: "\"\\f28b\""
	}
},
	"fa-paw": {
	"&:before": {
		content: "\"\\f1b0\""
	}
},
	"fa-paypal": {
	"&:before": {
		content: "\"\\f1ed\""
	}
},
	"fa-peace": {
	"&:before": {
		content: "\"\\f67c\""
	}
},
	"fa-pen": {
	"&:before": {
		content: "\"\\f304\""
	}
},
	"fa-pen-alt": {
	"&:before": {
		content: "\"\\f305\""
	}
},
	"fa-pen-fancy": {
	"&:before": {
		content: "\"\\f5ac\""
	}
},
	"fa-pen-nib": {
	"&:before": {
		content: "\"\\f5ad\""
	}
},
	"fa-pen-square": {
	"&:before": {
		content: "\"\\f14b\""
	}
},
	"fa-pencil-alt": {
	"&:before": {
		content: "\"\\f303\""
	}
},
	"fa-pencil-ruler": {
	"&:before": {
		content: "\"\\f5ae\""
	}
},
	"fa-penny-arcade": {
	"&:before": {
		content: "\"\\f704\""
	}
},
	"fa-people-arrows": {
	"&:before": {
		content: "\"\\f968\""
	}
},
	"fa-people-carry": {
	"&:before": {
		content: "\"\\f4ce\""
	}
},
	"fa-pepper-hot": {
	"&:before": {
		content: "\"\\f816\""
	}
},
	"fa-percent": {
	"&:before": {
		content: "\"\\f295\""
	}
},
	"fa-percentage": {
	"&:before": {
		content: "\"\\f541\""
	}
},
	"fa-periscope": {
	"&:before": {
		content: "\"\\f3da\""
	}
},
	"fa-person-booth": {
	"&:before": {
		content: "\"\\f756\""
	}
},
	"fa-phabricator": {
	"&:before": {
		content: "\"\\f3db\""
	}
},
	"fa-phoenix-framework": {
	"&:before": {
		content: "\"\\f3dc\""
	}
},
	"fa-phoenix-squadron": {
	"&:before": {
		content: "\"\\f511\""
	}
},
	"fa-phone": {
	"&:before": {
		content: "\"\\f095\""
	}
},
	"fa-phone-alt": {
	"&:before": {
		content: "\"\\f879\""
	}
},
	"fa-phone-slash": {
	"&:before": {
		content: "\"\\f3dd\""
	}
},
	"fa-phone-square": {
	"&:before": {
		content: "\"\\f098\""
	}
},
	"fa-phone-square-alt": {
	"&:before": {
		content: "\"\\f87b\""
	}
},
	"fa-phone-volume": {
	"&:before": {
		content: "\"\\f2a0\""
	}
},
	"fa-photo-video": {
	"&:before": {
		content: "\"\\f87c\""
	}
},
	"fa-php": {
	"&:before": {
		content: "\"\\f457\""
	}
},
	"fa-pied-piper": {
	"&:before": {
		content: "\"\\f2ae\""
	}
},
	"fa-pied-piper-alt": {
	"&:before": {
		content: "\"\\f1a8\""
	}
},
	"fa-pied-piper-hat": {
	"&:before": {
		content: "\"\\f4e5\""
	}
},
	"fa-pied-piper-pp": {
	"&:before": {
		content: "\"\\f1a7\""
	}
},
	"fa-pied-piper-square": {
	"&:before": {
		content: "\"\\f91e\""
	}
},
	"fa-piggy-bank": {
	"&:before": {
		content: "\"\\f4d3\""
	}
},
	"fa-pills": {
	"&:before": {
		content: "\"\\f484\""
	}
},
	"fa-pinterest": {
	"&:before": {
		content: "\"\\f0d2\""
	}
},
	"fa-pinterest-p": {
	"&:before": {
		content: "\"\\f231\""
	}
},
	"fa-pinterest-square": {
	"&:before": {
		content: "\"\\f0d3\""
	}
},
	"fa-pizza-slice": {
	"&:before": {
		content: "\"\\f818\""
	}
},
	"fa-place-of-worship": {
	"&:before": {
		content: "\"\\f67f\""
	}
},
	"fa-plane": {
	"&:before": {
		content: "\"\\f072\""
	}
},
	"fa-plane-arrival": {
	"&:before": {
		content: "\"\\f5af\""
	}
},
	"fa-plane-departure": {
	"&:before": {
		content: "\"\\f5b0\""
	}
},
	"fa-plane-slash": {
	"&:before": {
		content: "\"\\f969\""
	}
},
	"fa-play": {
	"&:before": {
		content: "\"\\f04b\""
	}
},
	"fa-play-circle": {
	"&:before": {
		content: "\"\\f144\""
	}
},
	"fa-playstation": {
	"&:before": {
		content: "\"\\f3df\""
	}
},
	"fa-plug": {
	"&:before": {
		content: "\"\\f1e6\""
	}
},
	"fa-plus": {
	"&:before": {
		content: "\"\\f067\""
	}
},
	"fa-plus-circle": {
	"&:before": {
		content: "\"\\f055\""
	}
},
	"fa-plus-square": {
	"&:before": {
		content: "\"\\f0fe\""
	}
},
	"fa-podcast": {
	"&:before": {
		content: "\"\\f2ce\""
	}
},
	"fa-poll": {
	"&:before": {
		content: "\"\\f681\""
	}
},
	"fa-poll-h": {
	"&:before": {
		content: "\"\\f682\""
	}
},
	"fa-poo": {
	"&:before": {
		content: "\"\\f2fe\""
	}
},
	"fa-poo-storm": {
	"&:before": {
		content: "\"\\f75a\""
	}
},
	"fa-poop": {
	"&:before": {
		content: "\"\\f619\""
	}
},
	"fa-portrait": {
	"&:before": {
		content: "\"\\f3e0\""
	}
},
	"fa-pound-sign": {
	"&:before": {
		content: "\"\\f154\""
	}
},
	"fa-power-off": {
	"&:before": {
		content: "\"\\f011\""
	}
},
	"fa-pray": {
	"&:before": {
		content: "\"\\f683\""
	}
},
	"fa-praying-hands": {
	"&:before": {
		content: "\"\\f684\""
	}
},
	"fa-prescription": {
	"&:before": {
		content: "\"\\f5b1\""
	}
},
	"fa-prescription-bottle": {
	"&:before": {
		content: "\"\\f485\""
	}
},
	"fa-prescription-bottle-alt": {
	"&:before": {
		content: "\"\\f486\""
	}
},
	"fa-print": {
	"&:before": {
		content: "\"\\f02f\""
	}
},
	"fa-procedures": {
	"&:before": {
		content: "\"\\f487\""
	}
},
	"fa-product-hunt": {
	"&:before": {
		content: "\"\\f288\""
	}
},
	"fa-project-diagram": {
	"&:before": {
		content: "\"\\f542\""
	}
},
	"fa-pump-medical": {
	"&:before": {
		content: "\"\\f96a\""
	}
},
	"fa-pump-soap": {
	"&:before": {
		content: "\"\\f96b\""
	}
},
	"fa-pushed": {
	"&:before": {
		content: "\"\\f3e1\""
	}
},
	"fa-puzzle-piece": {
	"&:before": {
		content: "\"\\f12e\""
	}
},
	"fa-python": {
	"&:before": {
		content: "\"\\f3e2\""
	}
},
	"fa-qq": {
	"&:before": {
		content: "\"\\f1d6\""
	}
},
	"fa-qrcode": {
	"&:before": {
		content: "\"\\f029\""
	}
},
	"fa-question": {
	"&:before": {
		content: "\"\\f128\""
	}
},
	"fa-question-circle": {
	"&:before": {
		content: "\"\\f059\""
	}
},
	"fa-quidditch": {
	"&:before": {
		content: "\"\\f458\""
	}
},
	"fa-quinscape": {
	"&:before": {
		content: "\"\\f459\""
	}
},
	"fa-quora": {
	"&:before": {
		content: "\"\\f2c4\""
	}
},
	"fa-quote-left": {
	"&:before": {
		content: "\"\\f10d\""
	}
},
	"fa-quote-right": {
	"&:before": {
		content: "\"\\f10e\""
	}
},
	"fa-quran": {
	"&:before": {
		content: "\"\\f687\""
	}
},
	"fa-r-project": {
	"&:before": {
		content: "\"\\f4f7\""
	}
},
	"fa-radiation": {
	"&:before": {
		content: "\"\\f7b9\""
	}
},
	"fa-radiation-alt": {
	"&:before": {
		content: "\"\\f7ba\""
	}
},
	"fa-rainbow": {
	"&:before": {
		content: "\"\\f75b\""
	}
},
	"fa-random": {
	"&:before": {
		content: "\"\\f074\""
	}
},
	"fa-raspberry-pi": {
	"&:before": {
		content: "\"\\f7bb\""
	}
},
	"fa-ravelry": {
	"&:before": {
		content: "\"\\f2d9\""
	}
},
	"fa-react": {
	"&:before": {
		content: "\"\\f41b\""
	}
},
	"fa-reacteurope": {
	"&:before": {
		content: "\"\\f75d\""
	}
},
	"fa-readme": {
	"&:before": {
		content: "\"\\f4d5\""
	}
},
	"fa-rebel": {
	"&:before": {
		content: "\"\\f1d0\""
	}
},
	"fa-receipt": {
	"&:before": {
		content: "\"\\f543\""
	}
},
	"fa-record-vinyl": {
	"&:before": {
		content: "\"\\f8d9\""
	}
},
	"fa-recycle": {
	"&:before": {
		content: "\"\\f1b8\""
	}
},
	"fa-red-river": {
	"&:before": {
		content: "\"\\f3e3\""
	}
},
	"fa-reddit": {
	"&:before": {
		content: "\"\\f1a1\""
	}
},
	"fa-reddit-alien": {
	"&:before": {
		content: "\"\\f281\""
	}
},
	"fa-reddit-square": {
	"&:before": {
		content: "\"\\f1a2\""
	}
},
	"fa-redhat": {
	"&:before": {
		content: "\"\\f7bc\""
	}
},
	"fa-redo": {
	"&:before": {
		content: "\"\\f01e\""
	}
},
	"fa-redo-alt": {
	"&:before": {
		content: "\"\\f2f9\""
	}
},
	"fa-registered": {
	"&:before": {
		content: "\"\\f25d\""
	}
},
	"fa-remove-format": {
	"&:before": {
		content: "\"\\f87d\""
	}
},
	"fa-renren": {
	"&:before": {
		content: "\"\\f18b\""
	}
},
	"fa-reply": {
	"&:before": {
		content: "\"\\f3e5\""
	}
},
	"fa-reply-all": {
	"&:before": {
		content: "\"\\f122\""
	}
},
	"fa-replyd": {
	"&:before": {
		content: "\"\\f3e6\""
	}
},
	"fa-republican": {
	"&:before": {
		content: "\"\\f75e\""
	}
},
	"fa-researchgate": {
	"&:before": {
		content: "\"\\f4f8\""
	}
},
	"fa-resolving": {
	"&:before": {
		content: "\"\\f3e7\""
	}
},
	"fa-restroom": {
	"&:before": {
		content: "\"\\f7bd\""
	}
},
	"fa-retweet": {
	"&:before": {
		content: "\"\\f079\""
	}
},
	"fa-rev": {
	"&:before": {
		content: "\"\\f5b2\""
	}
},
	"fa-ribbon": {
	"&:before": {
		content: "\"\\f4d6\""
	}
},
	"fa-ring": {
	"&:before": {
		content: "\"\\f70b\""
	}
},
	"fa-road": {
	"&:before": {
		content: "\"\\f018\""
	}
},
	"fa-robot": {
	"&:before": {
		content: "\"\\f544\""
	}
},
	"fa-rocket": {
	"&:before": {
		content: "\"\\f135\""
	}
},
	"fa-rocketchat": {
	"&:before": {
		content: "\"\\f3e8\""
	}
},
	"fa-rockrms": {
	"&:before": {
		content: "\"\\f3e9\""
	}
},
	"fa-route": {
	"&:before": {
		content: "\"\\f4d7\""
	}
},
	"fa-rss": {
	"&:before": {
		content: "\"\\f09e\""
	}
},
	"fa-rss-square": {
	"&:before": {
		content: "\"\\f143\""
	}
},
	"fa-ruble-sign": {
	"&:before": {
		content: "\"\\f158\""
	}
},
	"fa-ruler": {
	"&:before": {
		content: "\"\\f545\""
	}
},
	"fa-ruler-combined": {
	"&:before": {
		content: "\"\\f546\""
	}
},
	"fa-ruler-horizontal": {
	"&:before": {
		content: "\"\\f547\""
	}
},
	"fa-ruler-vertical": {
	"&:before": {
		content: "\"\\f548\""
	}
},
	"fa-running": {
	"&:before": {
		content: "\"\\f70c\""
	}
},
	"fa-rupee-sign": {
	"&:before": {
		content: "\"\\f156\""
	}
},
	"fa-sad-cry": {
	"&:before": {
		content: "\"\\f5b3\""
	}
},
	"fa-sad-tear": {
	"&:before": {
		content: "\"\\f5b4\""
	}
},
	"fa-safari": {
	"&:before": {
		content: "\"\\f267\""
	}
},
	"fa-salesforce": {
	"&:before": {
		content: "\"\\f83b\""
	}
},
	"fa-sass": {
	"&:before": {
		content: "\"\\f41e\""
	}
},
	"fa-satellite": {
	"&:before": {
		content: "\"\\f7bf\""
	}
},
	"fa-satellite-dish": {
	"&:before": {
		content: "\"\\f7c0\""
	}
},
	"fa-save": {
	"&:before": {
		content: "\"\\f0c7\""
	}
},
	"fa-schlix": {
	"&:before": {
		content: "\"\\f3ea\""
	}
},
	"fa-school": {
	"&:before": {
		content: "\"\\f549\""
	}
},
	"fa-screwdriver": {
	"&:before": {
		content: "\"\\f54a\""
	}
},
	"fa-scribd": {
	"&:before": {
		content: "\"\\f28a\""
	}
},
	"fa-scroll": {
	"&:before": {
		content: "\"\\f70e\""
	}
},
	"fa-sd-card": {
	"&:before": {
		content: "\"\\f7c2\""
	}
},
	"fa-search": {
	"&:before": {
		content: "\"\\f002\""
	}
},
	"fa-search-dollar": {
	"&:before": {
		content: "\"\\f688\""
	}
},
	"fa-search-location": {
	"&:before": {
		content: "\"\\f689\""
	}
},
	"fa-search-minus": {
	"&:before": {
		content: "\"\\f010\""
	}
},
	"fa-search-plus": {
	"&:before": {
		content: "\"\\f00e\""
	}
},
	"fa-searchengin": {
	"&:before": {
		content: "\"\\f3eb\""
	}
},
	"fa-seedling": {
	"&:before": {
		content: "\"\\f4d8\""
	}
},
	"fa-sellcast": {
	"&:before": {
		content: "\"\\f2da\""
	}
},
	"fa-sellsy": {
	"&:before": {
		content: "\"\\f213\""
	}
},
	"fa-server": {
	"&:before": {
		content: "\"\\f233\""
	}
},
	"fa-servicestack": {
	"&:before": {
		content: "\"\\f3ec\""
	}
},
	"fa-shapes": {
	"&:before": {
		content: "\"\\f61f\""
	}
},
	"fa-share": {
	"&:before": {
		content: "\"\\f064\""
	}
},
	"fa-share-alt": {
	"&:before": {
		content: "\"\\f1e0\""
	}
},
	"fa-share-alt-square": {
	"&:before": {
		content: "\"\\f1e1\""
	}
},
	"fa-share-square": {
	"&:before": {
		content: "\"\\f14d\""
	}
},
	"fa-shekel-sign": {
	"&:before": {
		content: "\"\\f20b\""
	}
},
	"fa-shield-alt": {
	"&:before": {
		content: "\"\\f3ed\""
	}
},
	"fa-shield-virus": {
	"&:before": {
		content: "\"\\f96c\""
	}
},
	"fa-ship": {
	"&:before": {
		content: "\"\\f21a\""
	}
},
	"fa-shipping-fast": {
	"&:before": {
		content: "\"\\f48b\""
	}
},
	"fa-shirtsinbulk": {
	"&:before": {
		content: "\"\\f214\""
	}
},
	"fa-shoe-prints": {
	"&:before": {
		content: "\"\\f54b\""
	}
},
	"fa-shopify": {
	"&:before": {
		content: "\"\\f957\""
	}
},
	"fa-shopping-bag": {
	"&:before": {
		content: "\"\\f290\""
	}
},
	"fa-shopping-basket": {
	"&:before": {
		content: "\"\\f291\""
	}
},
	"fa-shopping-cart": {
	"&:before": {
		content: "\"\\f07a\""
	}
},
	"fa-shopware": {
	"&:before": {
		content: "\"\\f5b5\""
	}
},
	"fa-shower": {
	"&:before": {
		content: "\"\\f2cc\""
	}
},
	"fa-shuttle-van": {
	"&:before": {
		content: "\"\\f5b6\""
	}
},
	"fa-sign": {
	"&:before": {
		content: "\"\\f4d9\""
	}
},
	"fa-sign-in-alt": {
	"&:before": {
		content: "\"\\f2f6\""
	}
},
	"fa-sign-language": {
	"&:before": {
		content: "\"\\f2a7\""
	}
},
	"fa-sign-out-alt": {
	"&:before": {
		content: "\"\\f2f5\""
	}
},
	"fa-signal": {
	"&:before": {
		content: "\"\\f012\""
	}
},
	"fa-signature": {
	"&:before": {
		content: "\"\\f5b7\""
	}
},
	"fa-sim-card": {
	"&:before": {
		content: "\"\\f7c4\""
	}
},
	"fa-simplybuilt": {
	"&:before": {
		content: "\"\\f215\""
	}
},
	"fa-sistrix": {
	"&:before": {
		content: "\"\\f3ee\""
	}
},
	"fa-sitemap": {
	"&:before": {
		content: "\"\\f0e8\""
	}
},
	"fa-sith": {
	"&:before": {
		content: "\"\\f512\""
	}
},
	"fa-skating": {
	"&:before": {
		content: "\"\\f7c5\""
	}
},
	"fa-sketch": {
	"&:before": {
		content: "\"\\f7c6\""
	}
},
	"fa-skiing": {
	"&:before": {
		content: "\"\\f7c9\""
	}
},
	"fa-skiing-nordic": {
	"&:before": {
		content: "\"\\f7ca\""
	}
},
	"fa-skull": {
	"&:before": {
		content: "\"\\f54c\""
	}
},
	"fa-skull-crossbones": {
	"&:before": {
		content: "\"\\f714\""
	}
},
	"fa-skyatlas": {
	"&:before": {
		content: "\"\\f216\""
	}
},
	"fa-skype": {
	"&:before": {
		content: "\"\\f17e\""
	}
},
	"fa-slack": {
	"&:before": {
		content: "\"\\f198\""
	}
},
	"fa-slack-hash": {
	"&:before": {
		content: "\"\\f3ef\""
	}
},
	"fa-slash": {
	"&:before": {
		content: "\"\\f715\""
	}
},
	"fa-sleigh": {
	"&:before": {
		content: "\"\\f7cc\""
	}
},
	"fa-sliders-h": {
	"&:before": {
		content: "\"\\f1de\""
	}
},
	"fa-slideshare": {
	"&:before": {
		content: "\"\\f1e7\""
	}
},
	"fa-smile": {
	"&:before": {
		content: "\"\\f118\""
	}
},
	"fa-smile-beam": {
	"&:before": {
		content: "\"\\f5b8\""
	}
},
	"fa-smile-wink": {
	"&:before": {
		content: "\"\\f4da\""
	}
},
	"fa-smog": {
	"&:before": {
		content: "\"\\f75f\""
	}
},
	"fa-smoking": {
	"&:before": {
		content: "\"\\f48d\""
	}
},
	"fa-smoking-ban": {
	"&:before": {
		content: "\"\\f54d\""
	}
},
	"fa-sms": {
	"&:before": {
		content: "\"\\f7cd\""
	}
},
	"fa-snapchat": {
	"&:before": {
		content: "\"\\f2ab\""
	}
},
	"fa-snapchat-ghost": {
	"&:before": {
		content: "\"\\f2ac\""
	}
},
	"fa-snapchat-square": {
	"&:before": {
		content: "\"\\f2ad\""
	}
},
	"fa-snowboarding": {
	"&:before": {
		content: "\"\\f7ce\""
	}
},
	"fa-snowflake": {
	"&:before": {
		content: "\"\\f2dc\""
	}
},
	"fa-snowman": {
	"&:before": {
		content: "\"\\f7d0\""
	}
},
	"fa-snowplow": {
	"&:before": {
		content: "\"\\f7d2\""
	}
},
	"fa-soap": {
	"&:before": {
		content: "\"\\f96e\""
	}
},
	"fa-socks": {
	"&:before": {
		content: "\"\\f696\""
	}
},
	"fa-solar-panel": {
	"&:before": {
		content: "\"\\f5ba\""
	}
},
	"fa-sort": {
	"&:before": {
		content: "\"\\f0dc\""
	}
},
	"fa-sort-alpha-down": {
	"&:before": {
		content: "\"\\f15d\""
	}
},
	"fa-sort-alpha-down-alt": {
	"&:before": {
		content: "\"\\f881\""
	}
},
	"fa-sort-alpha-up": {
	"&:before": {
		content: "\"\\f15e\""
	}
},
	"fa-sort-alpha-up-alt": {
	"&:before": {
		content: "\"\\f882\""
	}
},
	"fa-sort-amount-down": {
	"&:before": {
		content: "\"\\f160\""
	}
},
	"fa-sort-amount-down-alt": {
	"&:before": {
		content: "\"\\f884\""
	}
},
	"fa-sort-amount-up": {
	"&:before": {
		content: "\"\\f161\""
	}
},
	"fa-sort-amount-up-alt": {
	"&:before": {
		content: "\"\\f885\""
	}
},
	"fa-sort-down": {
	"&:before": {
		content: "\"\\f0dd\""
	}
},
	"fa-sort-numeric-down": {
	"&:before": {
		content: "\"\\f162\""
	}
},
	"fa-sort-numeric-down-alt": {
	"&:before": {
		content: "\"\\f886\""
	}
},
	"fa-sort-numeric-up": {
	"&:before": {
		content: "\"\\f163\""
	}
},
	"fa-sort-numeric-up-alt": {
	"&:before": {
		content: "\"\\f887\""
	}
},
	"fa-sort-up": {
	"&:before": {
		content: "\"\\f0de\""
	}
},
	"fa-soundcloud": {
	"&:before": {
		content: "\"\\f1be\""
	}
},
	"fa-sourcetree": {
	"&:before": {
		content: "\"\\f7d3\""
	}
},
	"fa-spa": {
	"&:before": {
		content: "\"\\f5bb\""
	}
},
	"fa-space-shuttle": {
	"&:before": {
		content: "\"\\f197\""
	}
},
	"fa-speakap": {
	"&:before": {
		content: "\"\\f3f3\""
	}
},
	"fa-speaker-deck": {
	"&:before": {
		content: "\"\\f83c\""
	}
},
	"fa-spell-check": {
	"&:before": {
		content: "\"\\f891\""
	}
},
	"fa-spider": {
	"&:before": {
		content: "\"\\f717\""
	}
},
	"fa-spinner": {
	"&:before": {
		content: "\"\\f110\""
	}
},
	"fa-splotch": {
	"&:before": {
		content: "\"\\f5bc\""
	}
},
	"fa-spotify": {
	"&:before": {
		content: "\"\\f1bc\""
	}
},
	"fa-spray-can": {
	"&:before": {
		content: "\"\\f5bd\""
	}
},
	"fa-square": {
	"&:before": {
		content: "\"\\f0c8\""
	}
},
	"fa-square-full": {
	"&:before": {
		content: "\"\\f45c\""
	}
},
	"fa-square-root-alt": {
	"&:before": {
		content: "\"\\f698\""
	}
},
	"fa-squarespace": {
	"&:before": {
		content: "\"\\f5be\""
	}
},
	"fa-stack-exchange": {
	"&:before": {
		content: "\"\\f18d\""
	}
},
	"fa-stack-overflow": {
	"&:before": {
		content: "\"\\f16c\""
	}
},
	"fa-stackpath": {
	"&:before": {
		content: "\"\\f842\""
	}
},
	"fa-stamp": {
	"&:before": {
		content: "\"\\f5bf\""
	}
},
	"fa-star": {
	"&:before": {
		content: "\"\\f005\""
	}
},
	"fa-star-and-crescent": {
	"&:before": {
		content: "\"\\f699\""
	}
},
	"fa-star-half": {
	"&:before": {
		content: "\"\\f089\""
	}
},
	"fa-star-half-alt": {
	"&:before": {
		content: "\"\\f5c0\""
	}
},
	"fa-star-of-david": {
	"&:before": {
		content: "\"\\f69a\""
	}
},
	"fa-star-of-life": {
	"&:before": {
		content: "\"\\f621\""
	}
},
	"fa-staylinked": {
	"&:before": {
		content: "\"\\f3f5\""
	}
},
	"fa-steam": {
	"&:before": {
		content: "\"\\f1b6\""
	}
},
	"fa-steam-square": {
	"&:before": {
		content: "\"\\f1b7\""
	}
},
	"fa-steam-symbol": {
	"&:before": {
		content: "\"\\f3f6\""
	}
},
	"fa-step-backward": {
	"&:before": {
		content: "\"\\f048\""
	}
},
	"fa-step-forward": {
	"&:before": {
		content: "\"\\f051\""
	}
},
	"fa-stethoscope": {
	"&:before": {
		content: "\"\\f0f1\""
	}
},
	"fa-sticker-mule": {
	"&:before": {
		content: "\"\\f3f7\""
	}
},
	"fa-sticky-note": {
	"&:before": {
		content: "\"\\f249\""
	}
},
	"fa-stop": {
	"&:before": {
		content: "\"\\f04d\""
	}
},
	"fa-stop-circle": {
	"&:before": {
		content: "\"\\f28d\""
	}
},
	"fa-stopwatch": {
	"&:before": {
		content: "\"\\f2f2\""
	}
},
	"fa-stopwatch-20": {
	"&:before": {
		content: "\"\\f96f\""
	}
},
	"fa-store": {
	"&:before": {
		content: "\"\\f54e\""
	}
},
	"fa-store-alt": {
	"&:before": {
		content: "\"\\f54f\""
	}
},
	"fa-store-alt-slash": {
	"&:before": {
		content: "\"\\f970\""
	}
},
	"fa-store-slash": {
	"&:before": {
		content: "\"\\f971\""
	}
},
	"fa-strava": {
	"&:before": {
		content: "\"\\f428\""
	}
},
	"fa-stream": {
	"&:before": {
		content: "\"\\f550\""
	}
},
	"fa-street-view": {
	"&:before": {
		content: "\"\\f21d\""
	}
},
	"fa-strikethrough": {
	"&:before": {
		content: "\"\\f0cc\""
	}
},
	"fa-stripe": {
	"&:before": {
		content: "\"\\f429\""
	}
},
	"fa-stripe-s": {
	"&:before": {
		content: "\"\\f42a\""
	}
},
	"fa-stroopwafel": {
	"&:before": {
		content: "\"\\f551\""
	}
},
	"fa-studiovinari": {
	"&:before": {
		content: "\"\\f3f8\""
	}
},
	"fa-stumbleupon": {
	"&:before": {
		content: "\"\\f1a4\""
	}
},
	"fa-stumbleupon-circle": {
	"&:before": {
		content: "\"\\f1a3\""
	}
},
	"fa-subscript": {
	"&:before": {
		content: "\"\\f12c\""
	}
},
	"fa-subway": {
	"&:before": {
		content: "\"\\f239\""
	}
},
	"fa-suitcase": {
	"&:before": {
		content: "\"\\f0f2\""
	}
},
	"fa-suitcase-rolling": {
	"&:before": {
		content: "\"\\f5c1\""
	}
},
	"fa-sun": {
	"&:before": {
		content: "\"\\f185\""
	}
},
	"fa-superpowers": {
	"&:before": {
		content: "\"\\f2dd\""
	}
},
	"fa-superscript": {
	"&:before": {
		content: "\"\\f12b\""
	}
},
	"fa-supple": {
	"&:before": {
		content: "\"\\f3f9\""
	}
},
	"fa-surprise": {
	"&:before": {
		content: "\"\\f5c2\""
	}
},
	"fa-suse": {
	"&:before": {
		content: "\"\\f7d6\""
	}
},
	"fa-swatchbook": {
	"&:before": {
		content: "\"\\f5c3\""
	}
},
	"fa-swift": {
	"&:before": {
		content: "\"\\f8e1\""
	}
},
	"fa-swimmer": {
	"&:before": {
		content: "\"\\f5c4\""
	}
},
	"fa-swimming-pool": {
	"&:before": {
		content: "\"\\f5c5\""
	}
},
	"fa-symfony": {
	"&:before": {
		content: "\"\\f83d\""
	}
},
	"fa-synagogue": {
	"&:before": {
		content: "\"\\f69b\""
	}
},
	"fa-sync": {
	"&:before": {
		content: "\"\\f021\""
	}
},
	"fa-sync-alt": {
	"&:before": {
		content: "\"\\f2f1\""
	}
},
	"fa-syringe": {
	"&:before": {
		content: "\"\\f48e\""
	}
},
	"fa-table": {
	"&:before": {
		content: "\"\\f0ce\""
	}
},
	"fa-table-tennis": {
	"&:before": {
		content: "\"\\f45d\""
	}
},
	"fa-tablet": {
	"&:before": {
		content: "\"\\f10a\""
	}
},
	"fa-tablet-alt": {
	"&:before": {
		content: "\"\\f3fa\""
	}
},
	"fa-tablets": {
	"&:before": {
		content: "\"\\f490\""
	}
},
	"fa-tachometer-alt": {
	"&:before": {
		content: "\"\\f3fd\""
	}
},
	"fa-tag": {
	"&:before": {
		content: "\"\\f02b\""
	}
},
	"fa-tags": {
	"&:before": {
		content: "\"\\f02c\""
	}
},
	"fa-tape": {
	"&:before": {
		content: "\"\\f4db\""
	}
},
	"fa-tasks": {
	"&:before": {
		content: "\"\\f0ae\""
	}
},
	"fa-taxi": {
	"&:before": {
		content: "\"\\f1ba\""
	}
},
	"fa-teamspeak": {
	"&:before": {
		content: "\"\\f4f9\""
	}
},
	"fa-teeth": {
	"&:before": {
		content: "\"\\f62e\""
	}
},
	"fa-teeth-open": {
	"&:before": {
		content: "\"\\f62f\""
	}
},
	"fa-telegram": {
	"&:before": {
		content: "\"\\f2c6\""
	}
},
	"fa-telegram-plane": {
	"&:before": {
		content: "\"\\f3fe\""
	}
},
	"fa-temperature-high": {
	"&:before": {
		content: "\"\\f769\""
	}
},
	"fa-temperature-low": {
	"&:before": {
		content: "\"\\f76b\""
	}
},
	"fa-tencent-weibo": {
	"&:before": {
		content: "\"\\f1d5\""
	}
},
	"fa-tenge": {
	"&:before": {
		content: "\"\\f7d7\""
	}
},
	"fa-terminal": {
	"&:before": {
		content: "\"\\f120\""
	}
},
	"fa-text-height": {
	"&:before": {
		content: "\"\\f034\""
	}
},
	"fa-text-width": {
	"&:before": {
		content: "\"\\f035\""
	}
},
	"fa-th": {
	"&:before": {
		content: "\"\\f00a\""
	}
},
	"fa-th-large": {
	"&:before": {
		content: "\"\\f009\""
	}
},
	"fa-th-list": {
	"&:before": {
		content: "\"\\f00b\""
	}
},
	"fa-the-red-yeti": {
	"&:before": {
		content: "\"\\f69d\""
	}
},
	"fa-theater-masks": {
	"&:before": {
		content: "\"\\f630\""
	}
},
	"fa-themeco": {
	"&:before": {
		content: "\"\\f5c6\""
	}
},
	"fa-themeisle": {
	"&:before": {
		content: "\"\\f2b2\""
	}
},
	"fa-thermometer": {
	"&:before": {
		content: "\"\\f491\""
	}
},
	"fa-thermometer-empty": {
	"&:before": {
		content: "\"\\f2cb\""
	}
},
	"fa-thermometer-full": {
	"&:before": {
		content: "\"\\f2c7\""
	}
},
	"fa-thermometer-half": {
	"&:before": {
		content: "\"\\f2c9\""
	}
},
	"fa-thermometer-quarter": {
	"&:before": {
		content: "\"\\f2ca\""
	}
},
	"fa-thermometer-three-quarters": {
	"&:before": {
		content: "\"\\f2c8\""
	}
},
	"fa-think-peaks": {
	"&:before": {
		content: "\"\\f731\""
	}
},
	"fa-thumbs-down": {
	"&:before": {
		content: "\"\\f165\""
	}
},
	"fa-thumbs-up": {
	"&:before": {
		content: "\"\\f164\""
	}
},
	"fa-thumbtack": {
	"&:before": {
		content: "\"\\f08d\""
	}
},
	"fa-ticket-alt": {
	"&:before": {
		content: "\"\\f3ff\""
	}
},
	"fa-times": {
	"&:before": {
		content: "\"\\f00d\""
	}
},
	"fa-times-circle": {
	"&:before": {
		content: "\"\\f057\""
	}
},
	"fa-tint": {
	"&:before": {
		content: "\"\\f043\""
	}
},
	"fa-tint-slash": {
	"&:before": {
		content: "\"\\f5c7\""
	}
},
	"fa-tired": {
	"&:before": {
		content: "\"\\f5c8\""
	}
},
	"fa-toggle-off": {
	"&:before": {
		content: "\"\\f204\""
	}
},
	"fa-toggle-on": {
	"&:before": {
		content: "\"\\f205\""
	}
},
	"fa-toilet": {
	"&:before": {
		content: "\"\\f7d8\""
	}
},
	"fa-toilet-paper": {
	"&:before": {
		content: "\"\\f71e\""
	}
},
	"fa-toilet-paper-slash": {
	"&:before": {
		content: "\"\\f972\""
	}
},
	"fa-toolbox": {
	"&:before": {
		content: "\"\\f552\""
	}
},
	"fa-tools": {
	"&:before": {
		content: "\"\\f7d9\""
	}
},
	"fa-tooth": {
	"&:before": {
		content: "\"\\f5c9\""
	}
},
	"fa-torah": {
	"&:before": {
		content: "\"\\f6a0\""
	}
},
	"fa-torii-gate": {
	"&:before": {
		content: "\"\\f6a1\""
	}
},
	"fa-tractor": {
	"&:before": {
		content: "\"\\f722\""
	}
},
	"fa-trade-federation": {
	"&:before": {
		content: "\"\\f513\""
	}
},
	"fa-trademark": {
	"&:before": {
		content: "\"\\f25c\""
	}
},
	"fa-traffic-light": {
	"&:before": {
		content: "\"\\f637\""
	}
},
	"fa-trailer": {
	"&:before": {
		content: "\"\\f941\""
	}
},
	"fa-train": {
	"&:before": {
		content: "\"\\f238\""
	}
},
	"fa-tram": {
	"&:before": {
		content: "\"\\f7da\""
	}
},
	"fa-transgender": {
	"&:before": {
		content: "\"\\f224\""
	}
},
	"fa-transgender-alt": {
	"&:before": {
		content: "\"\\f225\""
	}
},
	"fa-trash": {
	"&:before": {
		content: "\"\\f1f8\""
	}
},
	"fa-trash-alt": {
	"&:before": {
		content: "\"\\f2ed\""
	}
},
	"fa-trash-restore": {
	"&:before": {
		content: "\"\\f829\""
	}
},
	"fa-trash-restore-alt": {
	"&:before": {
		content: "\"\\f82a\""
	}
},
	"fa-tree": {
	"&:before": {
		content: "\"\\f1bb\""
	}
},
	"fa-trello": {
	"&:before": {
		content: "\"\\f181\""
	}
},
	"fa-tripadvisor": {
	"&:before": {
		content: "\"\\f262\""
	}
},
	"fa-trophy": {
	"&:before": {
		content: "\"\\f091\""
	}
},
	"fa-truck": {
	"&:before": {
		content: "\"\\f0d1\""
	}
},
	"fa-truck-loading": {
	"&:before": {
		content: "\"\\f4de\""
	}
},
	"fa-truck-monster": {
	"&:before": {
		content: "\"\\f63b\""
	}
},
	"fa-truck-moving": {
	"&:before": {
		content: "\"\\f4df\""
	}
},
	"fa-truck-pickup": {
	"&:before": {
		content: "\"\\f63c\""
	}
},
	"fa-tshirt": {
	"&:before": {
		content: "\"\\f553\""
	}
},
	"fa-tty": {
	"&:before": {
		content: "\"\\f1e4\""
	}
},
	"fa-tumblr": {
	"&:before": {
		content: "\"\\f173\""
	}
},
	"fa-tumblr-square": {
	"&:before": {
		content: "\"\\f174\""
	}
},
	"fa-tv": {
	"&:before": {
		content: "\"\\f26c\""
	}
},
	"fa-twitch": {
	"&:before": {
		content: "\"\\f1e8\""
	}
},
	"fa-twitter": {
	"&:before": {
		content: "\"\\f099\""
	}
},
	"fa-twitter-square": {
	"&:before": {
		content: "\"\\f081\""
	}
},
	"fa-typo3": {
	"&:before": {
		content: "\"\\f42b\""
	}
},
	"fa-uber": {
	"&:before": {
		content: "\"\\f402\""
	}
},
	"fa-ubuntu": {
	"&:before": {
		content: "\"\\f7df\""
	}
},
	"fa-uikit": {
	"&:before": {
		content: "\"\\f403\""
	}
},
	"fa-umbraco": {
	"&:before": {
		content: "\"\\f8e8\""
	}
},
	"fa-umbrella": {
	"&:before": {
		content: "\"\\f0e9\""
	}
},
	"fa-umbrella-beach": {
	"&:before": {
		content: "\"\\f5ca\""
	}
},
	"fa-underline": {
	"&:before": {
		content: "\"\\f0cd\""
	}
},
	"fa-undo": {
	"&:before": {
		content: "\"\\f0e2\""
	}
},
	"fa-undo-alt": {
	"&:before": {
		content: "\"\\f2ea\""
	}
},
	"fa-uniregistry": {
	"&:before": {
		content: "\"\\f404\""
	}
},
	"fa-unity": {
	"&:before": {
		content: "\"\\f949\""
	}
},
	"fa-universal-access": {
	"&:before": {
		content: "\"\\f29a\""
	}
},
	"fa-university": {
	"&:before": {
		content: "\"\\f19c\""
	}
},
	"fa-unlink": {
	"&:before": {
		content: "\"\\f127\""
	}
},
	"fa-unlock": {
	"&:before": {
		content: "\"\\f09c\""
	}
},
	"fa-unlock-alt": {
	"&:before": {
		content: "\"\\f13e\""
	}
},
	"fa-untappd": {
	"&:before": {
		content: "\"\\f405\""
	}
},
	"fa-upload": {
	"&:before": {
		content: "\"\\f093\""
	}
},
	"fa-ups": {
	"&:before": {
		content: "\"\\f7e0\""
	}
},
	"fa-usb": {
	"&:before": {
		content: "\"\\f287\""
	}
},
	"fa-user": {
	"&:before": {
		content: "\"\\f007\""
	}
},
	"fa-user-alt": {
	"&:before": {
		content: "\"\\f406\""
	}
},
	"fa-user-alt-slash": {
	"&:before": {
		content: "\"\\f4fa\""
	}
},
	"fa-user-astronaut": {
	"&:before": {
		content: "\"\\f4fb\""
	}
},
	"fa-user-check": {
	"&:before": {
		content: "\"\\f4fc\""
	}
},
	"fa-user-circle": {
	"&:before": {
		content: "\"\\f2bd\""
	}
},
	"fa-user-clock": {
	"&:before": {
		content: "\"\\f4fd\""
	}
},
	"fa-user-cog": {
	"&:before": {
		content: "\"\\f4fe\""
	}
},
	"fa-user-edit": {
	"&:before": {
		content: "\"\\f4ff\""
	}
},
	"fa-user-friends": {
	"&:before": {
		content: "\"\\f500\""
	}
},
	"fa-user-graduate": {
	"&:before": {
		content: "\"\\f501\""
	}
},
	"fa-user-injured": {
	"&:before": {
		content: "\"\\f728\""
	}
},
	"fa-user-lock": {
	"&:before": {
		content: "\"\\f502\""
	}
},
	"fa-user-md": {
	"&:before": {
		content: "\"\\f0f0\""
	}
},
	"fa-user-minus": {
	"&:before": {
		content: "\"\\f503\""
	}
},
	"fa-user-ninja": {
	"&:before": {
		content: "\"\\f504\""
	}
},
	"fa-user-nurse": {
	"&:before": {
		content: "\"\\f82f\""
	}
},
	"fa-user-plus": {
	"&:before": {
		content: "\"\\f234\""
	}
},
	"fa-user-secret": {
	"&:before": {
		content: "\"\\f21b\""
	}
},
	"fa-user-shield": {
	"&:before": {
		content: "\"\\f505\""
	}
},
	"fa-user-slash": {
	"&:before": {
		content: "\"\\f506\""
	}
},
	"fa-user-tag": {
	"&:before": {
		content: "\"\\f507\""
	}
},
	"fa-user-tie": {
	"&:before": {
		content: "\"\\f508\""
	}
},
	"fa-user-times": {
	"&:before": {
		content: "\"\\f235\""
	}
},
	"fa-users": {
	"&:before": {
		content: "\"\\f0c0\""
	}
},
	"fa-users-cog": {
	"&:before": {
		content: "\"\\f509\""
	}
},
	"fa-usps": {
	"&:before": {
		content: "\"\\f7e1\""
	}
},
	"fa-ussunnah": {
	"&:before": {
		content: "\"\\f407\""
	}
},
	"fa-utensil-spoon": {
	"&:before": {
		content: "\"\\f2e5\""
	}
},
	"fa-utensils": {
	"&:before": {
		content: "\"\\f2e7\""
	}
},
	"fa-vaadin": {
	"&:before": {
		content: "\"\\f408\""
	}
},
	"fa-vector-square": {
	"&:before": {
		content: "\"\\f5cb\""
	}
},
	"fa-venus": {
	"&:before": {
		content: "\"\\f221\""
	}
},
	"fa-venus-double": {
	"&:before": {
		content: "\"\\f226\""
	}
},
	"fa-venus-mars": {
	"&:before": {
		content: "\"\\f228\""
	}
},
	"fa-viacoin": {
	"&:before": {
		content: "\"\\f237\""
	}
},
	"fa-viadeo": {
	"&:before": {
		content: "\"\\f2a9\""
	}
},
	"fa-viadeo-square": {
	"&:before": {
		content: "\"\\f2aa\""
	}
},
	"fa-vial": {
	"&:before": {
		content: "\"\\f492\""
	}
},
	"fa-vials": {
	"&:before": {
		content: "\"\\f493\""
	}
},
	"fa-viber": {
	"&:before": {
		content: "\"\\f409\""
	}
},
	"fa-video": {
	"&:before": {
		content: "\"\\f03d\""
	}
},
	"fa-video-slash": {
	"&:before": {
		content: "\"\\f4e2\""
	}
},
	"fa-vihara": {
	"&:before": {
		content: "\"\\f6a7\""
	}
},
	"fa-vimeo": {
	"&:before": {
		content: "\"\\f40a\""
	}
},
	"fa-vimeo-square": {
	"&:before": {
		content: "\"\\f194\""
	}
},
	"fa-vimeo-v": {
	"&:before": {
		content: "\"\\f27d\""
	}
},
	"fa-vine": {
	"&:before": {
		content: "\"\\f1ca\""
	}
},
	"fa-virus": {
	"&:before": {
		content: "\"\\f974\""
	}
},
	"fa-virus-slash": {
	"&:before": {
		content: "\"\\f975\""
	}
},
	"fa-viruses": {
	"&:before": {
		content: "\"\\f976\""
	}
},
	"fa-vk": {
	"&:before": {
		content: "\"\\f189\""
	}
},
	"fa-vnv": {
	"&:before": {
		content: "\"\\f40b\""
	}
},
	"fa-voicemail": {
	"&:before": {
		content: "\"\\f897\""
	}
},
	"fa-volleyball-ball": {
	"&:before": {
		content: "\"\\f45f\""
	}
},
	"fa-volume-down": {
	"&:before": {
		content: "\"\\f027\""
	}
},
	"fa-volume-mute": {
	"&:before": {
		content: "\"\\f6a9\""
	}
},
	"fa-volume-off": {
	"&:before": {
		content: "\"\\f026\""
	}
},
	"fa-volume-up": {
	"&:before": {
		content: "\"\\f028\""
	}
},
	"fa-vote-yea": {
	"&:before": {
		content: "\"\\f772\""
	}
},
	"fa-vr-cardboard": {
	"&:before": {
		content: "\"\\f729\""
	}
},
	"fa-vuejs": {
	"&:before": {
		content: "\"\\f41f\""
	}
},
	"fa-walking": {
	"&:before": {
		content: "\"\\f554\""
	}
},
	"fa-wallet": {
	"&:before": {
		content: "\"\\f555\""
	}
},
	"fa-warehouse": {
	"&:before": {
		content: "\"\\f494\""
	}
},
	"fa-water": {
	"&:before": {
		content: "\"\\f773\""
	}
},
	"fa-wave-square": {
	"&:before": {
		content: "\"\\f83e\""
	}
},
	"fa-waze": {
	"&:before": {
		content: "\"\\f83f\""
	}
},
	"fa-weebly": {
	"&:before": {
		content: "\"\\f5cc\""
	}
},
	"fa-weibo": {
	"&:before": {
		content: "\"\\f18a\""
	}
},
	"fa-weight": {
	"&:before": {
		content: "\"\\f496\""
	}
},
	"fa-weight-hanging": {
	"&:before": {
		content: "\"\\f5cd\""
	}
},
	"fa-weixin": {
	"&:before": {
		content: "\"\\f1d7\""
	}
},
	"fa-whatsapp": {
	"&:before": {
		content: "\"\\f232\""
	}
},
	"fa-whatsapp-square": {
	"&:before": {
		content: "\"\\f40c\""
	}
},
	"fa-wheelchair": {
	"&:before": {
		content: "\"\\f193\""
	}
},
	"fa-whmcs": {
	"&:before": {
		content: "\"\\f40d\""
	}
},
	"fa-wifi": {
	"&:before": {
		content: "\"\\f1eb\""
	}
},
	"fa-wikipedia-w": {
	"&:before": {
		content: "\"\\f266\""
	}
},
	"fa-wind": {
	"&:before": {
		content: "\"\\f72e\""
	}
},
	"fa-window-close": {
	"&:before": {
		content: "\"\\f410\""
	}
},
	"fa-window-maximize": {
	"&:before": {
		content: "\"\\f2d0\""
	}
},
	"fa-window-minimize": {
	"&:before": {
		content: "\"\\f2d1\""
	}
},
	"fa-window-restore": {
	"&:before": {
		content: "\"\\f2d2\""
	}
},
	"fa-windows": {
	"&:before": {
		content: "\"\\f17a\""
	}
},
	"fa-wine-bottle": {
	"&:before": {
		content: "\"\\f72f\""
	}
},
	"fa-wine-glass": {
	"&:before": {
		content: "\"\\f4e3\""
	}
},
	"fa-wine-glass-alt": {
	"&:before": {
		content: "\"\\f5ce\""
	}
},
	"fa-wix": {
	"&:before": {
		content: "\"\\f5cf\""
	}
},
	"fa-wizards-of-the-coast": {
	"&:before": {
		content: "\"\\f730\""
	}
},
	"fa-wolf-pack-battalion": {
	"&:before": {
		content: "\"\\f514\""
	}
},
	"fa-won-sign": {
	"&:before": {
		content: "\"\\f159\""
	}
},
	"fa-wordpress": {
	"&:before": {
		content: "\"\\f19a\""
	}
},
	"fa-wordpress-simple": {
	"&:before": {
		content: "\"\\f411\""
	}
},
	"fa-wpbeginner": {
	"&:before": {
		content: "\"\\f297\""
	}
},
	"fa-wpexplorer": {
	"&:before": {
		content: "\"\\f2de\""
	}
},
	"fa-wpforms": {
	"&:before": {
		content: "\"\\f298\""
	}
},
	"fa-wpressr": {
	"&:before": {
		content: "\"\\f3e4\""
	}
},
	"fa-wrench": {
	"&:before": {
		content: "\"\\f0ad\""
	}
},
	"fa-x-ray": {
	"&:before": {
		content: "\"\\f497\""
	}
},
	"fa-xbox": {
	"&:before": {
		content: "\"\\f412\""
	}
},
	"fa-xing": {
	"&:before": {
		content: "\"\\f168\""
	}
},
	"fa-xing-square": {
	"&:before": {
		content: "\"\\f169\""
	}
},
	"fa-y-combinator": {
	"&:before": {
		content: "\"\\f23b\""
	}
},
	"fa-yahoo": {
	"&:before": {
		content: "\"\\f19e\""
	}
},
	"fa-yammer": {
	"&:before": {
		content: "\"\\f840\""
	}
},
	"fa-yandex": {
	"&:before": {
		content: "\"\\f413\""
	}
},
	"fa-yandex-international": {
	"&:before": {
		content: "\"\\f414\""
	}
},
	"fa-yarn": {
	"&:before": {
		content: "\"\\f7e3\""
	}
},
	"fa-yelp": {
	"&:before": {
		content: "\"\\f1e9\""
	}
},
	"fa-yen-sign": {
	"&:before": {
		content: "\"\\f157\""
	}
},
	"fa-yin-yang": {
	"&:before": {
		content: "\"\\f6ad\""
	}
},
	"fa-yoast": {
	"&:before": {
		content: "\"\\f2b1\""
	}
},
	"fa-youtube": {
	"&:before": {
		content: "\"\\f167\""
	}
},
	"fa-youtube-square": {
	"&:before": {
		content: "\"\\f431\""
	}
},
	"fa-zhihu": {
	"&:before": {
		content: "\"\\f63f\""
	}
},
	"sr-only": {
	border: "0",
	clip: "rect(0, 0, 0, 0)",
	height: "1px",
	margin: "-1px",
	overflow: "hidden",
	padding: "0",
	position: "absolute",
	width: "1px"
},
	"sr-only-focusable": {
	"&:active": {
		clip: "auto",
		height: "auto",
		margin: "0",
		overflow: "visible",
		position: "static",
		width: "auto"
	},
	"&:focus": {
		clip: "auto",
		height: "auto",
		margin: "0",
		overflow: "visible",
		position: "static",
		width: "auto"
	}
}
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const config = {
    name: "fontawesome-solid",
    label: "Font Awesome Solid",
    type: IconSetType.FontAwesome,
    npmModule: "@fortawesome/fontawesome-free",
    cssFiles: ["css/all.css"],
    fonts: [
        {
            fontsPath: "./webfonts",
            licensePath: "./LICENSE.txt",
            fontFace: `
font-family: 'Font Awesome 5 Free';
font-style: normal;
font-weight: 900;
font-display: block;`,
            source: "fa-solid-900.eot",
            sources: [
                {
                    path: "fa-solid-900.eot",
                    format: "embedded-opentype",
                    urlAddition: "?#iefix",
                },
                {
                    path: "fa-solid-900.woff2",
                    format: "woff2",
                },
                {
                    path: "fa-solid-900.woff",
                    format: "woff",
                },
                {
                    path: "fa-solid-900.ttf",
                    format: "truetype",
                },
                {
                    path: "fa-solid-900.svg",
                    format: "svg",
                    urlAddition: "#fontawesome",
                },
            ],
        },
    ],
};
function styles$1(theme) {
    const iconStyles = jssStyles;
    iconStyles["icon-add"] = {
        extend: [iconStyles.fas, iconStyles["fa-plus"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-address-book"] = {
        extend: [iconStyles.fas, iconStyles["fa-address-book"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-app"] = {
        extend: [iconStyles.fas, iconStyles["fa-rocket"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-audio"] = {
        extend: [iconStyles.fas, iconStyles["fa-headphones-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-back"] = {
        extend: [iconStyles.fas, iconStyles["fa-arrow-left"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-bars"] = {
        extend: [iconStyles.fas, iconStyles["fa-bars"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-calendar"] = {
        extend: [iconStyles.fas, iconStyles["fa-calendar-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-calendar-solid"] = {
        extend: [iconStyles.fas, iconStyles["fa-calendar"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-cancel"] = {
        extend: [iconStyles.fas, iconStyles["fa-times"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-caret-down"] = {
        extend: [iconStyles.fas, iconStyles["fa-angle-down"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-caret-left"] = {
        extend: [iconStyles.fas, iconStyles["fa-angle-left"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-caret-right"] = {
        extend: [iconStyles.fas, iconStyles["fa-angle-right"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-caret-up"] = {
        extend: [iconStyles.fas, iconStyles["fa-angle-up"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-chat"] = {
        extend: [iconStyles.fas, iconStyles["fa-comments"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-check"] = {
        extend: [iconStyles.fas, iconStyles["fa-check"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-company"] = {
        extend: [iconStyles.fas, iconStyles["fa-building"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-contact"] = {
        extend: [iconStyles.fas, iconStyles["fa-address-book"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-copy"] = {
        extend: [iconStyles.fas, iconStyles["fa-copy"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-copyright"] = {
        extend: [iconStyles.fas, iconStyles["fa-copyright"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-delete"] = {
        extend: [iconStyles.fas, iconStyles["fa-trash-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-doc"] = {
        extend: [iconStyles.fas, iconStyles["fa-file-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-edit"] = {
        extend: [iconStyles.fas, iconStyles["fa-pen"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-email"] = {
        extend: [iconStyles.fas, iconStyles["fa-envelope"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-error"] = {
        extend: [iconStyles.fas, iconStyles["fa-exclamation-triangle"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-event"] = {
        extend: [iconStyles.fas, iconStyles["fa-calendar-day"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-external-link"] = {
        extend: [iconStyles.fas, iconStyles["fa-external-link-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-view"] = {
        extend: [iconStyles.fas, iconStyles["fa-eye"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-file"] = {
        extend: [iconStyles.fas, iconStyles["fa-file"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-files"] = {
        extend: [iconStyles.fas, iconStyles["fa-archive"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-folder"] = {
        extend: [iconStyles.fas, iconStyles["fa-folder"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-folder-add"] = {
        extend: [iconStyles.fas, iconStyles["fa-folder-plus"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-globe"] = {
        extend: [iconStyles.fas, iconStyles["fa-globe-europe"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-home"] = {
        extend: [iconStyles["fas"], iconStyles["fa-home"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-image"] = {
        extend: [iconStyles.fas, iconStyles["fa-file-image"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-inbox"] = {
        extend: [iconStyles.fas, iconStyles["fa-inbox"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-info"] = {
        extend: [iconStyles.fas, iconStyles["fa-info-circle"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-listen"] = {
        extend: [iconStyles.fas, iconStyles["fa-volume-up"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-location"] = {
        extend: [iconStyles.fas, iconStyles["fa-map-marker-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-log-in"] = {
        extend: [iconStyles.fas, iconStyles["fa-sign-in-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-log-out"] = {
        extend: [iconStyles.fas, iconStyles["fa-sign-out-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-more"] = {
        extend: [iconStyles.fas, iconStyles["fa-ellipsis-v"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-move"] = {
        extend: [iconStyles.fas, iconStyles["fa-arrow-right"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-next"] = {
        extend: [iconStyles.fas, iconStyles["fa-arrow-right"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-notification"] = {
        extend: [iconStyles.fas, iconStyles["fa-bell"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-pdf"] = {
        extend: [iconStyles.fas, iconStyles["fa-file-pdf"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-project-diagram"] = {
        extend: [iconStyles.fas, iconStyles["fa-project-diagram"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-block"] = {
        extend: [iconStyles.fas, iconStyles["fa-minus-circle"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-editor"] = {
        extend: [iconStyles.fas, iconStyles["fa-pencil-ruler"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-phone"] = {
        extend: [iconStyles.fas, iconStyles["fa-phone"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-play"] = {
        extend: [iconStyles.fas, iconStyles["fa-play"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-presentation"] = {
        extend: [iconStyles.fas, iconStyles["fa-desktop"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-previous"] = {
        extend: [iconStyles.fas, iconStyles["fa-arrow-left"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-public"] = {
        extend: [iconStyles.fas, iconStyles["fa-globe"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-remove"] = {
        extend: [iconStyles.fas, iconStyles["fa-minus-circle"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-search"] = {
        extend: [iconStyles.fas, iconStyles["fa-search"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-settings"] = {
        extend: [iconStyles.fas, iconStyles["fa-cog"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-settings-alt"] = {
        extend: [iconStyles.fas, iconStyles["fa-sliders-h"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-share"] = {
        extend: [iconStyles.fas, iconStyles["fa-share-alt"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-spreadsheet"] = {
        extend: [iconStyles.fas, iconStyles["fa-table"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-star"] = {
        extend: [iconStyles.fas, iconStyles["fa-star"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-table"] = {
        extend: [iconStyles.fas, iconStyles["fa-table"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-tag"] = {
        extend: [iconStyles.fas, iconStyles["fa-tag"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-todo"] = {
        extend: [iconStyles.fas, iconStyles["fa-clipboard-check"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-trademark"] = {
        extend: [iconStyles.fas, iconStyles["fa-trademark"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-upload"] = {
        extend: [iconStyles.fas, iconStyles["fa-upload"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-user"] = {
        extend: [iconStyles.fas, iconStyles["fa-user"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-user-astronaut"] = {
        extend: [iconStyles.fas, iconStyles["fa-user-astronaut"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-user-add"] = {
        extend: [iconStyles.fas, iconStyles["fa-user-plus"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-user-circle"] = {
        extend: [iconStyles.fas, iconStyles["fa-user-circle"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-user-lock"] = {
        extend: [iconStyles.fas, iconStyles["fa-user-lock"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-user-shield"] = {
        extend: [iconStyles.fas, iconStyles["fa-user-shield"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-users"] = {
        extend: [iconStyles.fas, iconStyles["fa-users"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-warning"] = {
        extend: [iconStyles.fas, iconStyles["fa-exclamation"]],
        color: theme.palette.text.primary,
    };
    iconStyles["icon-webid"] = {
        extend: [iconStyles.fas, iconStyles["fa-atlas"]],
        color: theme.palette.text.primary,
    };
    return JSON.parse(JSON.stringify(iconStyles));
}
function iconColor(color) {
    return { color };
}
const fontAwesomeSolid = {
    config,
    styles: styles$1,
    iconColor,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const ralewayMedium = (path) => `
@font-face {
  font-family: "Raleway-Medium";
  src: url("${path}.eot"); /* IE9 Compat Modes */
  src: url("${path}.eot?#iefix") format("embedded-opentype"), /* IE6-IE8 */
    url("${path}.otf") format("opentype"), /* Open Type Font */
    url("${path}.svg") format("svg"), /* Legacy iOS */
    url("${path}.woff") format("woff"), /* Modern Browsers */
    url("${path}.woff2") format("woff2"); /* Modern Browsers */
  font-weight: normal;
  font-style: normal;
}`;

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const ralewayExtraBold = (path) => `
@font-face {
  font-family: "Raleway-ExtraBold";
  src: url("${path}.eot"); /* IE9 Compat Modes */
  src: url("${path}.eot?#iefix") format("embedded-opentype"), /* IE6-IE8 */
    url("${path}.otf") format("opentype"), /* Open Type Font */
    url("${path}.svg") format("svg"), /* Legacy iOS */
    url("${path}.ttf") format("truetype"), /* Safari, Android, iOS */
    url("${path}.woff") format("woff"), /* Modern Browsers */
    url("${path}.woff2") format("woff2"); /* Modern Browsers */
  font-weight: normal;
  font-style: normal;
}`;

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const localFonts = {
    "fonts/Raleway/Raleway-ExtraBold": ralewayExtraBold,
    "fonts/Raleway/Raleway-Medium": ralewayMedium,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const primaryFontFamily = '"Raleway-ExtraBold"';
const secondaryFontFamily = '"Raleway-Medium"';
const SDKDefault = {
    name: "sdk-default",
    label: "SDK Default",
    stylesheets: ["https://fonts.googleapis.com/css?family=Raleway:500,800&display=swap"],
    localFonts,
    licenses: ["fonts/Raleway/OFL.txt"],
    iconSet: fontAwesomeSolid,
    palette: {
        primary: {
            main: "#7C4DFF",
        },
        error: {
            light: "#e57373",
            main: "#C44539",
            dark: "#d32f2f",
        },
        secondary: {
            main: "#18A9E6",
            dark: "#083575",
            light: "#01C9EA",
        },
        background: {
            default: "#FFFFFF",
        },
        text: {
            primary: "#4E4E4E",
            secondary: "#4E4E4E",
        },
    },
    spacing: 10,
    typography: {
        fontWeightLight: 500,
        fontWeightMedium: 500,
        fontWeightBold: 800,
        h1: {
            fontFamily: primaryFontFamily,
            fontWeight: 800,
            fontSize: "1.375rem",
            lineHeight: 1.45,
        },
        h2: {
            fontFamily: primaryFontFamily,
            fontWeight: 800,
            fontSize: "1.125rem",
            lineHeight: 1.27,
        },
        h3: {
            fontFamily: primaryFontFamily,
            fontWeight: 800,
            fontSize: "1.05rem",
            lineHeight: 1.2,
        },
        h4: {
            fontFamily: primaryFontFamily,
            fontWeight: 800,
            fontSize: "1rem",
            lineHeight: 1.1,
        },
        h5: {
            fontFamily: primaryFontFamily,
            fontSize: "1rem",
            fontWeight: 800,
            lineHeight: 1,
        },
        h6: {
            fontFamily: primaryFontFamily,
            fontSize: "0.625rem",
            fontWeight: 800,
            lineHeight: 1,
        },
        body1: {
            fontFamily: primaryFontFamily,
            fontWeight: 800,
        },
        body2: {
            fontFamily: secondaryFontFamily,
            fontWeight: 500,
            fontSize: "0.9375rem",
        },
    },
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const defaultTheme = createPrismTheme(SDKDefault);
function makeStyles(style, options) {
    return makeStyles$1(style, { ...options, defaultTheme });
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function getModifier(base, modifier) {
    if (!modifier)
        return [];
    if (typeof modifier === "string")
        return [`${base}--${modifier}`];
    return Object.entries(modifier).map(([value, key]) => (key ? `${base}--${value}` : ""));
}
function bem(classes, base, ...modifiers) {
    const baseModifiers = modifiers.map((modifier) => getModifier(base, modifier));
    return [base]
        .concat(...baseModifiers)
        .map((className) => classes[className])
        .join(" ")
        .trim();
}
const normalizedButton = {
    background: "none",
    border: 0,
    cursor: "pointer",
    display: "inline-block",
    fontFamily: "inherit",
    fontSize: "100%",
    margin: 0,
    lineHeight: 1,
    overflow: "visible",
    padding: 0,
    textAlign: "left",
    textDecoration: "none",
    textTransform: "none",
    width: "auto",
};
function useBem(classes) {
    return (base, ...modifiers) => bem(classes, base, ...modifiers);
}
function useCss() {
    return (base, ...modifiers) => {
        const block = `prism-${base}`;
        return [block]
            .concat(...modifiers.map((modifier) => getModifier(block, modifier)))
            .join(" ")
            .trim();
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles = makeStyles((theme) => createStyles(actionButton.styles(theme)));
function ActionButton({ children, id, label, ...menuProps }) {
    const bem = useBem(useStyles());
    const [anchorEl, setAnchorEl] = React.useState(null);
    const items = React.Children.map(children, (child, index) => {
        const props = child.props;
        const disabledElement = !(props === null || props === void 0 ? void 0 : props.variant);
        const disabledButton = !!(props === null || props === void 0 ? void 0 : props.variant) && (props === null || props === void 0 ? void 0 : props.disabled);
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        const onClick = (props === null || props === void 0 ? void 0 : props.onClick) || (() => { });
        return {
            disabledElement,
            disabledButton,
            element: (props === null || props === void 0 ? void 0 : props.onClick) ? React.cloneElement(child, {
                onClick: () => null,
            })
                : child,
            key: (props === null || props === void 0 ? void 0 : props.key) || `${id}-${index}`,
            onClick,
        };
    });
    const handleClick = (event) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { variant: "icon-only", onClick: handleClick, className: bem("action-button") },
            React.createElement(Icon, { name: "more", "aria-label": label || "Show menu" })),
        React.createElement(Menu, Object.assign({ id: id, anchorEl: anchorEl, keepMounted: true, open: Boolean(anchorEl), onClose: handleClose, getContentAnchorEl: null, anchorOrigin: {
                vertical: "top",
                horizontal: "left",
            }, transformOrigin: {
                vertical: "top",
                horizontal: "right",
            } }, menuProps), items.map(({ element, disabledElement, disabledButton, key, onClick }) => (React.createElement(MenuItem, { className: bem("action-button__menu-item", {
                "disabled-element": disabledElement,
                "disabled-button": disabledButton,
            }), onClick: (...args) => {
                onClick(...args);
                handleClose();
            }, key: key, disabled: disabledElement || disabledButton }, element))))));
}
ActionButton.useBem = () => useBem(useStyles());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$2(theme) {
    return {
        "action-menu": {
            color: theme.palette.text.primary,
            fontFamily: theme.typography.body.fontFamily,
            margin: 0,
            padding: 0,
            listStyleType: "none",
        },
        "action-menu__item": {},
        "action-menu__trigger": {
            ...normalizedButton,
            color: theme.palette.primary.main,
            fontFamily: theme.typography.body.fontFamily,
            fontSize: theme.typography.body.fontSize,
            fontWeight: theme.typography.body.fontWeight,
            lineHeight: 2,
            textDecoration: "underline",
            textTransform: "uppercase",
        },
        "action-menu__trigger--danger": {
            color: theme.palette.error.main,
        },
        "action-menu__trigger--prompt": {
            position: "relative",
            "&::after": {
                content: "'>'",
                position: "absolute",
                fontSize: "0.8em",
                right: "-1em",
                top: "0.15em",
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const actionMenu = {
    name: "actionMenu",
    label: "Action Menu",
    status: PatternStatus.Experimental,
    styles: styles$2,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$1 = makeStyles((theme) => createStyles(actionMenu.styles(theme)));
function ActionMenuButton({ children, className, danger, prompt, ...props }) {
    const bem = useBem(useStyles$1());
    return (React.createElement("button", Object.assign({ className: clsx(bem("action-menu__trigger", { danger, prompt }), className) }, props), children));
}
function ActionMenuLink({ children, className, danger, prompt, ...props }) {
    const bem = useBem(useStyles$1());
    return (React.createElement("a", Object.assign({ className: clsx(bem("action-menu__trigger", { danger, prompt }), className) }, props), children));
}
function ActionMenuItem({ children }) {
    const bem = useBem(useStyles$1());
    return React.createElement("li", { className: bem("action-menu__item") }, children);
}
function ActionMenu({ children }) {
    const bem = useBem(useStyles$1());
    return React.createElement("ul", { className: bem("action-menu") }, children);
}
ActionMenu.useBem = () => {
    return useBem(useStyles$1());
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$3(theme) {
    return {
        "back-to-nav": {
            display: "flex",
            fontFamily: theme.typography.body.fontFamily,
            listStyleType: "none",
            margin: theme.spacing(1, 0),
            padding: 0,
            overflow: "auto",
        },
        "back-to-nav__caret": {
            margin: theme.spacing(0, 0.5),
        },
        "back-to-nav__link": {
            color: theme.palette.text.primary,
            lineHeight: 1.1,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const breadcrumb = {
    name: "backToNav",
    label: "Back-to Navigation",
    status: PatternStatus.Experimental,
    styles: styles$3,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$2 = makeStyles((theme) => createStyles(breadcrumb.styles(theme)));
function BackToNavLink({ children, className, ...props }) {
    const bem = useBem(useStyles$2());
    return (React.createElement("a", Object.assign({}, props, { className: clsx(bem("back-to-nav__link"), className) }),
        "Back to ",
        children));
}
function BackToNav({ children, className, href, link, ...props }) {
    const bem = useBem(useStyles$2());
    return (React.createElement("nav", { className: bem("back-to-nav") },
        React.createElement(Icon, { name: "caret-left", className: bem("back-to-nav__caret") }),
        link || (React.createElement(BackToNavLink, Object.assign({ className: className, href: href }, props), children))));
}
BackToNav.useBem = () => {
    return useBem(useStyles$2());
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$4(theme) {
    return {
        breadcrumb: {
            display: "flex",
            fontFamily: theme.typography.body.fontFamily,
            listStyleType: "none",
            margin: theme.spacing(1, 0),
            padding: 0,
            overflow: "auto",
        },
        // eslint-disable-next-line @typescript-eslint/camelcase
        breadcrumb__caret: {
            display: "none",
        },
        "breadcrumb__caret--small": {
            margin: theme.spacing(0, 0.5),
            display: "inline-block",
            [theme.breakpoints.up("sm")]: {
                display: "none",
            },
        },
        "breadcrumb__caret--large": {
            margin: theme.spacing(0, 0.5),
            [theme.breakpoints.up("sm")]: {
                display: "inline-block",
            },
        },
        // eslint-disable-next-line @typescript-eslint/camelcase
        breadcrumb__crumb: {
            display: "none",
            whiteSpace: "nowrap",
            "&:nth-last-child(1)": {
                display: "inline-block",
            },
            [theme.breakpoints.up("xs")]: {
                "&:nth-last-child(2)": {
                    display: "inline-block",
                },
                "&:last-child": {
                    display: "none",
                },
            },
            [theme.breakpoints.up("sm")]: {
                "&:first-child, &:nth-last-child(1), &:nth-last-child(2), &:nth-last-child(3)": {
                    display: "inline-block",
                },
                "&:last-child": {
                    "& $breadcrumb__caret--large": {
                        display: "none",
                    },
                },
                "&:nth-last-child(5) + $breadcrumb__crumb": {
                    display: "inline-block",
                    "& *": {
                        display: "none",
                    },
                    "& $breadcrumb__caret--large": {
                        display: "inline-block",
                    },
                    "&::before": {
                        content: '"…"',
                    },
                },
            },
        },
        // eslint-disable-next-line @typescript-eslint/camelcase
        breadcrumb__link: {
            color: theme.palette.text.primary,
        },
        // eslint-disable-next-line @typescript-eslint/camelcase
        breadcrumb__prefix: {
            [theme.breakpoints.up("sm")]: {
                display: "none",
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const icons = {
    name: "icons",
    label: "Icons",
    status: PatternStatus.Experimental,
    styles: (theme) => theme.icons.styles(theme),
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function expandPattern(config) {
    if (!config.dependencies) {
        return config;
    }
    return {
        ...config,
        styles: (theme, options) => {
            if (options && options.original) {
                return config.styles(theme);
            }
            return config.dependencies.concat(config).reduce((memo, currentConfig) => {
                return Object.assign(memo, currentConfig.styles(theme, { original: true }));
            }, {});
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const breadcrumb$1 = expandPattern({
    name: "breadcrumb",
    label: "Breadcrumb",
    status: PatternStatus.Experimental,
    dependencies: [icons],
    styles: styles$4,
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$3 = makeStyles((theme) => createStyles(breadcrumb$1.styles(theme)));
function Breadcrumbs({ crumbs }) {
    const bem = useBem(useStyles$3());
    return (React.createElement("nav", { "aria-label": "Breadcrumbs" },
        React.createElement("ol", { className: bem("breadcrumb"), id: "Breadcrumb" }, crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            const { label, href, ...props } = crumb;
            return (React.createElement("li", { key: label, className: bem("breadcrumb__crumb") },
                React.createElement("i", { className: clsx(bem("icon-caret-left"), bem("breadcrumb__caret", "small")) }),
                isLast ? (React.createElement("span", Object.assign({}, props), label)) : (React.createElement("a", Object.assign({ href: href }, props, { className: clsx(bem("breadcrumb__link", isLast ? "active" : null), props.className) }, (isLast ? { "aria-current": "location" } : {})),
                    React.createElement("span", { className: bem("breadcrumb__prefix") }, "Back to "),
                    label)),
                React.createElement("i", { className: clsx(bem("icon-caret-right"), bem("breadcrumb__caret", "large")) })));
        }))));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$5(theme) {
    const buttonInMenuStyles = {
        background: "transparent",
        borderRadius: 0,
        color: theme.palette.text.primary,
        border: 0,
        display: "block",
        fontFamily: theme.typography.body2.fontFamily,
        fontSize: "1rem",
        fontWeight: theme.typography.fontWeightLight,
        lineHeight: 1.3,
        padding: 0,
        textTransform: "none",
        maxWidth: 200,
        whiteSpace: "initial",
        wordBreak: "break-word",
        textAlign: "left",
        "&:hover, &:focus": {
            background: "transparent",
        },
        "&:disabled, &:disabled:hover, &:disabled:focus": {
            backgroundColor: theme.palette.grey["300"],
            color: theme.palette.text.primary,
        },
        "& $button__icon": theme.icons.iconColor(theme.palette.text.primary),
    };
    return {
        button: {
            ...normalizedButton,
            background: theme.palette.primary.main,
            fontFamily: theme.typography.h1.fontFamily,
            fontSize: theme.typography.body1.fontSize,
            fontWeight: theme.typography.h1.fontWeight,
            color: theme.palette.background.default,
            borderRadius: 10,
            border: 0,
            cursor: "pointer",
            textTransform: "uppercase",
            padding: theme.spacing(1.5, 2),
            transition: "0.25s all ease-in-out",
            lineHeight: 1.6,
            margin: 0,
            letterSpacing: "1.07px",
            maxWidth: "100%",
            wordBreak: "break-word",
            textAlign: "center",
            "&:active": {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: `0 0 0 2px ${theme.palette.warning.light}`,
            },
            "&:focus": {
                boxShadow: `0 0 0 2px ${theme.palette.warning.light}`,
                outline: "none",
            },
            "&:hover": {
                backgroundColor: theme.palette.primary.dark,
            },
        },
        button__icon: {
            ...theme.icons.iconColor(theme.palette.background.default),
        },
        "button__icon--before": {
            marginRight: theme.spacing(0.8),
        },
        "button__icon--after": {
            marginLeft: theme.spacing(0.8),
        },
        button__text: {},
        "button__disabled-text": {
            display: "block",
            fontSize: "0.8125rem",
        },
        "button--secondary": {
            background: "transparent",
            color: theme.palette.primary.main,
            textDecoration: "underline",
            "&:hover": {
                background: adjustAlpha(theme.palette.primary.light, 0.2),
            },
            "&:active": {
                background: adjustAlpha(theme.palette.primary.light, 0.3),
            },
            "& $button__icon": theme.icons.iconColor(theme.palette.primary.main),
        },
        "button--small": {
            background: adjustAlpha(theme.palette.primary.light, 0.1),
            borderRadius: 7,
            color: theme.palette.primary.main,
            fontSize: "0.8125rem",
            lineHeight: "1.5rem",
            padding: theme.spacing(0.6, 1.2),
            "&:hover": {
                background: adjustAlpha(theme.palette.primary.light, 0.2),
            },
            "&:active": {
                background: adjustAlpha(theme.palette.primary.light, 0.3),
            },
            "& $button__icon": theme.icons.iconColor(theme.palette.primary.main),
        },
        "button--text": {
            background: "transparent",
            borderRadius: 7,
            color: theme.palette.primary.main,
            fontSize: "0.8125rem",
            lineHeight: "1.5rem",
            padding: theme.spacing(0.6, 1.2),
            "&:hover": {
                background: adjustAlpha(theme.palette.primary.light, 0.2),
            },
            "&:active": {
                background: adjustAlpha(theme.palette.primary.light, 0.3),
            },
            "& $button__icon": theme.icons.iconColor(theme.palette.primary.main),
        },
        "button--with-input": {
            margin: theme.spacing(0.5, 0),
            padding: theme.spacing(1.2, 1.6),
            wordBreak: "normal",
        },
        "button--danger": {
            color: theme.palette.error.main,
            "& $button__icon": theme.icons.iconColor(theme.palette.error.main),
        },
        "button--icon-only": {
            background: "transparent",
            borderRadius: "50%",
            color: theme.palette.primary.main,
            lineHeight: "1.5rem",
            padding: theme.spacing(0.6, 0),
            display: "flex",
            justifyContent: "center",
            width: 36,
            "&:hover": {
                background: adjustAlpha(theme.palette.primary.light, 0.2),
            },
            "&:active": {
                background: adjustAlpha(theme.palette.primary.light, 0.3),
            },
            "& $button__icon": theme.icons.iconColor(theme.palette.primary.main),
        },
        "button--in-menu": buttonInMenuStyles,
        "button--in-menu-danger": {
            ...buttonInMenuStyles,
            color: theme.palette.error.main,
            fontFamily: theme.typography.body1.fontFamily,
            fontWeight: theme.typography.fontWeightBold,
            "& $button__icon": theme.icons.iconColor(theme.palette.error.main),
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const button = expandPattern({
    name: "button",
    label: "Button",
    status: PatternStatus.Experimental,
    dependencies: [icons],
    styles: styles$5,
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$4 = makeStyles((theme) => createStyles(icons.styles(theme)));
function Icon({ className, label, name, ...props }) {
    const bem = useBem(useStyles$4());
    return (React.createElement("span", Object.assign({}, props, { className: clsx(bem("icon"), bem(`icon-${name}`), className) }, (label ? { "aria-label": label } : {}))));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$5 = makeStyles((theme) => createStyles(button.styles(theme)));
function ButtonContent({ children, disabledText, iconBefore, iconAfter, iconProps }) {
    const bem = useBem(useStyles$5());
    return (React.createElement(React.Fragment, null,
        iconBefore && React.createElement(Icon, Object.assign({ name: iconBefore, className: bem("button__icon", "before") }, iconProps)),
        children && React.createElement("span", { className: bem("button__text") }, children),
        iconAfter && React.createElement(Icon, Object.assign({ name: iconAfter, className: bem("button__icon", "after") }, iconProps)),
        disabledText && (React.createElement("span", { className: bem("button__disabled-text") },
            React.createElement(Icon, { name: "info" }),
            "\u00A0",
            disabledText))));
}
const Button = React.forwardRef(function ButtonRef({ className, variant, ...props }, ref) {
    const bem = useBem(useStyles$5());
    const { disabledText, iconBefore, iconAfter, iconProps, ...safeProps } = props;
    return (React.createElement("button", Object.assign({ className: clsx(bem("button", variant), className), ref: ref }, safeProps),
        React.createElement(ButtonContent, Object.assign({ disabledText: disabledText, iconBefore: iconBefore, iconAfter: iconAfter, iconProps: iconProps }, props))));
});
Button.useBem = () => useBem(useStyles$5());
const LinkButton = React.forwardRef(function LinkButtonRef({ className, variant, ...props }, ref) {
    const bem = useBem(useStyles$5());
    const { iconBefore, iconAfter, ...safeProps } = props;
    return (React.createElement("a", Object.assign({ className: clsx(bem("button", variant), className), ref: ref }, safeProps),
        React.createElement(ButtonContent, Object.assign({ iconBefore: iconBefore, iconAfter: iconAfter }, props))));
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function getContainer(theme) {
    return {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        [theme.breakpoints.up("md")]: {
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
        },
    };
}
function styles$6(theme) {
    return {
        container: getContainer(theme),
        "container--empty": {
            paddingTop: "min(64px, 20%)",
            paddingBottom: "min(64px, 20%)",
            textAlign: "center",
            fallbacks: {
                paddingTop: 64,
                paddingBottom: 64,
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const container = {
    name: "container",
    label: "Container",
    status: PatternStatus.Experimental,
    styles: styles$6,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$6 = makeStyles((theme) => createStyles(container.styles(theme)));
function Container({ children, className, variant }) {
    const bem = useBem(useStyles$6());
    return React.createElement("div", { className: clsx(bem("container", variant), className) }, children);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$7(theme) {
    return {
        content: {
            color: theme.palette.text.primary,
            wordBreak: "break-word",
            "& blockquote": {
                extend: "content-blockquote",
            },
            "& h1": {
                extend: "content-h1",
            },
            "& h2": {
                extend: "content-h2",
            },
            "& h3": {
                extend: "content-h3",
            },
            "& h4": {
                extend: "content-h4",
            },
            "& h5": {
                extend: "content-h5",
            },
            "& h6": {
                extend: "content-h6",
            },
            "& img": {
                extend: "image",
            },
            "& p": {
                extend: "content-p",
            },
            "& ul": {
                extend: "content-ul",
            },
            "& ol": {
                extend: "content-ol",
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$8() {
    return {
        image: {
            maxHeight: "100%",
            maxWidth: "100%",
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const image = {
    name: "image",
    label: "Image",
    status: PatternStatus.Experimental,
    styles: styles$8,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const titleLink = {
    textTransform: "none",
};
function generateTitle(elementType, theme) {
    return {
        ...theme.typography[elementType],
        color: theme.palette.text.primary,
        "& a": {
            titleLink,
        },
    };
}
function getInlineElements(theme) {
    return {
        "& a": {
            color: theme.palette.primary.main,
            textDecoration: "underline",
            '& > [data-external="true"]': {
                ...theme.icons.iconColor(theme.palette.primary.main),
                paddingLeft: theme.spacing(0.25),
            },
        },
        "& b": {
            fontSize: "1rem",
            fontWeight: theme.typography.fontWeightBold,
        },
        "& em": {
            fontStyle: "italic",
        },
        "& small": {
            fontSize: "0.625rem",
            fontWeight: theme.typography.fontWeightLight,
        },
        "& strong": {
            fontSize: "1rem",
            fontWeight: theme.typography.fontWeightBold,
        },
    }; // TODO: https://github.com/inrupt/prism/issues/24
}
function styles$9(theme) {
    const inlineElements = getInlineElements(theme);
    const list = {
        "& li": {
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontSize: "1em",
            lineHeight: 1.6,
            fontWeight: 300,
            ...inlineElements,
        },
    };
    return {
        "content-h1": generateTitle("h1", theme),
        "content-h2": generateTitle("h2", theme),
        "content-h3": generateTitle("h3", theme),
        "content-h4": generateTitle("h4", theme),
        "content-h5": generateTitle("h5", theme),
        "content-h6": generateTitle("h6", theme),
        "content-ul": list,
        "content-ol": list,
        "content-blockquote": {
            color: theme.palette.text.primary,
            fontStyle: "italic",
            borderLeft: `solid 1px ${theme.palette.grey["800"]}`,
            margin: "1em 0",
            paddingLeft: "1em",
            ...inlineElements,
        },
        "content-p": {
            color: theme.palette.text.primary,
            fontFamily: theme.typography.body.fontFamily,
            fontWeight: theme.typography.body.fontWeight,
            lineHeight: 1.6,
            ...inlineElements,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const typography = {
    name: "typography",
    label: "Typography",
    status: PatternStatus.Experimental,
    styles: styles$9,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const content = expandPattern({
    name: "content",
    label: "Content",
    dependencies: [image, typography],
    status: PatternStatus.Experimental,
    styles: styles$7,
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$7 = makeStyles((theme) => createStyles(content.styles(theme)));
function Content({ children }) {
    const bem = useBem(useStyles$7());
    return React.createElement("div", { className: bem("content") }, children);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const ESC_KEYCODE = 27;
function handleEscKeydown(callback) {
    return (event) => {
        if (event.keyCode === ESC_KEYCODE || event.code === ESC_KEYCODE.toString())
            return callback(event);
    };
}
function useEscKey(callback) {
    return useEffect(() => {
        const escapeKeyEventListener = handleEscKeydown(callback);
        document.body.addEventListener("keydown", escapeKeyEventListener);
        return () => document.body.removeEventListener("keydown", escapeKeyEventListener);
    });
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/* eslint @typescript-eslint/camelcase: 0 */
function styles$a(theme) {
    const drawerArticle = {
        borderBottom: `solid 1px ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 2),
    };
    return {
        drawer: {
            backgroundColor: theme.palette.background.default,
            display: "none",
            flexGrow: 0,
        },
        "drawer--open": {
            display: "inline-flex",
            width: "90%",
            [theme.breakpoints.up("sm")]: {
                width: "50%",
            },
            [theme.breakpoints.up("lg")]: {
                width: "33.33%",
            },
        },
        drawer__article: drawerArticle,
        "drawer__article--close-button": {
            display: "flex",
            justifyContent: "flex-end",
        },
        "drawer__content-wrapper": {
            position: "sticky",
            top: 0,
        },
        drawer__content: {
            borderLeftStyle: "solid",
            borderLeftWidth: theme.spacing(1),
            borderLeftColor: theme.palette.grey["50"],
            width: "100%",
            "& article": drawerArticle,
        },
        "drawer__close-button": {
            color: theme.palette.text.primary,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const drawer = {
    name: "drawer",
    label: "Drawer",
    status: PatternStatus.Experimental,
    styles: styles$a,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$8 = makeStyles((theme) => createStyles(drawer.styles(theme)));
function Drawer({ children, open, close }) {
    useEscKey(close);
    const bem = useBem(useStyles$8());
    const closeButtonRef = useRef(null);
    useEffect(() => {
        var _a;
        if (open) {
            (_a = closeButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [open]);
    return (React.createElement("section", { className: bem("drawer", open ? "open" : null) },
        React.createElement("div", { className: bem("drawer__content") },
            React.createElement("div", { className: bem("drawer__content-wrapper") },
                React.createElement("div", { className: bem("drawer__article", "close-button") },
                    React.createElement(Button, { onClick: close, variant: "text", ref: closeButtonRef, className: bem("drawer__close-button") },
                        React.createElement(Icon, { name: "cancel" }),
                        " Close")),
                children))));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$b(theme) {
    return {
        "drawer-container": {
            display: "flex",
            flexGrow: 1,
        },
        "drawer-container__content": {
            flexGrow: 1,
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            [theme.breakpoints.up("md")]: {
                paddingLeft: theme.spacing(4),
                paddingRight: theme.spacing(4),
            },
        },
        "drawer-container__content--open-drawer": {
            marginRight: "-75%",
            paddingRight: 0,
            [theme.breakpoints.up("sm")]: {
                marginRight: 0,
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const drawer$1 = {
    name: "drawer",
    label: "Drawer",
    status: PatternStatus.Experimental,
    styles: styles$b,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$9 = makeStyles((theme) => createStyles(drawer$1.styles(theme)));
function DrawerContainer({ children, drawer, open }) {
    const bem = useBem(useStyles$9());
    return (React.createElement("div", { className: bem("drawer-container") },
        React.createElement("div", { className: bem("drawer-container__content", open ? "open-drawer" : null) }, children),
        drawer));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const FOOTER_HEIGHT = 60;
/* eslint @typescript-eslint/camelcase:0 */
function styles$c(theme) {
    const rawStyles = {
        footer: {
            background: theme.palette.background.default,
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.body.fontFamily,
            boxShadow: `0px -1px 4px ${theme.palette.grey["500"]}`,
            minHeight: FOOTER_HEIGHT - theme.spacing(4),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row",
            padding: theme.spacing(1),
            zIndex: theme.zIndex.appBar,
        },
        footer__info: {
            padding: theme.spacing(1),
            textAlign: "center",
        },
        "footer__info-text": {
            borderLeft: `solid 1px ${theme.palette.divider}`,
            display: "inline-block",
            padding: theme.spacing(0.5, 2),
            "&:first-child": {
                borderLeft: 0,
                paddingLeft: 0,
            },
            "&:last-child": {
                paddingRight: 0,
            },
        },
        "footer__info-icon": Object.assign({}, theme.icons.iconColor(theme.palette.text.secondary), {
            display: "inline",
        }),
        footer__links: {
            padding: theme.spacing(1),
            textAlign: "center",
        },
        footer__link: {
            borderRight: `solid 1px ${theme.palette.divider}`,
            color: theme.palette.primary.main,
            display: "inline-block",
            padding: theme.spacing(0.5, 2),
            "&:first-child": {
                paddingLeft: 0,
            },
            "&:last-child": {
                borderRight: 0,
                paddingRight: 0,
            },
        },
        "footer__link-icon": Object.assign({}, theme.icons.iconColor(theme.palette.primary.main), {
            display: "inline",
        }),
    };
    ["xs", "sm", "md", "lg", "xl"].forEach((breakpoint) => {
        rawStyles[`footer--${breakpoint}`] = {
            [theme.breakpoints.up(0)]: {
                flexDirection: "column",
            },
            [theme.breakpoints.up(breakpoint)]: {
                flexDirection: "row",
            },
        };
        rawStyles["footer__info"]["& + $footer__links"] = {
            [theme.breakpoints.up(breakpoint)]: {
                marginTop: 0,
            },
        };
    });
    return rawStyles;
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const footer = {
    name: "footer",
    label: "Footer",
    status: PatternStatus.Experimental,
    styles: styles$c,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$a = makeStyles((theme) => createStyles(footer.styles(theme)));
function Footer({ className, links, modifier, texts, ...otherProps }) {
    const bem = useBem(useStyles$a());
    return (React.createElement("footer", Object.assign({ className: clsx(bem("footer", modifier), className) }, otherProps),
        React.createElement("div", { className: bem("footer__info") }, texts.map(({ icon, text, ...props }) => (React.createElement("span", Object.assign({ key: text, className: bem("footer__info-text") }, props),
            icon ? (React.createElement(React.Fragment, null,
                React.createElement("i", { className: clsx(bem(icon), bem("footer__info-icon")) }),
                "\u00A0")) : null,
            text)))),
        React.createElement("div", { className: bem("footer__links") }, links.map(({ icon, text, ...props }) => (React.createElement("a", Object.assign({ key: text, className: bem("footer__link") }, props),
            icon ? (React.createElement(React.Fragment, null,
                React.createElement("i", { className: clsx(bem(icon), bem("footer__link-icon")) }),
                "\u00A0")) : null,
            text))))));
}
Footer.useBem = () => {
    return useBem(useStyles$a());
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$d(theme) {
    return {
        form: {
            fontFamily: theme.typography.body.fontFamily,
            fontWeight: theme.typography.fontWeightMedium,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const form = {
    name: "form",
    label: "Form",
    status: PatternStatus.Experimental,
    styles: styles$d,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$b = makeStyles((theme) => createStyles(form.styles(theme)));
function Form({ children, className, ...props }) {
    const bem = useBem(useStyles$b());
    return (React.createElement("form", Object.assign({ className: clsx(bem("form"), className) }, props), children));
}
Form.useBem = () => useBem(useStyles$b());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const cssBem = useCss();
const bem$1 = cssBem;
const preventDefaultOnClick = (event) => event.preventDefault();

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function Header({ children }) {
    return React.createElement("header", { className: bem$1("header") }, children);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$e(theme) {
    return {
        "main-nav": {
            background: theme.palette.background.default,
            fontFamily: theme.typography.body.fontFamily,
            height: "100%",
        },
        "main-nav__list": {
            display: "flex",
            listStyleType: "none",
            height: "100%",
            margin: 0,
            padding: 0,
        },
        "main-nav__item": {
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            flexGrow: 1,
            minHeight: 30,
            lineHeight: 1,
            [theme.breakpoints.up("sm")]: {
                flexGrow: 0,
            },
        },
        "main-nav__link": {
            border: `solid 0 ${theme.palette.background.default}`,
            borderTopWidth: 3,
            display: "flex",
            flexDirection: "row",
            fontWeight: theme.typography.fontWeightBold,
            width: "100%",
            alignItems: "center",
            padding: theme.spacing(0, 2),
            cursor: "pointer",
            color: theme.palette.text.primary,
            fontSize: "1rem",
            textAlign: "center",
            textDecoration: "none",
            "&:active, &:focus, &:hover": {
                borderColor: theme.palette.primary.main,
            },
            [theme.breakpoints.up("sm")]: {
                borderWidth: "0 0 3px 0",
            },
        },
        "main-nav__link--active": {
            borderColor: theme.palette.primary.main,
        },
        "main-nav__icon": {
            ...theme.icons.iconColor(theme.palette.primary.main),
            fontSize: "1.25rem",
        },
        "main-nav__label": {
            marginLeft: "0.5rem",
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$c = makeStyles((theme) => createStyles(styles$e(theme)));
const MainNavLink = forwardRef(function MainNavLinkComponent({ active, icon, onClick, href, text, ...props }, ref) {
    const bem = useBem(useStyles$c());
    const iconName = icon.replace(/icon-/, "");
    return (React.createElement("a", Object.assign({ className: bem("main-nav__link", active ? "active" : null), href: href, onClick: onClick, ref: ref }, props),
        React.createElement(Icon, { name: iconName, className: bem("main-nav__icon") }),
        React.createElement("span", { className: bem("main-nav__label") }, text)));
});
function MainNav({ className, links, renderLink, ...props }) {
    const bem = useBem(useStyles$c());
    return (React.createElement("nav", Object.assign({ className: clsx(bem("main-nav"), className) }, props),
        React.createElement("ul", { className: bem("main-nav__list") }, links === null || links === void 0 ? void 0 : links.map((link) => (React.createElement("li", { className: bem("main-nav__item"), key: link.text }, renderLink ? renderLink(link) : React.createElement(MainNavLink, Object.assign({}, link))))))));
}
MainNav.defaultProps = {
    className: null,
    links: [],
    renderLink: null,
};
MainNav.useBem = () => useBem(useStyles$c());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function useOutsideClick(ref, callback) {
    const handleClick = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            callback();
        }
    };
    useEffect(() => {
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [handleClick]);
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const HEADER_HEIGHT = 60;

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$f(theme) {
    return {
        "user-menu": {
            flexGrow: 0,
            position: "relative",
        },
        "user-menu__trigger": {
            ...normalizedButton,
            alignItems: "center",
            background: theme.palette.background.default,
            border: 0,
            borderRadius: 0,
            cursor: "pointer",
            display: "flex",
            fontFamily: theme.typography.body.fontFamily,
            gap: "0.5rem",
            height: "100%",
            padding: theme.spacing(0, 2),
            width: "auto",
        },
        "user-menu__trigger-icon": {
            ...theme.icons.iconColor(theme.palette.primary.main),
            width: "auto",
        },
        "user-menu__trigger-profile-image": {
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "50%",
            display: "block",
            height: 20,
            width: 20,
        },
        "user-menu__trigger-label": {
            maxWidth: "15ch",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        "user-menu__trigger-chevron": {
            display: "none",
            fontSize: 18,
        },
        "user-menu__trigger-chevron--show": {
            display: "block",
        },
        "user-menu__popup": {
            background: theme.palette.background.default,
            borderColor: `solid 1px ${theme.palette.grey["200"]}`,
            boxShadow: `0px 1px 4px ${theme.palette.grey["500"]}`,
            display: "none",
            position: "absolute",
            right: 0,
            top: HEADER_HEIGHT,
            "&[aria-hidden=false]": {
                display: "block",
            },
        },
        "user-menu__list": {
            display: "flex",
            flexDirection: "column",
            listStyleType: "none",
            margin: 0,
            padding: 0,
        },
        "user-menu__list-item": {
            display: "flex",
            flexDirection: "row",
            whiteSpace: "nowrap",
        },
        "user-menu__list-item-divider": {
            border: 0,
            borderTop: `solid 1px ${theme.palette.grey["400"]}`,
            height: 0,
            margin: 0,
            width: "100%",
        },
        "user-menu__list-item-icon": {
            ...theme.icons.iconColor(theme.palette.text.secondary),
        },
        "user-menu__list-item-trigger": {
            ...normalizedButton,
            background: theme.palette.background.default,
            border: 0,
            color: theme.palette.text.secondary,
            cursor: "pointer",
            display: "flex",
            flexDirection: "row",
            justifyContent: "left",
            padding: theme.spacing(2),
            textDecoration: "none",
            width: "100%",
            "&:focus, &:hover": {
                background: theme.palette.grey["100"],
            },
        },
        "user-menu__list-item-label": {
            paddingLeft: theme.spacing(2),
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$d = makeStyles((theme) => createStyles(styles$f(theme)));
const UserMenuLink = forwardRef(function UserMenuLinkComponent({ className, icon, label, ...props }, ref) {
    const bem = useBem(useStyles$d());
    const iconName = icon.replace(/icon-/, "");
    return (React.createElement("a", Object.assign({ className: clsx(bem("user-menu__list-item-trigger"), className), ref: ref }, props),
        React.createElement(Icon, { name: iconName, className: bem("user-menu__list-item-icon") }),
        React.createElement("span", { className: bem("user-menu__list-item-label") }, label)));
});
const UserMenuButton = forwardRef(function UserMenuButtonComponent({ className, icon, label, ...props }, ref) {
    const bem = useBem(useStyles$d());
    const iconName = icon.replace(/icon-/, "");
    return (React.createElement("button", Object.assign({ className: clsx(bem("user-menu__list-item-trigger"), className), type: "button", ref: ref }, props),
        React.createElement(Icon, { name: iconName, className: bem("user-menu__list-item-icon") }),
        React.createElement("span", { className: bem("user-menu__list-item-label") }, label)));
});
function UserMenu({ className, icon, imgSrc, label, menuId, menu, renderAction, ...props }) {
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = createRef();
    useOutsideClick(userMenuRef, () => setUserMenuOpen(false));
    const bem = useBem(useStyles$d());
    const iconName = icon === null || icon === void 0 ? void 0 : icon.replace(/icon-/, "");
    return (React.createElement("div", { className: bem("user-menu"), ref: userMenuRef },
        React.createElement("button", Object.assign({ className: clsx(bem("user-menu__trigger"), className), type: "button", "aria-haspopup": "true", "aria-controls": menuId, "aria-expanded": userMenuOpen, onClick: () => setUserMenuOpen(!userMenuOpen) }, props),
            imgSrc ? (React.createElement("span", { className: bem("user-menu__trigger-profile-image"), style: { backgroundImage: `url("${imgSrc}")` } }, "\u00A0")) : (React.createElement(Icon, { name: iconName || "user-circle", className: bem("user-menu__trigger-icon") })),
            React.createElement("span", { className: bem("user-menu__trigger-label") }, label),
            React.createElement(Icon, { name: "caret-down", className: bem("user-menu__trigger-chevron", { show: !userMenuOpen }) }),
            React.createElement(Icon, { name: "caret-up", className: bem("user-menu__trigger-chevron", { show: userMenuOpen }) })),
        React.createElement("div", { className: bem("user-menu__popup"), id: menuId, "aria-hidden": !userMenuOpen },
            React.createElement("ul", { className: bem("user-menu__list") }, menu.map((action) => (React.createElement(Fragment, { key: action.label },
                action.insertDividerBefore ? (React.createElement("li", { className: bem("user-menu__list-item") },
                    React.createElement("hr", { className: bem("user-menu__list-item-divider") }))) : null,
                React.createElement("li", { className: bem("user-menu__list-item") }, renderAction ? renderAction(action) : React.createElement(UserMenuButton, Object.assign({}, action))))))))));
}
UserMenu.useBem = () => useBem(useStyles$d());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$g(theme) {
    return {
        "header-toolbar": {
            flexGrow: 0,
        },
        "header-toolbar__trigger": {
            extend: "main-nav__link",
            alignItems: "center",
            background: theme.palette.background.default,
            border: 0,
            borderRadius: 0,
            fontFamily: theme.typography.body.fontFamily,
            height: "100%",
            padding: theme.spacing(0, 2),
            width: "auto",
        },
        "header-toolbar__trigger-icon": {
            ...theme.icons.iconColor(theme.palette.primary.main),
            width: "auto",
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$e = makeStyles((theme) => createStyles(styles$g(theme)));
function HeaderToolbar({ href, icon, label, onClick }) {
    const bem = useBem(useStyles$e());
    const iconName = icon.replace(/icon-/, "");
    return (React.createElement("div", { className: bem("header-toolbar"), key: label },
        React.createElement("a", { className: bem("header-toolbar__trigger"), href: href, onClick: onClick },
            React.createElement(Icon, { name: iconName, className: bem("header-toolbar__trigger-icon"), "aria-label": label }))));
}
HeaderToolbar.useBem = () => useBem(useStyles$e());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function HeaderBanner({ mainNavLinks, toolbarLinks, userMenu }) {
    return (React.createElement("header", { className: bem$1("header-banner") },
        React.createElement("a", { className: bem$1("header-banner__logo"), href: "#", onClick: preventDefaultOnClick },
            React.createElement("img", { src: "/SolidLogo.svg", className: bem$1("header-banner__logo-image"), alt: "Solid Logo" })),
        React.createElement("div", { className: bem$1("header-banner__main-nav") },
            React.createElement(MainNav, { links: mainNavLinks, className: bem$1("app-layout__header-nav") })), toolbarLinks === null || toolbarLinks === void 0 ? void 0 :
        toolbarLinks.map((link) => (React.createElement(HeaderToolbar, Object.assign({}, link, { key: link.href })))),
        userMenu && React.createElement(UserMenu, Object.assign({}, userMenu))));
}
HeaderBanner.defaultProps = {
    toolbarLinks: [],
    userMenu: [],
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function getHeroTitle(theme) {
    return {
        ...generateTitle("h1", theme),
        fontSize: "2rem !important",
        maxWidth: 700,
        [theme.breakpoints.up("sm")]: {
            fontSize: "4rem !important",
        },
    };
}
function styles$h(theme) {
    return {
        hero: {
            ...getContainer(theme),
            "& h1": getHeroTitle(theme),
        },
        hero__title: getHeroTitle(theme),
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const hero = {
    name: "hero",
    label: "Hero",
    status: PatternStatus.Experimental,
    styles: styles$h,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$f = makeStyles((theme) => createStyles(hero.styles(theme)));
function Hero({ children, className, ...props }) {
    const bem = useBem(useStyles$f());
    return (React.createElement("div", Object.assign({ className: clsx(bem("hero"), className) }, props), children));
}
Hero.useHeroTitleClassName = () => {
    const bem = useBem(useStyles$f());
    return bem("hero__title");
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$i(theme) {
    const activeFocusInput = {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        outline: "none",
    };
    const invalidInput = {
        borderColor: theme.palette.error.main,
        borderWidth: 2,
    };
    const readonlyInput = {
        border: "none",
        pointerEvents: "none",
        background: "none",
        "&:focus": {
            border: "none",
            pointerEvents: "none",
        },
        "&:active": {
            border: "none",
            pointerEvents: "none",
        },
    };
    const disabledInput = {
        pointerEvents: "none",
    };
    return {
        input: {
            borderRadius: 10,
            border: `solid 1px ${theme.palette.divider}`,
            fontFamily: theme.typography.body.fontFamily,
            width: "100%",
            appearance: "none",
            transition: theme.transitions.easing.easeInOut,
            fontSize: "0.8em",
            textIndent: "0.75em",
            display: "flex",
            flexWrap: "wrap",
            maxWidth: "95%",
            margin: theme.spacing(0.5, 0),
            padding: theme.spacing(1.5, 2),
            paddingLeft: 12,
            "&[disabled]": disabledInput,
            "&:not([disabled]):active": activeFocusInput,
            "&:not([disabled]):focus": activeFocusInput,
            "&:not([disabled]):invalid": invalidInput,
            "&[readonly], &:read-only": readonlyInput,
            "&::placeholder": {
                color: theme.palette.text.hint,
            },
        },
        "input--focus": activeFocusInput,
        "input--active": activeFocusInput,
        "input--invalid": invalidInput,
        "input--disabled": disabledInput,
        "input--readonly": readonlyInput,
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const input = expandPattern({
    name: "input",
    label: "Input",
    status: PatternStatus.Experimental,
    dependencies: [form],
    styles: styles$i,
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$j(theme) {
    return {
        "input-group": {
            display: "flex",
            flexDirection: "column",
            marginBottom: theme.spacing(2),
        },
        "input-group__inner": {
            display: "flex",
            flexDirection: "column",
        },
        "input-group__inner--with-button": {
            flexDirection: "row",
            gap: "0.5rem",
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$k(theme) {
    return {
        label: {
            fontFamily: theme.typography.body.fontFamily,
            fontSize: "0.8125rem",
            lineHeight: 1.6,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$g = makeStyles((theme) => createStyles(styles$k(theme)));
function Label({ children, className, ...props }) {
    const bem = useBem(useStyles$g());
    return (React.createElement("label", Object.assign({ className: clsx(bem("label"), className) }, props), children));
}
Label.useBem = () => useBem(useStyles$g());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$h = makeStyles((theme) => createStyles(styles$j(theme)));
function InputGroup({ children, className, id, invalidMessage, label, variant, ...props }) {
    const bem = useBem(useStyles$h());
    return (React.createElement("div", Object.assign({ className: clsx(bem("input-group", variant), className) }, props),
        React.createElement(Label, { htmlFor: id }, label),
        invalidMessage && React.createElement(Message, { variant: "invalid" }, invalidMessage),
        React.createElement("div", { className: bem("input-group__inner", variant) }, children)));
}
InputGroup.useBem = () => useBem(useStyles$h());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$i = makeStyles((theme) => createStyles(input.styles(theme)));
function Input({ id, invalidMessage, label, ...props }) {
    return (React.createElement(InputGroup, { id: id, label: label, invalidMessage: invalidMessage },
        React.createElement(SimpleInput, Object.assign({ id: id, name: id }, props))));
}
Input.useBem = () => {
    return useBem(useStyles$i());
};
function SimpleInput({ className, variant, ...props }) {
    const bem = useBem(useStyles$i());
    return React.createElement("input", Object.assign({ type: "text", className: clsx(bem("input", variant), className) }, props));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$l(theme) {
    return {
        "@keyframes circular-rotate": {
            "100%": {
                transform: "rotate(360deg)",
            },
        },
        "@keyframes circular-dash": {
            "0%": {
                strokeDasharray: "1px, 200px",
                strokeDashoffset: "0px",
            },
            "50%": {
                strokeDasharray: "100px, 200px",
                strokeDashoffset: "-15px",
            },
            "100%": {
                strokeDasharray: "100px, 200px",
                strokeDashoffset: "-125px",
            },
        },
        "loading-indicator": {
            animation: "$circular-rotate 1.4s linear infinite",
            height: 44,
            width: 44,
            "& circle": {
                animation: "$circular-dash 1.4s ease-in-out infinite",
                stroke: theme.palette.primary.main,
                // Some default value that looks fine waiting for the animation to kicks in.
                strokeDasharray: "80px, 200px",
                strokeDashoffset: "0px",
            },
        },
        "loading-indicator-container": {
            height: "100%",
            width: "100%",
        },
        "loading-indicator-container--center": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const loadingIndicator = {
    name: "loadingIndicator",
    label: "Loading Indicator",
    status: PatternStatus.Experimental,
    styles: styles$l,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$j = makeStyles((theme) => createStyles(loadingIndicator.styles(theme)));
function LoadingIndicator({ center, className, maxHeight, ...props }) {
    const bem = useBem(useStyles$j());
    return (React.createElement("div", { className: bem("loading-indicator-container", { center }), style: { maxHeight: maxHeight } },
        React.createElement("svg", Object.assign({ className: clsx(bem("loading-indicator"), className), viewBox: "22 22 44 44" }, props),
            React.createElement("circle", { cx: "44", cy: "44", r: "20.2", fill: "none", strokeWidth: "3.6" }))));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const PROMINENT_WARNING_BACKGROUND_COLOR = "#FFF4E5";
const PROMINENT_WARNING_COLOR = "#663C00";
const PROMINENT_WARNING_ICON_COLOR = "#FF9800";
function styles$m(theme) {
    return {
        message: {
            border: `solid 1px ${theme.palette.text.primary}`,
            color: theme.palette.text.primary,
            display: "flex",
            fontFamily: theme.typography.body.fontFamily,
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: theme.typography.body.lineHeight,
            margin: theme.spacing(1, 0),
            padding: theme.spacing(0.25, 0.5),
            ...getInlineElements(theme),
        },
        "message--error": {
            borderColor: theme.palette.error.main,
            color: theme.palette.error.main,
        },
        "message--info": {
            borderColor: theme.palette.info.main,
            color: theme.palette.info.main,
        },
        "message--invalid": {
            borderColor: "transparent",
            color: theme.palette.error.main,
            fontSize: "0.8125rem",
            padding: 0,
        },
        "message--prominent": {
            fontSize: "0.8125rem",
            padding: theme.spacing(0, 1.5),
            "&$message--warning": {
                backgroundColor: PROMINENT_WARNING_BACKGROUND_COLOR,
                borderColor: PROMINENT_WARNING_BACKGROUND_COLOR,
                color: PROMINENT_WARNING_COLOR,
            },
        },
        "message--warning": {
            borderColor: theme.palette.warning.main,
            color: theme.palette.warning.main,
        },
        message__close: {
            padding: theme.spacing(1.5, 0, 1.5, 1.5),
        },
        "message__close--warning": {
            "& span": {
                ...theme.icons.iconColor(PROMINENT_WARNING_COLOR),
            },
        },
        message__icon: {
            padding: theme.spacing(1.5, 1.5, 1.5, 0),
        },
        "message__icon--warning": {
            "& span": {
                ...theme.icons.iconColor(PROMINENT_WARNING_ICON_COLOR),
            },
        },
        message__main: {
            flexGrow: 1,
            fontWeight: theme.typography.fontWeightLight,
            ...getInlineElements(theme),
            "& ol": {
                paddingLeft: theme.spacing(1.5),
            },
        },
        message__title: {
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: 1.7,
            marginBottom: theme.spacing(0),
            "& + *": {
                marginTop: theme.spacing(0),
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const message = {
    name: "message",
    label: "Message",
    status: PatternStatus.Experimental,
    styles: styles$m,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$k = makeStyles((theme) => createStyles(message.styles(theme)));
function getIcon(variant) {
    switch (variant) {
        case "error":
        case "warning":
            return React.createElement(Icon, { name: "error" });
        default:
            return React.createElement(Icon, { name: "info" });
    }
}
function Message({ children, className, closeButtonProps, hasIcon, onClose, prominent, variant, ...props }) {
    const classes = useStyles$k();
    const bem = useBem(classes);
    const [open, setOpen] = useState(true);
    const onCloseClick = (event) => {
        setOpen(false);
        if (onClose) {
            onClose(event);
        }
    };
    return open ? (React.createElement("div", Object.assign({ className: clsx(bem("message", variant, {
            prominent,
        }), className) }, props),
        hasIcon ? React.createElement("div", { className: bem("message__icon", variant) }, getIcon(variant)) : null,
        React.createElement("div", { className: bem("message__main", variant) }, children),
        onClose ? (React.createElement("div", { className: bem("message__close", variant) },
            React.createElement(Button, Object.assign({ variant: "text", onClick: onCloseClick }, (closeButtonProps || {})),
                React.createElement(Icon, { name: "cancel", role: "img", label: "Close message" })))) : null)) : null;
}
Message.useBem = () => useBem(useStyles$k());

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const MODAL_MIN_WIDTH = 680;
function styles$n(theme) {
    return {
        modal: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
        },
        modal__container: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.shape.borderRadius,
            boxShadow: "0 9px 46px 8px rgba(0,0,0,0.12), 0 24px 38px 3px rgba(0,0,0,0.14), 0 11px 15px -7px rgba(0,0,0,0.2)",
            width: `calc(100vw - ${theme.spacing(2.8)}px)`,
            height: `calc(100vh - ${theme.spacing(5)}px)`,
            padding: theme.spacing(2, 1.4, 3),
            [theme.breakpoints.up("sm")]: {
                minWidth: MODAL_MIN_WIDTH,
                height: "auto",
                maxHeight: "80vh",
                width: MODAL_MIN_WIDTH,
                padding: theme.spacing(2, 4, 3),
            },
        },
        modal__title: {
            ...theme.typography.h1,
            marginBottom: theme.spacing(1.2),
            wordBreak: "break-word",
        },
        modal__body: {
            flexGrow: 1,
            maxHeight: "calc(100vh - 200px)",
            margin: theme.spacing(-0.2, -0.2),
            overflow: "auto",
            padding: theme.spacing(0.2, 0.2),
        },
        "modal__buttons-container": {
            display: "flex",
            flexDirection: "column-reverse",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: theme.spacing(2.4),
            [theme.breakpoints.up("sm")]: {
                display: "flex",
                flexDirection: "row",
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const modal = {
    name: "modal",
    label: "Modal",
    status: PatternStatus.Experimental,
    styles: styles$n,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$l = makeStyles((theme) => createStyles(modal.styles(theme)));
function Modal({ children, className, ...props }) {
    const bem = useBem(useStyles$l());
    return (React.createElement(Modal$1, Object.assign({ className: clsx(bem("modal"), className) }, props), children));
}
Modal.useBem = () => useBem(useStyles$l());
// eslint-disable-next-line react/display-name
const ModalContainer = forwardRef(({ children, className, ...props }, ref) => {
    const bem = useBem(useStyles$l());
    return (React.createElement("div", Object.assign({ className: clsx(bem("modal__container"), className), ref: ref }, props), children));
});
function ModalTitle({ children, className, ...props }) {
    const bem = useBem(useStyles$l());
    return (React.createElement("div", Object.assign({ className: clsx(bem("modal__title"), className) }, props), children));
}
function ModalBody({ children, className, ...props }) {
    const bem = useBem(useStyles$l());
    return (React.createElement("div", Object.assign({ className: clsx(bem("modal__body"), className) }, props), children));
}
function ModalButtonsContainer({ children, className, ...props }) {
    const bem = useBem(useStyles$l());
    return (React.createElement("div", Object.assign({ className: clsx(bem("modal__buttons-container"), className) }, props), children));
}
function FormModal({ children, onSubmit, submitButtonLabel, title, onClose, ...modalProps }) {
    const handleClose = (event) => (onClose || (() => null))(event, "escapeKeyDown");
    const handleSubmit = (event) => (onSubmit || (() => null))(event);
    return (React.createElement(Modal, Object.assign({ onClose: onClose }, modalProps),
        React.createElement(ModalContainer, null,
            React.createElement(ModalTitle, null, title),
            React.createElement(ModalBody, null,
                React.createElement(Form, { onSubmit: handleSubmit }, children)),
            React.createElement(ModalButtonsContainer, null,
                React.createElement(Button, { variant: "secondary", onClick: handleClose }, "Cancel"),
                React.createElement(Button, { onClick: handleSubmit }, submitButtonLabel || "Save")))));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$o(theme) {
    return {
        "page-header": {
            ...getContainer(theme),
            background: theme.palette.grey["50"],
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            flexFlow: "row wrap",
        },
        "page-header__title": {
            ...theme.typography.h1,
            flexGrow: 1,
            [theme.breakpoints.up("sm")]: {
                flexGrow: 0,
            },
        },
        "page-header__content": {
            flexGrow: 1,
            order: 2,
            padding: theme.spacing(1, 0),
            width: "100%",
            [theme.breakpoints.up("sm")]: {
                order: 1,
                padding: theme.spacing(1, 2),
                width: "auto",
            },
            [theme.breakpoints.up("md")]: {
                padding: theme.spacing(1, 4),
            },
        },
        "page-header__toolbar": {
            display: "flex",
            flexDirection: "column",
            flexGrow: 0,
            order: 1,
            [theme.breakpoints.up("sm")]: {
                order: 2,
            },
            [theme.breakpoints.up("md")]: {
                flexDirection: "row",
            },
        },
        "page-header__action": {
            ...normalizedButton,
            border: 0,
            color: theme.palette.primary.main,
            fontFamily: theme.typography.heading.fontFamily,
            fontWeight: theme.typography.heading.fontWeight,
            margin: theme.spacing(0.5),
            textDecoration: "underline",
            textTransform: "uppercase",
            [theme.breakpoints.up("sm")]: {
                "&:last-child": {
                    marginRight: 0,
                },
            },
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const select = expandPattern({
    name: "pageHeader",
    label: "Page Header",
    status: PatternStatus.Experimental,
    styles: styles$o,
});

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$m = makeStyles((theme) => createStyles(select.styles(theme)));
function PageHeader({ actions, children, className, title }) {
    const bem = useBem(useStyles$m());
    function Action(action) {
        return "href" in action ? React.createElement(ActionLink, Object.assign({}, action)) : React.createElement(ActionButton, Object.assign({}, action));
    }
    function ActionLink({ text, ...props }) {
        return (React.createElement("a", Object.assign({ className: bem("page-header__action") }, props), text));
    }
    function ActionButton({ text, ...props }) {
        return (React.createElement("button", Object.assign({ className: bem("page-header__action") }, props), text));
    }
    return (React.createElement("div", { className: clsx(bem("page-header"), className) },
        React.createElement("h1", { className: bem("page-header__title") }, title),
        children && React.createElement("div", { className: bem("page-header__content") }, children),
        actions && (React.createElement("div", { className: bem("page-header__toolbar") }, actions.map((action, index) => (React.createElement(Fragment, { key: `action-${index}` }, "text" in action ? React.createElement(Action, Object.assign({}, action)) : action)))))));
}
PageHeader.usePageHeaderActionClassName = () => {
    const bem = useBem(useStyles$m());
    return bem("page-header__action");
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$p(theme) {
    const cellAlignCenter = {
        textAlign: "center",
    };
    const cellAlignEnd = {
        textAlign: "right",
    };
    const cellWidthPreview = {
        minWidth: 30,
        width: 30,
    };
    const cellWidthSmall = {
        width: "5%",
    };
    const tableHeader = {
        fontFamily: theme.typography.heading.fontFamily,
    };
    const tableHeaderRow = {
        textAlign: "left",
        padding: "0 1em",
    };
    const tableHeaderCell = {
        padding: "1em",
        borderBottom: `1px solid ${theme.palette.grey["200"]}`,
        whiteSpace: "nowrap",
    };
    const tableBody = {
        fontFamily: theme.typography.body.fontFamily,
        padding: "0 1em",
    };
    const tableBodyRow = {
        padding: "0 1em",
        borderBottom: `1px solid ${theme.palette.grey["200"]}`,
    };
    const tableBodyCell = {
        padding: "1em",
        whiteSpace: "nowrap",
    };
    return {
        table: {
            background: theme.palette.background.default,
            borderCollapse: "collapse",
            color: theme.palette.text.secondary,
            fontSize: "0.9rem",
            overflow: "scroll",
            width: "100%",
        },
        "table--inherits": {
            "& thead": tableHeader,
            "& thead tr": tableHeaderRow,
            "& thead th": tableHeaderCell,
            "& tbody": tableBody,
            "& tbody tr": tableBodyRow,
            "& tbody td": tableBodyCell,
        },
        table__header: tableHeader,
        "table__header-row": tableHeaderRow,
        "table__header-cell": tableHeaderCell,
        "table__header-cell--align-center": cellAlignCenter,
        "table__header-cell--align-end": cellAlignEnd,
        "table__header-cell--width-preview": cellWidthPreview,
        "table__header-cell--width-small": cellWidthSmall,
        table__icon: {
            ...theme.icons.iconColor(theme.palette.text.secondary),
        },
        table__body: tableBody,
        "table__body-row": tableBodyRow,
        "table__body-row--selectable": {
            cursor: "pointer",
            "&:focus, &:hover": {
                extend: "table__body-row--selected",
            },
        },
        "table__body-row--selected": {
            background: theme.palette.grey["50"],
        },
        "table__body-cell": tableBodyCell,
        "table__body-cell--align-center": cellAlignCenter,
        "table__body-cell--align-end": cellAlignEnd,
        "table__body-cell--width-preview": cellWidthPreview,
        "table__body-cell--width-small": cellWidthSmall,
        table__link: {
            color: theme.palette.text.primary,
        },
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const table = {
    name: "table",
    label: "Table",
    status: PatternStatus.Experimental,
    styles: styles$p,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$n = makeStyles((theme) => createStyles(table.styles(theme)));
function Table() {
    throw new Error("Not implemented");
}
Table.useBem = () => useBem(useStyles$n());
/* To be deprecated - don't repeat this pattern */
function useClassName(className) {
    return (...modifiers) => {
        const bem = useBem(useStyles$n());
        return bem(className, ...modifiers);
    };
}
Table.useTableClass = useClassName("table");
Table.useTableHeaderClass = useClassName("table__header");
Table.useTableHeaderRowClass = useClassName("table__header-row");
Table.useTableHeaderCellClass = useClassName("table__header-cell");
Table.useTableBodyClass = useClassName("table__body");
Table.useTableBodyRowClass = useClassName("table__body-row");
Table.useTableBodyCellClass = useClassName("table__body-cell");
Table.useTableIconClass = useClassName("table__icon");
Table.useTableLinkClass = useClassName("table__link");

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function styles$q(theme) {
    const activeFocusInput = {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
        outline: "none",
    };
    const invalidInput = {
        borderColor: theme.palette.error.main,
        borderWidth: 2,
    };
    const readonlyInput = {
        border: "none",
        pointerEvents: "none",
        background: "none",
        "&:focus": {
            border: "none",
            pointerEvents: "none",
        },
        "&:active": {
            border: "none",
            pointerEvents: "none",
        },
    };
    const disabledInput = {
        pointerEvents: "none",
    };
    return {
        textarea: {
            borderRadius: 10,
            border: `solid 1px ${theme.palette.divider}`,
            fontFamily: theme.typography.body.fontFamily,
            width: "100%",
            appearance: "none",
            transition: theme.transitions.easing.easeInOut,
            fontSize: "0.8em",
            textIndent: "0.75em",
            display: "flex",
            flexWrap: "wrap",
            maxWidth: "95%",
            margin: theme.spacing(0.5, 0),
            padding: theme.spacing(1.5, 2),
            paddingLeft: 12,
            resize: "vertical",
            "&[disabled]": disabledInput,
            "&:not([disabled]):active": activeFocusInput,
            "&:not([disabled]):focus": activeFocusInput,
            "&:not([disabled]):invalid": invalidInput,
            "&[readonly], &:read-only": readonlyInput,
            "&::placeholder": {
                color: theme.palette.text.hint,
            },
        },
        "textarea--focus": activeFocusInput,
        "textarea--active": activeFocusInput,
        "textarea--invalid": invalidInput,
        "textarea--disabled": disabledInput,
        "textarea--readonly": readonlyInput,
    };
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const textarea = {
    name: "textarea",
    label: "Textarea",
    status: PatternStatus.Experimental,
    styles: styles$q,
};

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
const useStyles$o = makeStyles((theme) => createStyles(textarea.styles(theme)));
function Textarea({ id, invalidMessage, label, ...props }) {
    return (React.createElement(InputGroup, { id: id, label: label, invalidMessage: invalidMessage },
        React.createElement(SimpleTextarea, Object.assign({ id: id, name: id }, props))));
}
Textarea.useBem = () => useBem(useStyles$o());
function SimpleTextarea({ className, variant, ...props }) {
    const bem = useBem(useStyles$o());
    return React.createElement("textarea", Object.assign({ className: clsx(bem("textarea", variant), className) }, props));
}

/**
 * Copyright 2020 Inrupt Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to use,
 * copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the
 * Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
function ThemeProvider(args) {
    return ThemeProvider$1(args);
}

export { ActionButton, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuLink, BackToNav, BackToNavLink, Breadcrumbs, Button, Container, Content, Drawer, DrawerContainer, Footer, Form, FormModal, Header, HeaderBanner, HeaderToolbar, Hero, Icon as Icons, Input, InputGroup, Label, LinkButton, LoadingIndicator, MainNav, MainNavLink, Message, Modal, ModalBody, ModalButtonsContainer, ModalContainer, ModalTitle, PageHeader, SimpleInput, Table, Textarea, ThemeProvider, UserMenu, UserMenuButton, UserMenuLink, makeStyles, useOutsideClick };
